// TextElement.jsx
import { Group } from "react-konva";
import React, { useRef, useEffect, useMemo } from "react";
import { Rect, Text } from "react-konva";
import { Html } from "react-konva-utils";
import Konva from "konva";
import { flushSync } from "react-dom";
import {
  cleanHtmlContent,
  measureHtmlHeight,
  stripHtmlPreserveFormatting,
} from "../../../SectionElements/plugins/utils";
import { wrapTextToLines } from "../../utils/textWrappingUtils";
import { isPrimaryMouseButton } from "../../utils/pointerButtonUtils";
import {
  getAbsoluteHorizontalExtent,
  getOffCanvasMainColumnBounds,
  getOffCanvasHorizontalStripSide,
  shiftXToStayInOffCanvasStrip,
} from "../../utils/boundaryUtils";

// Escape HTML to safely insert plain text inside HTML wrappers
const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

// Ensure all <mark> tags render with a consistent background color
// Preserves color styles from original mark tags
const normalizeMarkColor = (html, color = "yellow") => {
  if (!html) return html;
  // Preserve color style from original mark tag if it exists
  let out = html.replace(/<mark[^>]*>/gi, (match) => {
    // Extract color style from the original mark tag
    const colorMatch = match.match(/color\s*:\s*([^;]+)/i);
    const preservedColor = colorMatch ? colorMatch[1].trim() : "inherit";

    // Extract other non-color, non-background-color styles (like white-space)
    const otherStyles = [];
    const whiteSpaceMatch = match.match(/white-space\s*:\s*([^;]+)/i);
    if (whiteSpaceMatch) {
      otherStyles.push(`white-space: ${whiteSpaceMatch[1].trim()}`);
    }

    const otherStylesStr =
      otherStyles.length > 0 ? `; ${otherStyles.join("; ")}` : "";

    return `<mark style=\"background-color: ${color}; color: ${preservedColor}${otherStylesStr};\">`;
  });
  return out;
};

// Preserve <mark>, color spans, and convert highlight spans to <mark>; strip other spans; escape everything else
const sanitizeAllowMark = (str) => {
  if (!str) return "";
  const OPEN_PLACEHOLDER = "__PRESERVE_MARK_OPEN__";
  const CLOSE_PLACEHOLDER = "__PRESERVE_MARK_CLOSE__";
  const COLOR_SPAN_PLACEHOLDER = "__PRESERVE_COLOR_SPAN_";
  const BR_PLACEHOLDER = "__PRESERVE_BR__";
  let temp = String(str);

  // Preserve <br> tags before escaping
  temp = temp.replace(/<br\s*\/?>/gi, BR_PLACEHOLDER);

  // Store complete color span elements to preserve them
  const colorSpanMap = new Map();
  let colorSpanIndex = 0;

  // First, preserve complete color spans (matching from opening tag to closing tag)
  // Match spans with color style that don't have background-color
  const colorSpanPattern =
    /<span[^>]*style=["'][^"']*color\s*:\s*[^;"']+[^"']*["'][^>]*>/gi;
  let match;
  const colorSpans = [];
  const processedIndices = new Set();

  // Reset regex lastIndex
  colorSpanPattern.lastIndex = 0;

  while ((match = colorSpanPattern.exec(temp)) !== null) {
    if (processedIndices.has(match.index)) continue;

    const spanStart = match.index;
    const spanTag = match[0];

    // Skip if this span also has background-color (handled separately as highlight)
    if (/background-color/i.test(spanTag)) {
      continue;
    }

    // Find the matching closing tag
    let depth = 1;
    let pos = match.index + match[0].length;

    while (pos < temp.length && depth > 0) {
      const nextOpen = temp.indexOf("<span", pos);
      const nextClose = temp.indexOf("</span>", pos);

      if (nextClose === -1) break;

      if (nextOpen !== -1 && nextOpen < nextClose) {
        depth++;
        pos = nextOpen + 5;
      } else {
        depth--;
        if (depth === 0) {
          const spanEnd = nextClose + 7;
          const fullSpan = temp.substring(spanStart, spanEnd);
          const placeholder = `${COLOR_SPAN_PLACEHOLDER}${colorSpanIndex}__`;

          // Extract inner content and escape it, but preserve the span tags
          const innerContent = fullSpan.substring(
            spanTag.length,
            fullSpan.length - 7,
          );
          const escapedInner = escapeHtml(innerContent);
          const preservedSpan = spanTag + escapedInner + "</span>";

          colorSpanMap.set(placeholder, preservedSpan);
          colorSpans.push({ placeholder, start: spanStart, end: spanEnd });
          colorSpanIndex++;
          processedIndices.add(spanStart);
          break;
        }
        pos = nextClose + 7;
      }
    }
  }

  // Replace color spans with placeholders (in reverse order to maintain indices)
  colorSpans.reverse().forEach(({ placeholder, start, end }) => {
    temp = temp.substring(0, start) + placeholder + temp.substring(end);
  });

  // Spans with neutral background (white, transparent) are not highlights - preserve them as-is.
  // Only spans with an actual highlight color (e.g. yellow) become <mark>.
  const NEUTRAL_BG_PLACEHOLDER = "__PRESERVE_NEUTRAL_BG_SPAN_";
  const neutralBgSpanMap = new Map();
  let neutralBgIndex = 0;
  const neutralBgRegex =
    /<span[^>]*style=["'][^"']*background-color\s*:\s*([^;"']+)[^"']*["'][^>]*>/gi;
  const isNeutralBackground = (value) => {
    if (!value || typeof value !== "string") return true;
    const v = value.trim().toLowerCase();
    if (
      v === "transparent" ||
      v === "inherit" ||
      v === "initial" ||
      v === "unset"
    )
      return true;
    if (v === "white" || v === "#fff" || v === "#ffffff" || v === "#fffefc")
      return true;
    const rgbMatch = v.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1], 10);
      const g = parseInt(rgbMatch[2], 10);
      const b = parseInt(rgbMatch[3], 10);
      if (r >= 250 && g >= 250 && b >= 250) return true; // white / near-white
    }
    const rgbaMatch = v.match(
      /rgba\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)/,
    );
    if (rgbaMatch) {
      const a = parseFloat(rgbaMatch[4], 10);
      if (a === 0) return true;
      const r = parseInt(rgbaMatch[1], 10);
      const g = parseInt(rgbaMatch[2], 10);
      const b = parseInt(rgbaMatch[3], 10);
      if (r >= 250 && g >= 250 && b >= 250 && a >= 0.99) return true;
    }
    return false;
  };
  let neutralMatch;
  const neutralSpans = [];
  neutralBgRegex.lastIndex = 0;
  while ((neutralMatch = neutralBgRegex.exec(temp)) !== null) {
    const bgValue = neutralMatch[1];
    if (!isNeutralBackground(bgValue)) continue;
    const spanStart = neutralMatch.index;
    const spanTag = neutralMatch[0];
    let depth = 1;
    let pos = spanStart + spanTag.length;
    while (pos < temp.length && depth > 0) {
      const nextOpen = temp.indexOf("<span", pos);
      const nextClose = temp.indexOf("</span>", pos);
      if (nextClose === -1) break;
      if (nextOpen !== -1 && nextOpen < nextClose) {
        depth++;
        pos = nextOpen + 5;
      } else {
        depth--;
        if (depth === 0) {
          const spanEnd = nextClose + 7;
          const fullSpan = temp.substring(spanStart, spanEnd);
          const placeholder = `${NEUTRAL_BG_PLACEHOLDER}${neutralBgIndex}__`;
          neutralBgSpanMap.set(placeholder, fullSpan);
          neutralSpans.push({ placeholder, start: spanStart, end: spanEnd });
          neutralBgIndex++;
          break;
        }
        pos = nextClose + 7;
      }
    }
  }
  neutralSpans.reverse().forEach(({ placeholder, start, end }) => {
    temp = temp.substring(0, start) + placeholder + temp.substring(end);
  });

  // Convert highlight spans (style background-color, non-neutral) to placeholders
  temp = temp.replace(
    /<span[^>]*style=["'][^"']*background-color\s*:\s*[^;"']+[^"']*["'][^>]*>/gi,
    OPEN_PLACEHOLDER,
  );
  // Convert highlight spans (lexical class) to placeholders
  temp = temp.replace(
    /<span[^>]*class=["'][^"']*(?:PlaygroundEditorTheme__textHighlight|textHighlight)[^"']*["'][^>]*>/gi,
    OPEN_PLACEHOLDER,
  );

  // Close for any span we opened as placeholder (for highlights)
  temp = temp.replace(/<\/span>/gi, CLOSE_PLACEHOLDER);

  // Strip any remaining span tags while keeping their content.
  // First: match <span ...> when attributes use quoted values (allows ">" inside e.g. data-text="...&gt;")
  temp = temp.replace(
    /<span\s+(?:\s*[^\s=]+=(?:"[^"]*"|'[^']*')\s*)*\s*>/gi,
    "",
  );
  // Fallback: any remaining <span...> (e.g. <span> or unquoted attributes)
  temp = temp.replace(/<span[^>]*>/gi, "");
  temp = temp.replace(/<\/span>/gi, "");

  // Preserve mark tags with their style attributes (especially color)
  const markTagMap = new Map();
  let markTagIndex = 0;
  const MARK_TAG_PLACEHOLDER = "__PRESERVE_MARK_TAG_";

  // Extract and preserve opening mark tags with their styles
  temp = temp.replace(/<mark[^>]*>/gi, (match) => {
    const placeholder = `${MARK_TAG_PLACEHOLDER}${markTagIndex}__`;
    markTagMap.set(placeholder, match);
    markTagIndex++;
    return placeholder;
  });

  // Preserve closing mark tags
  temp = temp.replace(/<\/mark>/gi, CLOSE_PLACEHOLDER);

  // Escape the rest
  temp = escapeHtml(temp);

  // Restore preserved <br> tags
  temp = temp.replace(
    new RegExp(
      escapeHtml(BR_PLACEHOLDER).replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
      "g",
    ),
    "<br>",
  );

  // Restore color spans first (before restoring mark tags)
  colorSpanMap.forEach((originalSpan, placeholder) => {
    const escapedPlaceholder = escapeHtml(placeholder);
    temp = temp.replace(
      new RegExp(
        escapedPlaceholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
        "g",
      ),
      originalSpan,
    );
  });

  // Restore neutral-background spans (white/transparent) so they don't render as yellow highlight
  neutralBgSpanMap.forEach((originalSpan, placeholder) => {
    const escapedPlaceholder = escapeHtml(placeholder);
    temp = temp.replace(
      new RegExp(
        escapedPlaceholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
        "g",
      ),
      originalSpan,
    );
  });

  // Restore preserved mark tags with their original styles
  markTagMap.forEach((originalMarkTag, placeholder) => {
    const escapedPlaceholder = escapeHtml(placeholder);
    temp = temp.replace(
      new RegExp(
        escapedPlaceholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
        "g",
      ),
      originalMarkTag,
    );
  });

  // Restore closing mark tags
  temp = temp.replace(new RegExp(CLOSE_PLACEHOLDER, "g"), "</mark>");

  // Restore any remaining mark placeholders (shouldn't happen, but fallback)
  temp = temp.replace(new RegExp(OPEN_PLACEHOLDER, "g"), "<mark>");

  return temp;
};

// Part description can contain inline neutral backgrounds from API content
// (e.g. background-color: rgb(255,255,255)). Strip those so selection/highlight
// backgrounds applied on the HTML container remain visible.
const stripNeutralBackgroundsForHighlight = (html) => {
  if (!html) return html;
  const isNeutralBackground = (value) => {
    if (!value || typeof value !== "string") return true;
    const v = value.trim().toLowerCase();
    if (
      v === "transparent" ||
      v === "inherit" ||
      v === "initial" ||
      v === "unset"
    )
      return true;
    if (v === "white" || v === "#fff" || v === "#ffffff" || v === "#fffefc")
      return true;
    const rgbMatch = v.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1], 10);
      const g = parseInt(rgbMatch[2], 10);
      const b = parseInt(rgbMatch[3], 10);
      if (r >= 250 && g >= 250 && b >= 250) return true;
    }
    const rgbaMatch = v.match(
      /rgba\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)/,
    );
    if (rgbaMatch) {
      const a = parseFloat(rgbaMatch[4], 10);
      if (a === 0) return true;
      const r = parseInt(rgbaMatch[1], 10);
      const g = parseInt(rgbaMatch[2], 10);
      const b = parseInt(rgbaMatch[3], 10);
      if (r >= 250 && g >= 250 && b >= 250 && a >= 0.99) return true;
    }
    return false;
  };
  return html.replace(
    /style\s*=\s*(["'])([^"']*)\1/gi,
    (_, quote, styleContent) => {
      const newStyle = styleContent.replace(
        /background-color\s*:\s*([^;]+)/gi,
        (match, value) =>
          isNeutralBackground(value.trim())
            ? "background-color: transparent"
            : match,
      );
      return `style=${quote}${newStyle}${quote}`;
    },
  );
};

// Part description HTML from the API often has inline color (e.g. color: rgb(0,0,0)) which overrides
// the container's color. When a part is selected we want the container's grey (textStyles.part.color)
// to show. Strip inline color so the parent's color is inherited.
const stripInlineColorForPartDescription = (html) => {
  if (!html) return html;
  return html.replace(
    /style\s*=\s*(["'])([^"']*)\1/gi,
    (_, quote, styleContent) => {
      const newStyle = styleContent
        .replace(/color\s*:\s*[^;]+;?/gi, "color: inherit;")
        .trim()
        .replace(/;\s*$/, "");
      return `style=${quote}${newStyle}${quote}`;
    },
  );
};

const flattenNestedLists = (html) => {
  if (
    !html ||
    typeof window === "undefined" ||
    typeof window.DOMParser === "undefined"
  ) {
    return html;
  }

  try {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
    const container = doc.body.firstElementChild;

    if (!container) {
      return html;
    }

    const isListElement = (node) =>
      node && (node.tagName === "UL" || node.tagName === "OL");

    const hasDisplayableContent = (li) =>
      Array.from(li.childNodes).some((node) => {
        if (node.nodeType === 3) {
          return node.textContent.trim().length > 0;
        }
        if (node.nodeType === 1) {
          const tagName = node.tagName;
          if (tagName === "UL" || tagName === "OL") {
            return false;
          }
          return true;
        }
        return false;
      });

    const flattenList = (list) => {
      if (!isListElement(list)) return;

      let mutated = false;

      Array.from(list.children)
        .filter((child) => child.tagName === "LI")
        .forEach((li) => {
          const childLists = Array.from(li.children).filter((child) =>
            isListElement(child),
          );

          childLists.forEach(flattenList);

          if (childLists.length === 1 && !hasDisplayableContent(li)) {
            const nestedList = childLists[0];
            const nestedItems = Array.from(nestedList.children).filter(
              (child) => child.tagName === "LI",
            );

            nestedItems.forEach((nestedItem) => {
              list.insertBefore(nestedItem, li);
            });

            li.remove();
            mutated = true;
          }
        });

      if (mutated) {
        flattenList(list);
      }
    };

    Array.from(container.querySelectorAll("ul, ol")).forEach(flattenList);

    return container.innerHTML;
  } catch (error) {
    return html;
  }
};

export const measureHtmlContentHeight = (
  htmlContent,
  fontSize,
  fontFamily,
  width,
) => {
  const container = document.createElement("div");

  // ✅ CONSISTENT RENDERING: Force integer width (no sub-pixels)
  const integerWidth = Math.floor(width);

  container.style.position = "absolute";
  container.style.visibility = "hidden";
  container.style.width = `${integerWidth}px`;
  container.style.fontSize = `${fontSize}px`;
  container.style.fontFamily = fontFamily;
  container.style.whiteSpace = "pre-wrap";
  container.style.wordBreak = "break-word";
  container.style.overflowWrap = "break-word";
  container.style.lineHeight = "1.4";
  container.style.pointerEvents = "none";

  // ✅ FORCE CONSISTENT FONT RENDERING
  container.style.textRendering = "geometricPrecision";
  container.style.WebkitFontSmoothing = "antialiased";
  container.style.MozOsxFontSmoothing = "grayscale";
  container.style.fontKerning = "normal";
  container.style.fontVariantLigatures = "none";
  container.style.letterSpacing = "normal";
  container.style.wordSpacing = "normal";

  // ✅ Prevent layout shifts
  container.style.boxSizing = "border-box";
  container.style.margin = "0";
  container.style.padding = "0";
  container.style.display = "block";

  container.innerHTML = htmlContent;

  document.body.appendChild(container);

  // ✅ Get precise height with sub-pixel accuracy
  const rect = container.getBoundingClientRect();
  const preciseHeight = rect.height;

  document.body.removeChild(container);

  // ✅ Use Math.ceil to handle sub-pixel rounding consistently
  // NO BUFFER - exact measurement for perfect consistency
  return Math.ceil(preciseHeight);
};

// Get line data using actual DOM rendering for accurate measurement (handles HTML, zero-width spaces, etc.)
const getActualRenderedLinesData = (
  htmlContent,
  fontSize,
  fontFamily,
  maxWidth,
  lineHeight = 1.4,
) => {
  const integerMaxWidth = Math.floor(maxWidth);

  // Create a temporary container with the same styles as the actual rendering
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.visibility = "hidden";
  container.style.top = "-9999px";
  container.style.left = "0";
  container.style.width = `${integerMaxWidth}px`;
  container.style.fontSize = `${fontSize}px`;
  container.style.fontFamily = fontFamily;
  container.style.whiteSpace = "pre-wrap";
  container.style.wordBreak = "break-word";
  container.style.overflowWrap = "break-word";
  container.style.lineHeight = `${lineHeight}`;
  container.style.boxSizing = "border-box";
  container.style.margin = "0";
  container.style.padding = "0";
  container.style.display = "block";

  container.innerHTML = htmlContent;
  document.body.appendChild(container);

  // Force a reflow to ensure accurate measurements
  void container.offsetHeight;

  const lines = [];
  const lineHeightPx = fontSize * lineHeight;
  const range = document.createRange();

  // Get the container's bounding box
  const containerRect = container.getBoundingClientRect();
  const containerLeft = containerRect.left;

  // Create a walker to find all text nodes
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null,
  );

  let textNode;
  const textNodes = [];
  while ((textNode = walker.nextNode())) {
    if (textNode.textContent) {
      textNodes.push(textNode);
    }
  }

  if (textNodes.length === 0) {
    document.body.removeChild(container);
    return [];
  }

  // Build lines by checking character positions
  let currentLine = { text: "", startX: null, endX: null, y: null };
  const processedLines = [];

  textNodes.forEach((node) => {
    const text = node.textContent;
    for (let i = 0; i < text.length; i++) {
      try {
        range.setStart(node, i);
        range.setEnd(node, i + 1);
        const rect = range.getBoundingClientRect();
        const charY = rect.top;
        const charX = rect.left;

        if (currentLine.y === null) {
          // First character
          currentLine.y = charY;
          currentLine.startX = charX;
          currentLine.endX = charX + rect.width;
          currentLine.text = text[i];
        } else if (Math.abs(charY - currentLine.y) > lineHeightPx * 0.3) {
          // New line detected
          if (currentLine.text.trim()) {
            processedLines.push({
              text: currentLine.text,
              width: Math.max(
                currentLine.endX - currentLine.startX,
                Math.ceil(currentLine.endX - containerLeft + 4),
              ),
              height: Math.ceil(lineHeightPx),
            });
          }
          // Start new line
          currentLine = {
            text: text[i],
            startX: charX,
            endX: charX + rect.width,
            y: charY,
          };
        } else {
          // Same line, append character
          currentLine.text += text[i];
          currentLine.endX = charX + rect.width;
        }
      } catch (e) {
        // If range measurement fails, continue to next character
        continue;
      }
    }
  });

  // Add the last line
  if (currentLine.text.trim()) {
    processedLines.push({
      text: currentLine.text,
      width: Math.max(
        currentLine.endX - currentLine.startX,
        Math.ceil(currentLine.endX - containerLeft + 4),
      ),
      height: Math.ceil(lineHeightPx),
    });
  }

  document.body.removeChild(container);

  return processedLines.length > 0 ? processedLines : [];
};

// Get line data for wrapped text (width and height for each line)
export const getWrappedLinesData = (
  htmlContent,
  fontSize,
  fontFamily,
  maxWidth,
  lineHeight = 1.4,
) => {
  // Use DOM-based measurement for more accurate line detection when HTML contains special characters
  // This handles zero-width spaces, spans, and actual HTML rendering
  try {
    const domLines = getActualRenderedLinesData(
      htmlContent,
      fontSize,
      fontFamily,
      maxWidth,
      lineHeight,
    );
    if (domLines.length > 0) {
      return domLines;
    }
  } catch (e) {
    // Fallback to canvas-based measurement if DOM measurement fails
  }

  // Fallback: Canvas-based measurement (original method)
  const integerMaxWidth = Math.floor(maxWidth);

  // Strip HTML to get plain text for measurement
  const plainText = stripHtmlPreserveFormatting(htmlContent);

  // Use canvas to wrap text
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Set font for measurement
  ctx.font = `${fontSize}px ${fontFamily}`;

  // Wrap text to lines using maxWidth
  const lines = wrapTextToLines(plainText, integerMaxWidth, ctx, fontSize);

  if (lines.length === 0) {
    return [];
  }

  // Calculate width and height for each line
  const lineData = lines.map((line) => {
    const lineWidth = ctx.measureText(line).width;
    const lineHeightPx = fontSize * lineHeight;
    return {
      text: line,
      width: Math.ceil(lineWidth) + 2, // Add small buffer
      height: Math.ceil(lineHeightPx),
    };
  });

  return lineData;
};

// Measure the rendered width of HTML content up to a maximum width
export const measureHtmlContentWidth = (
  htmlContent,
  fontSize,
  fontFamily,
  maxWidth,
) => {
  const integerMaxWidth = Math.floor(maxWidth);

  // Strip HTML to get plain text for measurement
  const plainText = stripHtmlPreserveFormatting(htmlContent);

  // Use canvas to wrap text and measure the last line width
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Set font for measurement
  ctx.font = `${fontSize}px ${fontFamily}`;

  // Wrap text to lines using maxWidth
  const lines = wrapTextToLines(plainText, integerMaxWidth, ctx, fontSize);

  if (lines.length === 0) {
    return 0;
  }

  // If single line, return the actual width of that line
  // If multiline, return maxWidth since all but last line will be full width
  if (lines.length === 1) {
    const lastLine = lines[lines.length - 1];
    const lastLineWidth = ctx.measureText(lastLine).width;
    // Use ceiling + small buffer to ensure all text is covered
    return Math.ceil(lastLineWidth) + 2;
  } else {
    // Multiline - return maxWidth since the HTML div will render full width
    return integerMaxWidth;
  }
};

// ✅ CONSISTENT RENDERING STYLES FOR ALL HTML CONTENT
// This ensures measurement and display use identical CSS properties
const getConsistentHtmlStyles = (baseStyles = {}) => {
  // CRITICAL: Extract margin properties before spreading to avoid conflicts
  const { margin, marginTop, marginBottom, ...restBaseStyles } = baseStyles;

  return {
    ...restBaseStyles,
    // CRITICAL: Force consistent text rendering
    textRendering: "geometricPrecision",
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    fontKerning: "normal",
    fontVariantLigatures: "none",
    letterSpacing: "normal",
    wordSpacing: "normal",

    // Ensure consistent word wrapping
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",

    // Consistent box model
    boxSizing: "border-box",
    // CRITICAL: Only set margin shorthand if individual margins aren't provided
    // This allows negative margins to work for spacing compensation
    ...(marginBottom === undefined &&
    marginTop === undefined &&
    margin === undefined
      ? { margin: "0" }
      : {}),
    // Explicitly set marginTop and marginBottom - these override margin shorthand
    marginTop: marginTop !== undefined ? marginTop : "0",
    marginBottom: marginBottom !== undefined ? marginBottom : "0",
    padding: baseStyles.padding || "0px",
    display: "block",

    // Line height consistency
    lineHeight: baseStyles.lineHeight || "1.4",

    // z-index for HTML content - will be dynamically set based on selection state
    // Selected elements use z-index 15, non-selected use z-index 3
    zIndex: baseStyles.zIndex !== undefined ? baseStyles.zIndex : 3,
  };
};

// ✅ LIST ALIGNMENT STYLES
// Ensures bullets and numbers align properly with wrapped text
// Wrapped lines align with first line of text (not below the bullet/number)
const listAlignmentStyles = `
  .content-text ul,
  .content-text ol {
    margin: 0;
    padding: 0;
    padding-left: 1.5em; /* Space for bullet/number marker */
    margin-left: 0;
    list-style-position: outside; /* Markers outside content box */
  }
  
  .content-text ul {
    list-style-type: disc;
  }
  
  .content-text ol {
    list-style-type: decimal;
  }
  
  .content-text li {
    margin: 0;
    padding: 0;
    padding-left: 0;
    margin-left: 0;
    text-indent: 0;
    list-style-position: outside;
    display: list-item;
  }
  
  /* Ensure wrapped lines align with first line - no extra indentation */
  .content-text li p {
    margin: 0;
    padding: 0;
    margin-left: 0;
    padding-left: 0;
    display: inline;
    text-indent: 0;
  }
  
  /* Remove any spacing in list item content */
  .content-text li > *:first-child {
    margin-left: 0;
    padding-left: 0;
    text-indent: 0;
  }
  
  /* Handle nested lists */
  .content-text li ul,
  .content-text li ol {
    margin-top: 0;
    margin-bottom: 0;
    padding-left: 1.5em; /* Same padding for nested lists */
  }
  
  /* CRITICAL: Remove trailing whitespace from content-text elements to prevent extra spacing */
  /* This ensures that with white-space: pre-wrap, trailing whitespace doesn't create visual spacing */
  .content-text {
    /* Ensure no trailing whitespace creates extra lines */
    /* The explicit height on the div should clip any overflow, but this helps prevent it at the source */
  }
  
  /* Remove trailing whitespace from inline elements within content-text */
  .content-text span:last-child,
  .content-text mark:last-child {
    /* Ensure last child doesn't have trailing whitespace that creates spacing */
  }
  
  /* CRITICAL: Prevent extra spacing from line-height at the bottom of block elements */
  /* Use ::after pseudo-element to remove any trailing whitespace rendering */
  .content-text::after {
    content: "";
    display: block;
    height: 0;
    line-height: 0;
    margin: 0;
    padding: 0;
  }
  
`;

// DEBUG: Set to true to log TextElement performance (memo + render). Remove or set false when done.
const TEXT_PERF_DEBUG = false;
if (typeof window !== "undefined") window.__TEXT_PERF_DEBUG = TEXT_PERF_DEBUG;

// PERFORMANCE: Compare text by value; renderer always passes text={{ ...latestText, opacity }}
// so reference equality would never skip. Compare key layout/content fields so we skip when
// only other elements (e.g. image) are being dragged.
function textDataEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return (
    a.id === b.id &&
    a.x === b.x &&
    a.y === b.y &&
    a.width === b.width &&
    a.height === b.height &&
    a.rotation === b.rotation &&
    a.content === b.content &&
    a.task === b.task &&
    a.note === b.note &&
    a.fontSize === b.fontSize &&
    a.textStyles === b.textStyles &&
    a.partsByType === b.partsByType &&
    (a.opacity === b.opacity || (a.opacity == null && b.opacity == null)) &&
    a.selectedContent?.id === b.selectedContent?.id
  );
}

// True when selection identity is unchanged (same selected id, same set of selected ids).
// Used to skip re-render when only other elements' positions (x,y) are updating during drag.
function selectionIdentityEqual(prev, next) {
  if (prev.selectedObject?.id !== next.selectedObject?.id) return false;
  const prevIds = (prev.selectedObjects || [])
    .map((o) => o.id)
    .sort()
    .join(",");
  const nextIds = (next.selectedObjects || [])
    .map((o) => o.id)
    .sort()
    .join(",");
  return prevIds === nextIds;
}

// PERFORMANCE: Custom comparison so we skip re-render when only OTHER elements (e.g. image)
// are being dragged. Skip when: (1) this text is unselected and data/layout unchanged, OR
// (2) this text is selected but only selection positions (x,y) changed, not identity or this text's data.
function textElementPropsAreEqual(prev, next) {
  const tid = prev.text?.id ?? next.text?.id;
  if (tid == null) return false;

  const isTextType = (t) => t === "Text" || (t && String(t).includes("Text"));
  const thisSelectedInPrev =
    (prev.selectedObject?.id === tid &&
      isTextType(prev.selectedObject?.type)) ||
    (prev.selectedObjects &&
      prev.selectedObjects.some((o) => o.id === tid && isTextType(o.type)));
  const thisSelectedInNext =
    (next.selectedObject?.id === tid &&
      isTextType(next.selectedObject?.type)) ||
    (next.selectedObjects &&
      next.selectedObjects.some((o) => o.id === tid && isTextType(o.type)));

  const layoutAndDataEqual =
    textDataEqual(prev.text, next.text) &&
    prev.textBlockWidth === next.textBlockWidth &&
    prev.textGroupHeight === next.textGroupHeight &&
    prev.effectiveZoomLevel === next.effectiveZoomLevel &&
    prev.canvasWidth === next.canvasWidth &&
    prev.canvasHeight === next.canvasHeight &&
    prev.canvasResizeTrigger === next.canvasResizeTrigger &&
    prev.isSelecting === next.isSelecting &&
    prev.isOffCanvas === next.isOffCanvas &&
    prev.isPreview === next.isPreview &&
    prev.isSelected === next.isSelected &&
    prev.disableInteraction === next.disableInteraction &&
    prev.isResizing === next.isResizing &&
    prev.blockClicksOnOffCanvasElements ===
      next.blockClicksOnOffCanvasElements &&
    prev.blockClicksOnMainStageElements ===
      next.blockClicksOnMainStageElements;

  if (thisSelectedInPrev || thisSelectedInNext) {
    if (layoutAndDataEqual && selectionIdentityEqual(prev, next)) return true;
    return false;
  }

  if (!layoutAndDataEqual) return false;
  return true;
}

const TextElementInner = ({
  text,
  MAX_WIDTH,
  offCanvasTexts = [],
  effectiveZoomLevel = 1,
  setOffCanvasTexts = () => {},
  setTexts = () => {},
  texts = [],
  onDragMove,
  handleElementDragEnd,
  textBlockWidth = text.width,
  textGroupHeight = text.height,
  formatListContent,
  getTextStyle,
  setSelectedImage,
  setSelectedTextId = () => {},
  setShowTextBox,
  transformerRef,
  selectedObject,
  setSelectedObject,
  saveToHistory,
  selectedText,
  setEditTextBoxContent = () => {},
  editTextBoxContent = false,
  setShowEditTask = () => {},
  setShowEditNote = () => {},
  showEditTask = false,
  showEditNote = false,
  setSelectedText = () => {},
  handlePartClick = () => {},
  hasUserContent, // Assuming these helper functions are passed down or imported
  estimateKonvaTextHeight = () => {},
  stripHtmlPreserveFormatting,
  hasTaskContent,
  hasNoteContent,
  onClick = () => {},
  setAction,
  ACTIONS,
  action,
  setSelectedObjects = () => {},
  selectedObjects = [],
  setEditing,
  onTransformEnd = () => {},
  onDragEnd,
  calculateGroupHeight,
  canvasWidth = window.innerWidth * 0.45, // Default fallback
  canvasHeight = window.innerHeight, // Default fallback
  canvasResizeTrigger = 0, // Trigger for canvas resize
  isResizing = false, // Whether canvas is currently being resized
  isOffCanvas = false,
  isPreview = false,
  isSelected = false,
  onCopyToOppositeCanvas,
  lockedAction,
  isElementBeingEdited = false,
  isMultiSelectMode = false, // Multi-select mode flag
  isMultiSelectModeRef = null, // Ref to avoid stale closures
  blockClicksOnOffCanvasElements = false,
  blockClicksOnMainStageElements = false,
  handleGroupDragStart = null, // Handler for group drag start
  handleGroupDragMove = null, // Handler for group drag move
  isDraggable = true,
  setPendingChanges = () => {},
  pendingChangesRef = null, // Add ref for synchronous updates
  isSelecting = false, // Selection box is being drawn
  clearDragPreview = () => {},
  crossStageGestureBegin = null,
  crossStageGestureEnd = null,
}) => {
  // Ensure isDraggable is always defined with a fallback
  const isDraggableProp = isDraggable !== undefined ? isDraggable : true;

  // Debug logging to track component rendering

  // Main-canvas text visibility should use full stage height.
  const effectiveCanvasHeight = Math.max(0, Number(canvasHeight) || 0);
  const TRANSFORMER_ANCHOR_BUFFER_PX = 10;
  const textVisibilityDebugEnabled = (() => {
    if (typeof window === "undefined") return false;
    try {
      return (
        window.__canvasTextVisibilityDebug === true ||
        window.localStorage?.getItem("canvasTextVisibilityDebug") === "true"
      );
    } catch (_) {
      return window.__canvasTextVisibilityDebug === true;
    }
  })();

  // Memoize text styles so getTextStyle isn't called repeatedly during render
  const textStylesBySection = useMemo(
    () => ({
      content: getTextStyle(text, "content"),
      task: getTextStyle(text, "task"),
      note: getTextStyle(text, "note"),
    }),
    [text?.textStyles, text?.fontSize],
  );

  let currentYOffset = 10; // Initial padding from the top of the group's content area
  const shapeRef = useRef(); // 👈 Add this
  const lastSelectedAtRef = useRef(0);
  const isDraggingRef = useRef(false); // Track if currently dragging
  const isTransformingRef = useRef(false); // Track if currently transforming
  const doubleClickPendingRef = useRef(false); // Track if double-click is pending to prevent onClick interference
  const dragStartMousePosRef = useRef(null); // Store initial mouse position for group drag
  const dragStartElementPosRef = useRef(null); // Store initial element position (from handleGroupDragStart)
  /** "left" | "right" | null — locked for the current off-canvas text drag (no crossing main column) */
  const offCanvasDragStripSideRef = useRef(null);
  const transformActiveAnchorRef = useRef(null);

  // CRITICAL PERFORMANCE FIX: Track if we're in a group drag to prevent HTML re-renders
  const isInGroupDragRef = useRef(false);
  const flattenedContentRef = useRef(null); // Cache flattened content during group drag

  // Check if this element is part of a group drag
  const isInGroupDrag =
    selectedObjects &&
    selectedObjects.length > 1 &&
    selectedObjects.some(
      (obj) =>
        obj.id === text.id &&
        (obj.type === "Text" || obj.type?.includes("Text")),
    );

  // Update ref to track group drag state
  isInGroupDragRef.current = isInGroupDrag;

  // Cache flattened content when entering group drag
  useEffect(() => {
    if (isInGroupDrag && text.content) {
      const rawContent = text.content || "";
      const safeContent = rawContent
        .replace(/\n/g, "<br>")
        .replace(/<div>/g, "<br>")
        .replace(/<\/div>/g, "")
        .replace(/^<br>/, "")
        .replace(/(<br>\s*){2,}/g, "<br><br>")
        .trim();
      flattenedContentRef.current = flattenNestedLists(safeContent);
    } else if (!isInGroupDrag) {
      // Clear cache when not in group drag
      flattenedContentRef.current = null;
    }
  }, [isInGroupDrag, text.content]);

  // Check if text element is selected
  const isTextSelected = () => {
    if (isSelected) return true;
    if (
      selectedObject?.id === text.id &&
      (selectedObject?.type === "Text" ||
        selectedObject?.type?.includes("Text"))
    ) {
      return true;
    }
    if (selectedObjects && selectedObjects.length > 0) {
      return selectedObjects.some(
        (obj) =>
          obj.id === text.id &&
          (obj.type === "Text" || obj.type?.includes("Text")),
      );
    }
    return false;
  };

  // Helper function to determine pointer events for HTML content
  // CRITICAL: Disable pointer events when text is in multi-selection to prevent text selection
  // CRITICAL: Also disable pointer events when selection box is being drawn to allow selection box drawing
  const getHtmlPointerEvents = () => {
    // If selection box is being drawn, disable pointer events to allow selection box to be drawn over the element
    if (isSelecting) {
      return "none";
    }
    // When editing task or note (including textbox_note), always allow HTML
    // interaction so HTML-based dragging works reliably, regardless of canvas.
    if (showEditTask || showEditNote) {
      return "auto";
    }
    // If text is in multi-selection, disable all pointer events on HTML content
    if (selectedObjects && selectedObjects.length > 1) {
      const isInMultiSelection = selectedObjects.some(
        (obj) =>
          obj.id === text.id &&
          (obj.type === "Text" || obj.type?.includes("Text")),
      );
      if (isInMultiSelection) {
        return "none"; // Disable all pointer events for HTML content in multi-selection
      }
    }
    // For off-canvas texts we generally want HTML (including textbox_note) to be
    // interactive so dragging works reliably.
    if (isOffCanvas) {
      return "auto";
    }
    // Normal behavior (main canvas): allow pointer events when text is selected
    return isTextSelected() ? "auto" : "none";
  };

  // Off-canvas HTML should not show a hand cursor on hover.
  const getHtmlHoverCursor = (forceDefault = false) => {
    if (forceDefault) return "default";
    if (isOffCanvas) return "default";
    return isSelecting || isTextSelected() ? "default" : "pointer";
  };

  // Get z-index for HTML content based on selection state

  // Removed: getInnerDivMask - no longer hiding HTML content
  const getInnerDivMask = () => {
    return { maskImage: "none", WebkitMaskImage: "none" };
  };

  // Removed: getInnerDivClipPath - no longer hiding HTML content
  const getInnerDivClipPath = (
    innerDivTop = 0,
    innerDivLeft = 10,
    innerDivWidth = 0,
    innerDivHeight = 0,
  ) => {
    return "none";
  };

  // Removed: getHtmlClipPath - no longer hiding HTML content
  const getHtmlClipPath = (
    sectionY = 0,
    sectionHeight = totalContentHeight,
    contentLeftOffset = 10,
  ) => {
    return "none";
  };

  // Removed: getHtmlMask - no longer hiding HTML content
  const getHtmlMask = (
    sectionY = 0,
    sectionHeight = totalContentHeight,
    contentLeftOffset = 10,
  ) => {
    return { maskImage: "none", WebkitMaskImage: "none" };
  };

  // Removed: getHtmlVisibility - no longer hiding HTML content
  const getHtmlVisibility = () => {
    return "visible";
  };

  // Legacy function kept for backward compatibility but simplified
  const getInnerDivMask_OLD = (
    innerDivTop = 0,
    innerDivLeft = 10,
    innerDivWidth = 0,
    innerDivHeight = 0,
  ) => {
    // If this element is selected, don't mask
    if (isTextSelected()) {
      return { maskImage: "none", WebkitMaskImage: "none" };
    }

    // Get all selected elements
    const allSelected = [];
    if (selectedObjects && selectedObjects.length > 0) {
      allSelected.push(...selectedObjects);
    } else if (selectedObject) {
      allSelected.push(selectedObject);
    }

    if (allSelected.length === 0) {
      return { maskImage: "none", WebkitMaskImage: "none" };
    }

    // The inner div's bounding box in canvas coordinates
    const innerDivBBox = {
      x: (text.x || 0) + innerDivLeft,
      y: (text.y || 0) + innerDivTop,
      width: innerDivWidth,
      height: innerDivHeight,
    };

    // Collect all overlapping areas (in inner div-relative coordinates)
    const maskAreas = [];

    allSelected.forEach((selected) => {
      // Skip if it's the same element
      if (selected.id === text.id) {
        return;
      }

      // Get selected element's exact bounding box in canvas coordinates (accounting for rotation)
      let selectedBBox = null;
      const rotation = selected.rotation || 0;

      if (selected.type === "Rect") {
        const x = selected.x || 0;
        const y = selected.y || 0;
        const width = selected.width || 0;
        const height = selected.height || 0;

        if (rotation && rotation !== 0) {
          const angle = (rotation * Math.PI) / 180;
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          const centerX = x + width / 2;
          const centerY = y + height / 2;
          const corners = [
            { x: -width / 2, y: -height / 2 },
            { x: width / 2, y: -height / 2 },
            { x: width / 2, y: height / 2 },
            { x: -width / 2, y: height / 2 },
          ];
          const rotatedCorners = corners.map((corner) => ({
            x: centerX + corner.x * cos - corner.y * sin,
            y: centerY + corner.x * sin + corner.y * cos,
          }));
          const xs = rotatedCorners.map((c) => c.x);
          const ys = rotatedCorners.map((c) => c.y);
          selectedBBox = {
            x: Math.min(...xs),
            y: Math.min(...ys),
            width: Math.max(...xs) - Math.min(...xs),
            height: Math.max(...ys) - Math.min(...ys),
          };
        } else {
          selectedBBox = { x, y, width, height };
        }
      } else if (selected.type === "Circle") {
        const radius =
          selected.radius ||
          Math.max(selected.radiusX || 0, selected.radiusY || 0);
        const centerX = selected.x || 0;
        const centerY = selected.y || 0;
        if (rotation && rotation !== 0) {
          const angle = (rotation * Math.PI) / 180;
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          const width = radius * 2;
          const height = radius * 2;
          const corners = [
            { x: -width / 2, y: -height / 2 },
            { x: width / 2, y: -height / 2 },
            { x: width / 2, y: height / 2 },
            { x: -width / 2, y: height / 2 },
          ];
          const rotatedCorners = corners.map((corner) => ({
            x: centerX + corner.x * cos - corner.y * sin,
            y: centerY + corner.x * sin + corner.y * cos,
          }));
          const xs = rotatedCorners.map((c) => c.x);
          const ys = rotatedCorners.map((c) => c.y);
          selectedBBox = {
            x: Math.min(...xs),
            y: Math.min(...ys),
            width: Math.max(...xs) - Math.min(...xs),
            height: Math.max(...ys) - Math.min(...ys),
          };
        } else {
          selectedBBox = {
            x: centerX - radius,
            y: centerY - radius,
            width: radius * 2,
            height: radius * 2,
          };
        }
      } else if (selected.type === "Text" || selected.type?.includes("Text")) {
        const x = selected.x || 0;
        const y = selected.y || 0;
        const width = selected.width || 0;
        const height = selected.height || 0;

        if (rotation && rotation !== 0) {
          const angle = (rotation * Math.PI) / 180;
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          const centerX = x + width / 2;
          const centerY = y + height / 2;
          const corners = [
            { x: -width / 2, y: -height / 2 },
            { x: width / 2, y: -height / 2 },
            { x: width / 2, y: height / 2 },
            { x: -width / 2, y: height / 2 },
          ];
          const rotatedCorners = corners.map((corner) => ({
            x: centerX + corner.x * cos - corner.y * sin,
            y: centerY + corner.x * sin + corner.y * cos,
          }));
          const xs = rotatedCorners.map((c) => c.x);
          const ys = rotatedCorners.map((c) => c.y);
          selectedBBox = {
            x: Math.min(...xs),
            y: Math.min(...ys),
            width: Math.max(...xs) - Math.min(...xs),
            height: Math.max(...ys) - Math.min(...ys),
          };
        } else {
          selectedBBox = { x, y, width, height };
        }
      } else if (selected.type === "Image") {
        const centerX = selected.x || 0;
        const centerY = selected.y || 0;
        const width = selected.width || 0;
        const height = selected.height || 0;

        if (rotation && rotation !== 0) {
          const angle = (rotation * Math.PI) / 180;
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          const corners = [
            { x: -width / 2, y: -height / 2 },
            { x: width / 2, y: -height / 2 },
            { x: width / 2, y: height / 2 },
            { x: -width / 2, y: height / 2 },
          ];
          const rotatedCorners = corners.map((corner) => ({
            x: centerX + corner.x * cos - corner.y * sin,
            y: centerY + corner.x * sin + corner.y * cos,
          }));
          const xs = rotatedCorners.map((c) => c.x);
          const ys = rotatedCorners.map((c) => c.y);
          selectedBBox = {
            x: Math.min(...xs),
            y: Math.min(...ys),
            width: Math.max(...xs) - Math.min(...xs),
            height: Math.max(...ys) - Math.min(...ys),
          };
        } else {
          selectedBBox = {
            x: centerX - width / 2,
            y: centerY - height / 2,
            width,
            height,
          };
        }
      }

      if (!selectedBBox) {
        return;
      }

      // Calculate intersection (overlapping area) in canvas coordinates
      const intersectLeft = Math.max(innerDivBBox.x, selectedBBox.x);
      const intersectTop = Math.max(innerDivBBox.y, selectedBBox.y);
      const intersectRight = Math.min(
        innerDivBBox.x + innerDivBBox.width,
        selectedBBox.x + selectedBBox.width,
      );
      const intersectBottom = Math.min(
        innerDivBBox.y + innerDivBBox.height,
        selectedBBox.y + selectedBBox.height,
      );

      // If there's an intersection, convert to INNER DIV-RELATIVE coordinates
      if (intersectLeft < intersectRight && intersectTop < intersectBottom) {
        // Convert to inner div-relative coordinates (0,0 is top-left of inner div)
        const relativeLeft = Math.max(0, intersectLeft - innerDivBBox.x);
        const relativeTop = Math.max(0, intersectTop - innerDivBBox.y);
        const relativeRight = Math.min(
          innerDivBBox.width,
          intersectRight - innerDivBBox.x,
        );
        const relativeBottom = Math.min(
          innerDivBBox.height,
          intersectBottom - innerDivBBox.y,
        );

        // Only add if the area is valid and within inner div bounds
        if (relativeLeft < relativeRight && relativeTop < relativeBottom) {
          maskAreas.push({
            left: relativeLeft,
            top: relativeTop,
            right: relativeRight,
            bottom: relativeBottom,
          });
        }
      }
    });

    // If no overlapping areas, don't mask
    if (maskAreas.length === 0) {
      return { maskImage: "none", WebkitMaskImage: "none" };
    }

    // For mask-image, we'll use linear gradients to create a mask that hides the overlapping areas
    // We create gradients for each overlapping area
    const maskImages = maskAreas
      .map((area) => {
        const leftPercent = (area.left / innerDivWidth) * 100;
        const rightPercent = (area.right / innerDivWidth) * 100;
        const topPercent = (area.top / innerDivHeight) * 100;
        const bottomPercent = (area.bottom / innerDivHeight) * 100;

        // Create a mask using multiple gradients to hide the rectangle
        // We need to create a mask that shows everything except the rectangle
        // This is complex with mask-image, so we'll use clip-path instead for now
        // But return the mask style for potential future use
        return null; // Will use clip-path instead
      })
      .filter(Boolean);

    // For now, return none and use clip-path
    return { maskImage: "none", WebkitMaskImage: "none" };
  };

  // Removed: Old getInnerDivClipPath and getHtmlClipPath implementations - no longer hiding HTML content

  // Handle mouse down on HTML content to enable dragging
  // This prevents text selection and manually triggers Konva drag
  const handleHtmlMouseDown = (e) => {
    // CRITICAL: If text is in multi-selection, don't handle HTML mouse events
    // Let them pass through to the Konva Group for group dragging
    // This prevents conflicts between custom drag handler and group drag handler
    if (selectedObjects && selectedObjects.length > 1) {
      const isInMultiSelection = selectedObjects.some(
        (obj) =>
          obj.id === text.id &&
          (obj.type === "Text" || obj.type?.includes("Text")),
      );
      if (isInMultiSelection) {
        // Don't prevent default or stop propagation - let the event bubble to Konva Group
        // This allows the Group's native drag to work properly
        return; // Don't handle the event - let Konva Group handle it
      }
    }

    // If in drawing mode, allow events to pass through to Background
    if (shouldAllowPointerEventsThrough()) {
      return; // Don't handle the event, let it pass through
    }

    // Only allow drag if text element is marked draggable.
    // Do NOT rely on isDraggingDisabled here – textbox_note/task/note edit
    // modes can temporarily flag selection as "editable" even though we still
    // want HTML-based dragging to work.
    if (!isDraggableProp) {
      return;
    }

    // CRITICAL: Prevent text selection more aggressively
    e.preventDefault();
    e.stopPropagation();

    // Clear any existing text selection to prevent interference
    if (window.getSelection) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        selection.removeAllRanges();
      }
    }

    // Prevent text selection on the target element
    if (e.target) {
      e.target.style.userSelect = "none";
      e.target.style.webkitUserSelect = "none";
      e.target.style.mozUserSelect = "none";
      e.target.style.msUserSelect = "none";

      // Also prevent drag events on the HTML element
      e.target.draggable = false;
    }

    const groupNode = shapeRef.current;
    if (!groupNode || !groupNode.draggable()) {
      return;
    }

    const stage = groupNode.getStage();
    if (!stage) {
      return;
    }

    // Add dragstart and selectstart event listeners to prevent HTML drag/selection behavior
    const preventDragStart = (dragEvent) => {
      dragEvent.preventDefault();
      dragEvent.stopPropagation();
      return false;
    };

    const preventSelectStart = (selectEvent) => {
      selectEvent.preventDefault();
      selectEvent.stopPropagation();
      return false;
    };

    // Add the event listeners to the target and its parent elements
    let elementsWithDragPrevention = [];
    if (e.target) {
      e.target.addEventListener("dragstart", preventDragStart);
      e.target.addEventListener("selectstart", preventSelectStart);
      elementsWithDragPrevention.push(e.target);

      // Also add to parent elements to catch bubbled events
      let parent = e.target.parentElement;
      while (parent && elementsWithDragPrevention.length < 5) {
        // Limit to 5 levels up
        parent.addEventListener("dragstart", preventDragStart);
        parent.addEventListener("selectstart", preventSelectStart);
        elementsWithDragPrevention.push(parent);
        parent = parent.parentElement;
      }
    }

    // Get initial positions
    // Convert client coordinates to stage coordinates
    const stageBox = stage.container().getBoundingClientRect();
    const stageScaleX = stage.scaleX();
    const stageScaleY = stage.scaleY();
    const stageX = stage.x();
    const stageY = stage.y();

    const startStagePos = {
      x: (e.clientX - stageBox.left) / stageScaleX - stageX,
      y: (e.clientY - stageBox.top) / stageScaleY - stageY,
    };
    const startGroupPos = groupNode.position();

    // CRITICAL: Trigger onDragStart manually to set the dragging flag
    isDraggingRef.current = true;
    setShowTextBox(true);

    // CRITICAL: Mark this node as being dragged via HTML so transformer
    // anchor logic (which checks node dragging state) can hide anchors
    // just like it does for native Konva drags.
    if (typeof groupNode.setAttr === "function") {
      groupNode.setAttr("_isHtmlDragging", true);
    }

    // Set up drag handlers
    const handleMouseMove = (moveEvent) => {
      // CRITICAL: Double-check that we're not in multi-selection
      // This prevents conflicts with the group drag handler
      if (selectedObjects && selectedObjects.length > 1) {
        const isInMultiSelection = selectedObjects.some(
          (obj) =>
            obj.id === text.id &&
            (obj.type === "Text" || obj.type?.includes("Text")),
        );
        if (isInMultiSelection) {
          // Remove event listeners and let group drag handle it
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
          return;
        }
      }

      const currentStagePos = {
        x: (moveEvent.clientX - stageBox.left) / stageScaleX - stageX,
        y: (moveEvent.clientY - stageBox.top) / stageScaleY - stageY,
      };

      const deltaX = currentStagePos.x - startStagePos.x;
      const deltaY = currentStagePos.y - startStagePos.y;

      let newPos = {
        x: startGroupPos.x + deltaX,
        y: startGroupPos.y + deltaY,
      };

      let displayX = newPos.x;
      let displayY = newPos.y;

      // Pointer is over source stage: use current stage bbox so we don't use stale bounds (scroll/resize)
      const elementStage = groupNode.getStage();
      const currentStageBox = elementStage
        ?.container()
        ?.getBoundingClientRect?.();
      const pointerOverSourceStage = currentStageBox
        ? moveEvent.clientX >= currentStageBox.left &&
          moveEvent.clientX <= currentStageBox.right &&
          moveEvent.clientY >= currentStageBox.top &&
          moveEvent.clientY <= currentStageBox.bottom
        : moveEvent.clientX >= stageBox.left &&
          moveEvent.clientX <= stageBox.right &&
          moveEvent.clientY >= stageBox.top &&
          moveEvent.clientY <= stageBox.bottom;

      // Keep HTML drag visually moving on BOTH canvases while pointer is still over its source stage.
      // Previously this only ran for main-canvas text, which made off-canvas HTML text appear frozen.
      if (pointerOverSourceStage) {
        groupNode.position(newPos);
        displayX = newPos.x;
        displayY = newPos.y;
      }

      // CRITICAL: For drag preview, mimic Konva's dragMove signature.
      // newAttrs.x/y and _stageX/_stageY should be in the *source stage*
      // coordinate system (like Circle/Rect), and cross‑canvas conversion
      // is handled centrally in Canvas.handleDragPreview.
      const newAttrs = {
        ...text,
        id: text.id,
        x: newPos.x,
        y: newPos.y,
        width: text.width,
        height: text.height,
        rotation: text.rotation || 0,
        text: text.text,
        type: "Text",
        strokeWidth: text.strokeWidth ?? 0,
        _stageX: newPos.x,
        _stageY: newPos.y,
      };

      // CRITICAL: Create a synthetic event object that mimics Konva's event structure
      // This ensures handleGroupDragMove/handleDragPreview receive the event
      // in the same shape as native Konva drags.
      const syntheticEvent = {
        evt: moveEvent,
        target: groupNode,
      };

      // Match Circle/Rect behavior: always call onDragMove for individual drags.
      // Canvas.handleDragPreview will decide when to show/clear cross‑canvas ghosts.
      onDragMove?.(newAttrs, syntheticEvent);
    };

    const handleMouseUp = (upEvent) => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);

      // CRITICAL: Restore text selection properties that were modified during drag
      if (upEvent.target) {
        upEvent.target.style.userSelect = "";
        upEvent.target.style.webkitUserSelect = "";
        upEvent.target.style.mozUserSelect = "";
        upEvent.target.style.msUserSelect = "";
        upEvent.target.draggable = true; // Restore draggable property
      }

      // Clean up dragstart and selectstart event listeners
      elementsWithDragPrevention.forEach((element) => {
        element.removeEventListener("dragstart", preventDragStart);
        element.removeEventListener("selectstart", preventSelectStart);
      });

      // CRITICAL: Get final position and trigger onDragEnd
      // Recompute final position from mouse location and initial drag state instead of relying
      // on the Konva node's last x/y. This avoids cases where the node position was frozen at
      // the main/off-canvas seam during cross-canvas preview (main → off → main).
      let finalX = groupNode.x();
      let finalY = groupNode.y();

      try {
        // CRITICAL: Use startStagePos/startGroupPos from closure (set in handleHtmlMouseDown).
        // Do NOT use dragStartMousePosRef/dragStartElementPosRef — those are only set for Konva group drag.
        if (
          typeof upEvent.clientX === "number" &&
          typeof upEvent.clientY === "number" &&
          startStagePos &&
          startGroupPos
        ) {
          const stage = groupNode.getStage();
          if (stage) {
            const stageBoxAtUp = stage.container().getBoundingClientRect();
            const stageScaleX = stage.scaleX();
            const stageScaleY = stage.scaleY();
            const stageX = stage.x();
            const stageY = stage.y();

            const endStagePos = {
              x: (upEvent.clientX - stageBoxAtUp.left) / stageScaleX - stageX,
              y: (upEvent.clientY - stageBoxAtUp.top) / stageScaleY - stageY,
            };

            const deltaX = endStagePos.x - startStagePos.x;
            const deltaY = endStagePos.y - startStagePos.y;
            const recomputedPos = {
              x: startGroupPos.x + deltaX,
              y: startGroupPos.y + deltaY,
            };
            finalX = recomputedPos.x;
            finalY = recomputedPos.y;
            groupNode.position(recomputedPos);
          }
        }
      } catch (err) {
        // Ignore errors when recomputing final position
      }

      // Get current rotation from the Konva node (may have been updated above)
      let nodeRotation = groupNode.rotation();
      nodeRotation = nodeRotation % 360;
      if (nodeRotation < 0) nodeRotation += 360;

      // Create currentText with properties from node
      const currentText = {
        ...text,
        x: finalX,
        y: finalY,
        rotation: nodeRotation,
        scaleX: groupNode.scaleX(),
        scaleY: groupNode.scaleY(),
      };

      // Get actual mouse position from the event
      const mouseClientX = upEvent.clientX;
      const mouseClientY = upEvent.clientY;

      // CRITICAL: Call onDragEnd to update state properly
      onDragEnd(
        text.id,
        finalX,
        finalY,
        mouseClientX,
        mouseClientY,
        currentText,
      );

      // Clear HTML dragging marker so transformer anchors can show again
      if (typeof groupNode.setAttr === "function") {
        groupNode.setAttr("_isHtmlDragging", false);
      }

      // Reset dragging flag after a small delay
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 100);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // Removed: Old getInnerDivClipPath and getHtmlClipPath implementations - no longer hiding HTML content

  // Calculate total content height
  const calculateTotalContentHeight = () => {
    let totalHeight = 10; // Initial padding

    // Add content section height
    if (hasUserContent(text)) {
      const contentFontSize =
        text.fontSize || textStylesBySection.content.fontSize || 14;
      const contentFontFamily = "Roboto";
      const rawContent = text.content || "";
      const safeContent = rawContent
        .replace(/\n/g, "<br>")
        .replace(/<div>/g, "<br>")
        .replace(/<\/div>/g, "")
        .replace(/^<br>/, "")
        .replace(/(<br>\s*){2,}/g, "<br><br>")
        .trim();

      const flattenedContent = flattenNestedLists(safeContent);

      // Calculate actual available width considering canvas boundary
      // ✅ Use integer width for consistency
      const actualContentWidth = Math.floor(textBlockWidth - 20); // Use full width - let container clip-path handle clipping

      const renderedContentHeight = measureHtmlHeight(
        flattenedContent,
        actualContentWidth,
        contentFontSize,
        contentFontFamily,
      );
      totalHeight += renderedContentHeight;
    }

    // Add parts by type section height
    if (
      text.partsByType &&
      Object.keys(text.partsByType || {}).some(
        (key) => key && text.partsByType[key]?.parts?.length > 0,
      )
    ) {
      Object.entries(text.partsByType)
        .sort((a, b) => (a[1].order || 9999) - (b[1].order || 9999))
        .forEach(([typeName, typeData]) => {
          if (!typeData.parts || typeData.parts.length === 0) return;

          const typeFontSize =
            text.fontSize || text.textStyles?.type?.fontSize || 16;
          const typeFontFamily = "Roboto";
          const typeTextHeight = estimateKonvaTextHeight(
            typeName,
            typeFontSize,
            typeFontFamily,
            textBlockWidth - 20,
          );
          // Reduce spacing more for big font size
          const spacingReduction = text.fontSize > 14 ? 16 : 5;
          totalHeight += typeTextHeight - spacingReduction;

          typeData.parts.forEach((partData) => {
            // Skip null or undefined partData entries
            if (!partData) return;

            const { part, qty, unit, itemCodeNote, note, highlighted } =
              partData;
            const partFontSize =
              text.fontSize || text.textStyles?.part?.fontSize || 14;
            const partFontFamily = "Roboto";
            const qtySuffix =
              qty && parseInt(qty) > 0
                ? ` - Qty-${qty} ${unit || ""}`
                : ` ${unit || ""}`;
            const sanitizedPartHtml = sanitizeAllowMark(part || "");
            const hasExistingMark = /<mark/i.test(part || "");
            const partHtmlSegment = hasExistingMark
              ? sanitizedPartHtml
              : highlighted
                ? `<mark>${sanitizedPartHtml}</mark>`
                : sanitizedPartHtml;
            // Add zero-width space before qty to create a break opportunity
            // This allows qty to wrap to next line if part text is too long
            const qtySeparator =
              qtySuffix && qtySuffix.trim() !== "" ? " \u200B" : ""; // Zero-width space + regular space
            const content = `${part || ""}${qtySeparator}${qtySuffix}`;
            // Wrap qty in span to ensure it can break/wrap properly
            const qtyHtml =
              qtySuffix && qtySuffix.trim() !== ""
                ? `<span style="display: inline; white-space: normal; word-break: break-word; overflow-wrap: break-word;">${escapeHtml(
                    qtySuffix,
                  )}</span>`
                : "";
            const contentHtml = `${partHtmlSegment}${qtySeparator}${qtyHtml}`;
            // Normalize contentHtml for accurate measurement (matches what will be rendered)
            const normalizedForMeasurement = normalizeMarkColor(
              contentHtml,
              "yellow",
            );
            const flattenedContentHtml = flattenNestedLists(
              normalizedForMeasurement,
            );

            // Use the HTML version for accurate height measurement that matches rendering
            // This ensures qty wrapping is properly accounted for in height calculation
            // CRITICAL: Apply the SAME aggressive cleaning as rendering to ensure height matches exactly
            let safeContentForMeasurement = flattenedContentHtml
              .replace(/\n/g, "<br>")
              .replace(/<div>/g, "<br>")
              .replace(/<\/div>/g, "")
              .replace(/^<br>/, "")
              .replace(/(<br>\s*){2,}/g, "<br><br>")
              .trim();

            // Remove trailing paragraph tags and their content that might add spacing
            safeContentForMeasurement = safeContentForMeasurement
              .replace(/<\/p>$/i, "")
              .replace(/^<p[^>]*>/i, "")
              .trim();

            // Remove trailing <br> tags and whitespace
            safeContentForMeasurement = safeContentForMeasurement
              .replace(/(<br\s*\/?>)+$/i, "") // Remove any remaining trailing <br> tags
              .replace(/[\s\u00A0\u200B\u2009\u2008]+$/, "") // Remove any remaining trailing whitespace
              .trim();

            // CRITICAL: Final aggressive cleanup - remove any trailing whitespace that could create spacing
            safeContentForMeasurement = safeContentForMeasurement
              .replace(/[\s\u00A0\u200B\u2009\u2008\u200C\u200D\uFEFF]+$/, "") // Remove all types of trailing whitespace
              .replace(/(<br\s*[^>]*>)+$/i, "") // Remove any remaining <br> tags with attributes
              .trim();

            // CRITICAL: Remove trailing whitespace from nested elements (like spans, marks, etc.)
            // Do this multiple times to catch nested elements
            for (let i = 0; i < 5; i++) {
              // Remove whitespace before closing tags (handles nested elements)
              safeContentForMeasurement = safeContentForMeasurement.replace(
                /([^>\s])([\s\u00A0\u200B\u2009\u2008\u200C\u200D\uFEFF\n\r\t]+)(<\/[^>]+>)/g,
                "$1$3",
              );
              // Also handle cases where there might be whitespace between tags
              safeContentForMeasurement = safeContentForMeasurement.replace(
                /(<\/[^>]+>)([\s\u00A0\u200B\u2009\u2008\u200C\u200D\uFEFF\n\r\t]+)(<\/[^>]+>)/g,
                "$1$3",
              );
            }

            // CRITICAL: Remove any trailing newlines or spaces that might create an extra line with pre-wrap
            safeContentForMeasurement = safeContentForMeasurement
              .replace(/\s+$/, "") // Remove any trailing whitespace of any kind
              .replace(/\n+$/, "") // Remove any trailing newlines
              .replace(/\r+$/, "") // Remove any trailing carriage returns
              .trim();

            // CRITICAL: Final pass - remove any remaining trailing content that could create spacing
            while (
              safeContentForMeasurement.endsWith(" ") ||
              safeContentForMeasurement.endsWith("\n") ||
              safeContentForMeasurement.endsWith("\r") ||
              safeContentForMeasurement.endsWith("\t") ||
              safeContentForMeasurement.match(
                /[\s\u00A0\u200B\u2009\u2008\u200C\u200D\uFEFF]$/,
              )
            ) {
              safeContentForMeasurement = safeContentForMeasurement
                .replace(
                  /[\s\u00A0\u200B\u2009\u2008\u200C\u200D\uFEFF\n\r\t]+$/,
                  "",
                )
                .trim();
            }

            // Calculate actual available width for parts considering canvas boundary
            // ✅ Use integer width for consistency
            const actualPartWidth = Math.floor(textBlockWidth - 10); // Use full width - let container clip-path handle clipping

            // CRITICAL: Measure height, but account for Math.ceil rounding that adds extra space
            const measuredHeight = measureHtmlHeight(
              safeContentForMeasurement, // Use HTML version to match rendered output
              actualPartWidth,
              partFontSize,
              partFontFamily,
            );
            // CRITICAL: For parts, use Math.floor to prevent extra height from Math.ceil rounding
            const lineHeight1_4 = partFontSize * 1.4;
            const estimatedLines = Math.ceil(measuredHeight / lineHeight1_4);
            // If it's a single line, use the actual line height instead of rounded-up measurement
            const partTextHeight =
              estimatedLines === 1
                ? Math.floor(lineHeight1_4) // Single line: use floor of line-height 1.4
                : measuredHeight; // Multi-line: use measured height as-is
            totalHeight += partTextHeight;

            // Helper function to calculate note height
            const calculateNoteHeight = (noteContent) => {
              if (!noteContent) return 0;
              const safeNoteContent = noteContent
                .replace(/\n/g, "<br>")
                .replace(/<div>/g, "<br>")
                .replace(/<\/div>/g, "")
                .replace(/^<br>/, "")
                .replace(/(<br>\s*){2,}/g, "<br><br>")
                .trim();

              // CRITICAL: Check if note is only <br> tags (with or without attributes) - these create unwanted spacing
              // Match the same check used in rendering to ensure height calculation matches rendering behavior
              const onlyBrTagsPattern = /^(<br\s*[^>]*>\s*)+$/i;
              if (onlyBrTagsPattern.test(safeNoteContent.trim())) {
                return 0; // Skip height calculation for notes that only contain <br> tags (they won't be rendered)
              }

              const flattenedNoteContent = flattenNestedLists(safeNoteContent);

              const noteFontSize =
                text.fontSize || textStylesBySection.note.fontSize || 14;
              const noteFontFamily = "Roboto";

              // Calculate actual available width for part notes considering canvas boundary
              // ✅ Use integer width for consistency
              const actualPartNoteWidth = Math.floor(textBlockWidth - 10); // Use full width - let container clip-path handle clipping

              return measureHtmlHeight(
                flattenedNoteContent,
                actualPartNoteWidth,
                noteFontSize,
                noteFontFamily,
              );
            };

            // Add itemCodeNote height first (system-generated note from Item Code Table)
            if (itemCodeNote) {
              totalHeight += calculateNoteHeight(itemCodeNote);
            }

            // Add user's note height (if present)
            if (note) {
              totalHeight += calculateNoteHeight(note);
            }
          });
        });
    }

    // Add task section height
    if (hasTaskContent(text)) {
      const taskFontSize =
        text.fontSize || textStylesBySection.task.fontSize || 14;
      const taskFontFamily = textStylesBySection.task.fontFamily || "Roboto";
      totalHeight += 15; // Task label space

      const safeTaskContent = text.task
        .replace(/<div>/g, "<br>")
        .replace(/<\/div>/g, "")
        .replace(/^<br>/, "")
        .replace(/(<br>\s*){2,}/g, "<br><br>")
        .trim();
      const flattenedTaskContent = flattenNestedLists(safeTaskContent);

      // Calculate actual available width for task considering canvas boundary
      // ✅ Use integer width for consistency
      const actualTaskWidth = Math.floor(textBlockWidth - 20); // Use full width - let container clip-path handle clipping

      const renderedTaskHeight = measureHtmlHeight(
        flattenedTaskContent,
        actualTaskWidth,
        taskFontSize,
        taskFontFamily,
      );
      totalHeight += renderedTaskHeight;
    }

    // Add note section height
    if (hasNoteContent(text)) {
      const noteFontSize =
        text.fontSize || textStylesBySection.note.fontSize || 14;
      const noteFontFamily = textStylesBySection.note.fontFamily || "Roboto";
      totalHeight += 15; // Note label space

      const safeNoteContent = text.note
        .replace(/<div>/g, "<br>")
        .replace(/<\/div>/g, "")
        .replace(/^<br>/, "")
        .replace(/(<br>\s*){2,}/g, "<br><br>")
        .trim();
      const flattenedNoteContent = flattenNestedLists(safeNoteContent);

      // Calculate actual available width for note considering canvas boundary
      // ✅ Use integer width for consistency
      const actualNoteWidth = Math.floor(textBlockWidth - 20); // Use full width - let container clip-path handle clipping

      const renderedNoteHeight = measureHtmlHeight(
        flattenedNoteContent,
        actualNoteWidth,
        noteFontSize,
        noteFontFamily,
      );
      totalHeight += renderedNoteHeight;
    }

    return totalHeight;
  };

  // Conditional minimum height based on font size
  const getMinHeight = () => {
    if (text.fontSize === 11) return 30; // Small font - most reduction
    if (text.fontSize === 14 || text.fontSize === "14") return 35; // Medium font - more reduction
    return 43; // Big font - keep at 45
  };

  // PERFORMANCE: Cache expensive HTML height calculations so they don't rerun on every drag frame.
  const totalContentHeight = useMemo(
    () => Math.max(calculateTotalContentHeight(), getMinHeight()),
    [
      text?.content,
      text?.task,
      text?.note,
      text?.fontSize,
      text?.textStyles,
      text?.partsByType,
      textBlockWidth,
    ],
  );
  // Only use stored height when it was set by our resize (onTransformEnd). Otherwise always totalContentHeight so selection never injects extra height.
  const effectiveContentHeight = totalContentHeight;
  const prevCanvasHeightRef = useRef(Number(canvasHeight) || 0);
  const prevContentHeightRef = useRef(Number(effectiveContentHeight) || 0);

  // Keep main-canvas texts inside visible stage bounds when content height changes
  // (typing, task/note edits, parts add/remove, part note edits).
  // REMOVED: This aggressive Y clamping was preventing text elements from reaching 
  // the bottom of the canvas. Text elements now behave like rectangles without this constraint.
  useEffect(() => {
    // No-op - removed aggressive Y clamping to match rectangle behavior
  }, []);

  // Solo-selected text: Group width/height often updates from React after the global Transformer
  // attaches; Konva Transformer does not always pick that up. Panel clicks forced a re-render and
  // masked the bug. Sync bounds whenever layout-derived dimensions change.
  const selectedObjectsLen = selectedObjects?.length ?? 0;
  React.useLayoutEffect(() => {
    if (
      !isSelected ||
      !transformerRef?.current ||
      !shapeRef.current ||
      selectedObjectsLen > 1
    ) {
      return;
    }
    try {
      const tr = transformerRef.current;
      const nodes = typeof tr.nodes === "function" ? tr.nodes() : [];
      if (!nodes.length || nodes[0] !== shapeRef.current) return;
      if (typeof tr.forceUpdate === "function") {
        tr.forceUpdate();
      }
      const layer = tr.getLayer?.();
      if (layer && typeof layer.batchDraw === "function") {
        layer.batchDraw();
      }
    } catch (_) {
      // ignore
    }
  }, [
    isSelected,
    selectedObjectsLen,
    textBlockWidth,
    effectiveContentHeight,
    text.id,
    transformerRef,
  ]);

  const markSelectedNow = () => {
    lastSelectedAtRef.current = Date.now();
  };

  const toggleThisTextInMultiSelection = (evt) => {
    const currentSelectedObjects = selectedObjects || [];
    const isAlreadySelected = currentSelectedObjects.some(
      (obj) => obj.id === text.id && String(obj.type || "").includes("Text"),
    );
    if (!isAlreadySelected) {
      return false;
    }

    const updatedSelection = currentSelectedObjects.filter(
      (obj) => !(obj.id === text.id && String(obj.type || "").includes("Text")),
    );
    const nextPrimary = updatedSelection[0] || null;

    try {
      flushSync(() => {
        setSelectedObjects(updatedSelection);
        setSelectedObject(nextPrimary);
      });
    } catch (_err) {
      setSelectedObjects(updatedSelection);
      setSelectedObject(nextPrimary);
    }

    if (nextPrimary) {
      const isTextPrimary =
        nextPrimary.type === "Text" ||
        String(nextPrimary.type || "").includes("Text");
      if (isTextPrimary) {
        const nextText =
          texts?.find((t) => t.id === nextPrimary.id) ||
          offCanvasTexts?.find((t) => t.id === nextPrimary.id);
        setSelectedText({
          ...nextPrimary,
          ...(nextText || {}),
          type: "Text",
          selectedContent: nextText?.selectedContent ?? null,
        });
        setSelectedTextId(nextPrimary.id);
        setShowTextBox(true);
      } else {
        setSelectedText(null);
        setSelectedTextId(null);
        setShowTextBox(false);
      }
    } else {
      setSelectedText(null);
      setSelectedTextId(null);
      setShowTextBox(false);
    }

    evt?.evt?.stopPropagation?.();
    evt?.evt?.preventDefault?.();
    evt.cancelBubble = true;
    return true;
  };

  /** Multi-select + another element selected: merge this text via canvas onClick (TYPE heading, PARTS TYPE hit, etc.). */
  const delegateMultiSelectMergeText = (rawEvt) => {
    const currentIsMultiSelectMode =
      isMultiSelectModeRef?.current ?? isMultiSelectMode;
    const isSameElement = selectedObject && selectedObject.id === text.id;
    if (
      !currentIsMultiSelectMode ||
      !selectedObject ||
      isSameElement
    ) {
      return false;
    }
    if (
      (!isOffCanvas && blockClicksOnMainStageElements) ||
      (isOffCanvas && blockClicksOnOffCanvasElements)
    ) {
      return false;
    }
    const latestText = texts?.find((t) => t.id === text.id) || text;
    const nativeEvt =
      rawEvt?.nativeEvent ?? rawEvt?.evt ?? rawEvt ?? undefined;
    setTexts((prevTexts) =>
      prevTexts.map((t) => ({ ...t, selectedContent: null })),
    );
    setEditTextBoxContent(false);
    setShowEditTask(false);
    setShowEditNote(false);
    setSelectedImage(null);
    transformerRef?.current?.nodes([]);
    setShowTextBox(true);
    setSelectedTextId(latestText.id);
    setSelectedText({ ...latestText, selectedContent: null });
    const konvaEvent = {
      target: {
        getClassName: () => "Group",
        attrs: {
          id: text.id,
          type: "Text",
          name: text.name,
          x: text.x || 0,
          y: text.y || 0,
          width: text.width || 0,
          height: totalContentHeight || 0,
          fontSize: text.fontSize || 0,
        },
      },
      evt: nativeEvt,
    };
    markSelectedNow();
    onClick(konvaEvent);
    return true;
  };

  /** Part note click while this text is in a multi-selection: remove this text from the group (matches canvas toggle behavior). */
  const removeThisTextFromMultiSelectionIfGrouped = (domEvt) => {
    const currentIsMultiSelectMode =
      isMultiSelectModeRef?.current ?? isMultiSelectMode;
    if (!currentIsMultiSelectMode) return false;

    const selKey = (id) =>
      id === undefined || id === null ? "" : String(id);
    const merged = [];
    const seen = new Set();
    if (selectedObjects?.length) {
      for (const obj of selectedObjects) {
        if (obj?.id == null) continue;
        const k = selKey(obj.id);
        if (!seen.has(k)) {
          merged.push(obj);
          seen.add(k);
        }
      }
    }
    if (selectedObject?.id != null) {
      const k = selKey(selectedObject.id);
      if (!seen.has(k)) {
        merged.push(selectedObject);
        seen.add(k);
      }
    }
    const textKey = selKey(text.id);
    if (merged.length < 2 || !merged.some((o) => selKey(o.id) === textKey)) {
      return false;
    }

    const nextSel = merged.filter((o) => selKey(o.id) !== textKey);
    if (nextSel.length === 0) {
      setSelectedObjects([]);
      setSelectedObject(null);
      setSelectedText(null);
      setShowTextBox(false);
      setSelectedTextId(null);
    } else {
      setSelectedObjects(nextSel);
      const newPrimary = nextSel[0];
      setSelectedObject(newPrimary);
      const isTxt =
        newPrimary.type === "Text" ||
        String(newPrimary.type || "").includes("Text");
      if (isTxt) {
        const tw =
          texts?.find((t) => selKey(t.id) === selKey(newPrimary.id)) ||
          offCanvasTexts?.find((t) => selKey(t.id) === selKey(newPrimary.id));
        setSelectedText({
          ...newPrimary,
          ...(tw || {}),
          type: "Text",
          selectedContent: tw?.selectedContent ?? null,
        });
        setShowTextBox(true);
        setSelectedTextId(newPrimary.id);
      } else {
        setSelectedText(null);
        setShowTextBox(false);
        setSelectedTextId(null);
      }
    }
    domEvt?.stopPropagation?.();
    return true;
  };

  // CRITICAL: Allow double-click if:
  // 1. Text element is selected OR we're switching views (allow even if selectedObject is temporarily null)
  // 2. AND either:
  //    - 500ms have passed since last selection (normal case)
  //    - OR we're clicking on a different part than the currently selected one (fast part switching)
  //    - OR we're switching between part view and note view of the same part (view switching - ALWAYS ALLOW)
  // clickedPartId is optional - if not provided, only checks time delay
  // isNoteClick indicates if this is a note click (for view switching detection)
  const allowDbl = (clickedPartId, isNoteClick = false) => {
    const isTextSelected = selectedObject?.id === text.id;
    const hasEnoughTime = Date.now() - lastSelectedAtRef.current >= 500;

    // If clicking on a different part, allow double-click immediately (no delay needed)
    const isDifferentPart =
      clickedPartId !== undefined &&
      clickedPartId &&
      text?.selectedContent?.id &&
      clickedPartId !== text?.selectedContent?.id;

    // CRITICAL: Allow immediate double-click when switching between part view and note view
    // If clicking on the same part's note while in part view (showEditNote=false), allow it
    // If clicking on the same part while in note view (showEditNote=true), allow it
    // This works even if selectedObject is temporarily null
    const isViewSwitching =
      clickedPartId !== undefined &&
      clickedPartId &&
      text?.selectedContent?.id &&
      clickedPartId === text?.selectedContent?.id && // Same part
      ((isNoteClick && !showEditNote) || (!isNoteClick && showEditNote)); // Switching views

    // CRITICAL: If view switching, always allow regardless of selectedObject or time delay
    if (isViewSwitching) {
      return true; // Always allow view switching
    }

    // CRITICAL: Allow double-click on a part even if text isn't selected yet
    // This handles the case where user double-clicks quickly before the first click's state update completes
    // If clicking on a part (clickedPartId is provided and not a note), allow double-click
    const isPartClick =
      clickedPartId !== undefined && clickedPartId && !isNoteClick;
    if (isPartClick && !isTextSelected) {
      return true; // Allow double-click on part even if text selection is pending
    }

    const canDoubleClick = isTextSelected && (hasEnoughTime || isDifferentPart);

    if (!canDoubleClick && isTextSelected) {
    }

    return canDoubleClick;
  };

  // Check if text selection should be disabled
  // This matches the condition from TextBoxWithButtons.jsx
  const isTextSelectionDisabled = () => {
    // CRITICAL: If this text is part of selectedObjects array (multi-selection),
    // ALWAYS disable text selection to prevent interference with group dragging
    if (selectedObjects && selectedObjects.length > 1) {
      const isInMultiSelection = selectedObjects.some(
        (obj) =>
          obj.id === text.id &&
          (obj.type === "Text" || obj.type?.includes("Text")),
      );
      if (isInMultiSelection) {
        return true; // Always disable text selection when in multi-selection
      }
    }

    // First check if this text is not the selected object - if so, disable text selection
    if (selectedObject?.id !== text.id) {
      return true;
    }

    // We need to check if the text box is expanded and none of the editing modes are active
    // Since we don't have direct access to isExpanded, we'll assume it's true when the text is selected
    // and none of the editing modes are active
    const isExpanded = selectedObject?.id === text.id;

    // Disable text selection when editTextBoxContent is false
    const shouldDisableSelection =
      isExpanded &&
      !editTextBoxContent &&
      !showEditTask &&
      !text.selectedContent &&
      !showEditNote;

    return shouldDisableSelection;
  };

  // Check if dragging should be disabled
  const isDraggingDisabled = () => {
    // CRITICAL: If this text is part of selectedObjects array (multi-selection),
    // NEVER disable dragging - group dragging should always work
    if (selectedObjects && selectedObjects.length > 1) {
      const isInMultiSelection = selectedObjects.some(
        (obj) =>
          obj.id === text.id &&
          (obj.type === "Text" || obj.type?.includes("Text")),
      );
      if (isInMultiSelection) {
        return false; // Always enable dragging when in multi-selection
      }
    }

    // When text selection is disabled (text is selected but not being edited),
    // dragging should be ENABLED. When text selection is enabled (text is being edited),
    // dragging should be DISABLED to prevent interference with text editing.
    return !isTextSelectionDisabled();
  };

  // Check if pointer events should pass through (for drawing on top of text elements)
  // When in drawing mode (not SELECT), allow events to pass through to Background
  const shouldAllowPointerEventsThrough = () => {
    // CRITICAL: If this text is part of selectedObjects array (multi-selection),
    // NEVER allow pointer events to pass through - we need to capture them for group dragging
    if (selectedObjects && selectedObjects.length > 1) {
      const isInMultiSelection = selectedObjects.some(
        (obj) =>
          obj.id === text.id &&
          (obj.type === "Text" || obj.type?.includes("Text")),
      );
      if (isInMultiSelection) {
        return false; // Never allow pass-through when in multi-selection
      }
    }

    // If action is not provided, default to allowing pointer events (for text editing)
    if (!action || !ACTIONS) {
      return false; // Don't allow through - enable text editing
    }
    // Allow events to pass through when in drawing mode (not SELECT)
    return action !== ACTIONS.SELECT;
  };

  // Check if content exceeds canvas bounds
  const isContentOverflowing = () => {
    const pageHeight = canvasHeight;
    const mainCanvasWidth = window.innerWidth * 0.45;

    // Check if the text element exceeds canvas height / top boundary (same for both canvases)
    const exceedsHeight = text.y + totalContentHeight > pageHeight;
    const isOutsideTop = text.y < 0;

    if (isOffCanvas) {
      // For off-canvas elements we only treat them as "overflowing" when they
      // actually leave the off-canvas area (top/left/vertical overflow).
      // Merely approaching or overlapping the main canvas edge should NOT show
      // the red overflow stroke – that indicator is intended for the main canvas.
      const isOutsideLeft = text.x < 0;
      return exceedsHeight || isOutsideTop || isOutsideLeft;
    }

    // Main canvas: check against main canvas width / left boundary
    const pageWidth =
      Number.isFinite(mainCanvasWidth) && mainCanvasWidth > 0
        ? mainCanvasWidth
        : canvasWidth;

    const exceedsWidth = text.x + textBlockWidth > pageWidth;
    const isOutsideLeft = text.x < 0;

    return exceedsWidth || exceedsHeight || isOutsideLeft || isOutsideTop;
  };

  // Force re-render when canvas is resized
  useEffect(() => {
    // This effect will trigger a re-render when canvasResizeTrigger changes
    // The HTML content width calculations will be updated
    // Force a re-render by updating a dummy state or calling a callback
  }, [canvasResizeTrigger, canvasHeight]);

  // Inject list alignment styles for proper bullet/number alignment
  useEffect(() => {
    // Check if style tag already exists
    let styleTag = document.getElementById("text-element-list-styles");

    if (!styleTag) {
      // Create and inject style tag
      styleTag = document.createElement("style");
      styleTag.id = "text-element-list-styles";
      styleTag.textContent = listAlignmentStyles;
      document.head.appendChild(styleTag);
    }

    // Cleanup: remove style tag when component unmounts (only if no other TextElements exist)
    return () => {
      // Only remove if this is the last TextElement
      // In practice, we might want to keep it for all TextElements
      // So we'll leave it in the document for now
      // If needed, we can add a ref counter to track TextElement instances
    };
  }, []);

  // Sync layer draw once when this text enters multi-selection. Do NOT run a continuous
  // requestAnimationFrame loop—it caused 60 batchDraw()/sec per text and severe lag.
  React.useEffect(() => {
    const isInGroupDrag =
      selectedObjects &&
      selectedObjects.length > 1 &&
      selectedObjects.some(
        (obj) =>
          obj.id === text.id &&
          (obj.type === "Text" || obj.type?.includes("Text")),
      );

    if (!isInGroupDrag || !shapeRef.current) {
      return;
    }

    const layer = shapeRef.current.getLayer();
    if (layer && typeof layer.batchDraw === "function") {
      layer.batchDraw();
    }
  }, [selectedObjects, text.id]);

  // During group drag, handleGroupDragMove updates Konva nodes directly and efficiently
  // We should only sync when drag is NOT active to avoid performance issues
  React.useLayoutEffect(() => {
    // Skip sync if currently dragging (isDraggingRef is set during drag)
    if (isDraggingRef.current) {
      return; // Don't sync during drag - handleGroupDragMove handles it
    }

    const groupNode = shapeRef.current;
    const isInMultiSelection =
      selectedObjects &&
      selectedObjects.length > 1 &&
      selectedObjects.some(
        (obj) =>
          obj.id === text.id &&
          (obj.type === "Text" || obj.type?.includes("Text")),
      );

    if (isInMultiSelection && groupNode) {
      try {
        const nodeX = groupNode.x();
        const nodeY = groupNode.y();
        const textX = text.x || 0;
        const textY = text.y || 0;

        // If node position differs from text prop, sync it
        if (
          nodeX !== undefined &&
          nodeY !== undefined &&
          !isNaN(nodeX) &&
          !isNaN(nodeY)
        ) {
          // CRITICAL FIX: Don't sync negative positions for off-canvas elements
          // Negative positions indicate the element was on main canvas and crossed the boundary
          // The correct position should come from state (text.x, text.y), not the stale negative node position
          if (isOffCanvas && (nodeX < 0 || nodeY < 0)) {
            // Use state position instead of negative node position
            groupNode.position({ x: textX, y: textY });
            groupNode.getLayer()?.batchDraw();
            return;
          }

          const deltaX = Math.abs(nodeX - textX);
          const deltaY = Math.abs(nodeY - textY);

          // Only sync if there's a significant difference (avoid micro-adjustments during drag)
          if (deltaX > 0.1 || deltaY > 0.1) {
            // Update Group position to match node
            groupNode.position({ x: nodeX, y: nodeY });
            groupNode.getLayer()?.batchDraw();
          }
        }
      } catch (error) {
      }
    }
  }, [text.x, text.y, selectedObjects, isOffCanvas, text.id]);

  // CRITICAL FIX: Get actual position from Konva node during group drag
  // During drag (single or group), use Konva node position so the Group doesn't jitter from state update lag.
  // When we rely on text.x/text.y from state, setSelectedText in onDragMove causes re-renders that can lag or batch,
  // making the element fluctuate when dragging slowly. Using the node position keeps the visual in sync with the drag.
  const getGroupPosition = () => {
    const isInMultiSelection =
      selectedObjects &&
      selectedObjects.length > 1 &&
      selectedObjects.some(
        (obj) =>
          obj.id === text.id &&
          (obj.type === "Text" || obj.type?.includes("Text")),
      );
    const isDragging = isDraggingRef.current;

    // Use node position when dragging (single or multi) or in multi-selection so visual follows the node
    if (isInMultiSelection || isDragging) {
      let node = shapeRef.current;

      // If ref doesn't have the node, try to find it by searching stages
      if (!node) {
        try {
          // Search for the node on all Konva stages in the document
          // This is a fallback when ref doesn't work (e.g., node is on different stage)
          const allStages = [];

          // Try to find all Konva stages by searching for Stage containers
          // Konva stages are typically stored in window.Konva.stages
          if (window.Konva && window.Konva.stages) {
            allStages.push(...window.Konva.stages);
          }

          // Also try to find stages by searching for canvas elements with Konva data
          try {
            const stageContainers = document.querySelectorAll(
              "canvas[data-konva-stage], div[data-konva-stage]",
            );
            stageContainers.forEach((container) => {
              // Try to get stage from container
              const konvaStage = container._konvaStage;
              if (konvaStage && !allStages.includes(konvaStage)) {
                allStages.push(konvaStage);
              }
            });
          } catch (e) {
            // Ignore errors when searching for stages
          }

          // Search for the node on all stages
          for (const stage of allStages) {
            if (stage && typeof stage.findOne === "function") {
              try {
                const foundNode = stage.findOne(`#${text.id}`);
                if (foundNode) {
                  node = foundNode;
                  break;
                }
              } catch (e) {
                // Continue searching other stages
              }
            }
          }
        } catch (error) {
        }
      }

      if (node) {
        try {
          // During group drag, Konva node position is updated directly by handleGroupDragMove
          // Read the actual position from the node to keep the white rect visible
          const nodeX = node.x();
          const nodeY = node.y();
          const textX = text.x || 0;
          const textY = text.y || 0;

          // Always use node position if it's valid
          // This ensures the white rect (which is relative to Group) stays in the correct position
          if (
            nodeX !== undefined &&
            nodeY !== undefined &&
            !isNaN(nodeX) &&
            !isNaN(nodeY)
          ) {
            // CRITICAL: If text prop has negative X and we're on off-canvas, the prop is definitely stale
            // Off-canvas elements should have positive X coordinates (after canvas offset)
            // Use node position if text prop seems wrong
            const textPropSeemsWrong = isOffCanvas && textX < 0;

            // Check if there's a significant difference (more than 0.1px) or if prop seems wrong
            const deltaX = Math.abs(nodeX - textX);
            const deltaY = Math.abs(nodeY - textY);

            if (textPropSeemsWrong || deltaX > 0.1 || deltaY > 0.1) {
              return { x: nodeX, y: nodeY };
            } else {
              // Positions match, use node position anyway to ensure consistency
              return { x: nodeX, y: nodeY };
            }
          } else {
          }
        } catch (error) {
        }
      } else {
        // Node not found - might be on a different stage or not mounted yet

        // CRITICAL: If text prop has negative X and we're on off-canvas, the prop is definitely stale
        // Don't use the stale negative position - it will cause the white rect to be in the wrong place
        // The actual position should be positive (after canvas offset) for off-canvas elements
        if (isOffCanvas && text.x < 0) {
          // Can't fix it without the node - the state should be updated by Canvas.jsx
          // For now, use the text prop but log the issue
        }
      }
    }

    return { x: text.x || 0, y: text.y || 0 };
  };

  // Sync HTML position to node during drag (like RectangleElement: update in onDragMove, no React re-render)
  const syncHtmlPositionToNode = () => {
    const node = shapeRef.current;
    if (!node) return;
    const stage = node.getStage();
    if (!stage) return;
    const stageContainer = stage.container();
    const stageRect = stageContainer.getBoundingClientRect();
    // Konva's DOM differs depending on how the Stage is mounted.
    // In some cases (notably the fixed full-viewport off-canvas stage),
    // `stage.container()` is already the `.konvajs-content` node.
    const htmlContainer = stageContainer?.classList?.contains("konvajs-content")
      ? stageContainer
      : stageContainer.querySelector(".konvajs-content");
    const currentX = node.x();
    const currentY = node.y();
    const currentRotation = node.rotation();

    // NOTE: `Html` portals don't always live *inside* `.konvajs-content` across stages/layouts.
    // Off-canvas (fixed full viewport) can produce portal nodes outside the expected container.
    // Use the stage-scoped container when available, but fall back to a global query so we
    // still hide duplicates during drag.
    const primary =
      htmlContainer?.querySelector?.(
        '[data-text-id="' + text.id + '"][data-primary-content="true"]',
      ) ||
      document.querySelector(
        '[data-text-id="' + text.id + '"][data-primary-content="true"]',
      );
    const allTextElements = htmlContainer
      ? htmlContainer.querySelectorAll('[data-text-id="' + text.id + '"]')
      : document.querySelectorAll('[data-text-id="' + text.id + '"]');

    if (!primary || !allTextElements || allTextElements.length === 0) return;

    allTextElements.forEach((htmlEl) => {
      if (!htmlEl?.style) return;
      if (htmlEl === primary) {
        const offsetX = parseFloat(htmlEl.getAttribute("data-offset-x")) || 0;
        const offsetY = parseFloat(htmlEl.getAttribute("data-offset-y")) || 0;
        const absoluteX = Math.round(stageRect.left + currentX + offsetX);
        const absoluteY = Math.round(stageRect.top + currentY + offsetY);
        htmlEl.style.transform = `translate(${absoluteX}px, ${absoluteY}px) rotate(${currentRotation}deg)`;
        htmlEl.style.position = "fixed";
        htmlEl.style.zIndex = isDraggingRef.current || isSelected ? "35" : "10";
        htmlEl.style.visibility = "visible";
        htmlEl.style.opacity = "1";
      } else {
        htmlEl.style.visibility = "hidden";
        htmlEl.style.opacity = "0";
      }
    });
    node.getLayer()?.batchDraw();
  };

  const resetHtmlVisibilityAfterDrag = () => {
    try {
      const node = shapeRef.current;
      const stage = node?.getStage();
      const stageContainer = stage?.container?.();
      const htmlContainer = stageContainer?.classList?.contains(
        "konvajs-content",
      )
        ? stageContainer
        : stageContainer?.querySelector?.(".konvajs-content");
      const nodes = htmlContainer
        ? htmlContainer.querySelectorAll('[data-text-id="' + text.id + '"]')
        : document.querySelectorAll('[data-text-id="' + text.id + '"]');
      nodes.forEach((el) => {
        if (el?.style) {
          el.style.visibility = "";
          el.style.opacity = "";
          el.style.transform = "";
          el.style.position = "";
          el.style.zIndex = "";
        }
      });
    } catch (_) {}
  };

  const groupPosition = getGroupPosition();

  // Check if text element is completely outside canvas boundaries
  const isOutsideCanvas = () => {
    // CRITICAL: Off-canvas elements should never be considered "outside canvas"
    // This fixes the issue where text elements in selectedObjectsArray are not visible when dragged off-canvas
    if (isOffCanvas) {
      return false;
    }

    const elementRight = text.x + textBlockWidth;
    const elementBottom = text.y + totalContentHeight;

    const isOutside =
      text.x >= canvasWidth || // Completely to the right
      text.y >= canvasHeight || // Completely below (use full height for boundary detection, matching rectangles)
      elementRight <= 0 || // Completely to the left
      elementBottom <= 0; // Completely above

    if (!isOffCanvas && isOutside && textVisibilityDebugEnabled) {
    }

    return isOutside;
  };

  const clampNodeInsideOffCanvasStripsByClientRect = (
    node,
    stageWidth,
    stageHeight,
    inset = 0
  ) => {
    if (!node || stageWidth <= 0 || stageHeight <= 0) {
      return { x: node?.x?.() ?? 0, y: node?.y?.() ?? 0 };
    }
    const getEffectiveInset = (requestedInset, stageSize, rectSize) => {
      const safeInset = Math.max(0, Number(requestedInset) || 0);
      const safeStage = Math.max(0, Number(stageSize) || 0);
      const safeRect = Math.max(0, Number(rectSize) || 0);
      return Math.max(0, Math.min(safeInset, (safeStage - safeRect) / 2));
    };
    const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
    for (let pass = 0; pass < 8; pass += 1) {
      const rect = node.getClientRect();
      const insetX = getEffectiveInset(inset, stageWidth, rect.width);
      const insetY = getEffectiveInset(inset, stageHeight, rect.height);
      let dx = 0;
      let dy = 0;

      if (rect.x < insetX) dx += insetX - rect.x;
      else if (rect.x + rect.width > stageWidth - insetX) {
        dx += stageWidth - insetX - (rect.x + rect.width);
      }
      if (rect.y < insetY) dy += insetY - rect.y;
      else if (rect.y + rect.height > stageHeight - insetY) {
        dy += stageHeight - insetY - (rect.y + rect.height);
      }

      const overlapsMainColumn =
        rect.x < mainZoneEnd && rect.x + rect.width > mainZoneStart;
      if (overlapsMainColumn) {
        // For off-canvas text elements we always want the resolved placement
        // to end up in the left strip (never on the right side).
        dx += mainZoneStart - (rect.x + rect.width);
      }

      if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) break;
      node.x((Number(node.x()) || 0) + dx);
      node.y((Number(node.y()) || 0) + dy);
    }
    return { x: node.x(), y: node.y() };
  };

  // Helper function to check if a content section should be visible
  const isContentSectionVisible = (contentY, contentHeight) => {
    const contentTop = text.y + contentY;
    const contentBottom = contentTop + contentHeight;

    // Check if content is completely outside canvas boundaries (use full height for boundary detection)
    return !(
      (
        contentTop >= canvasHeight || // Completely below canvas (use full height, matching rectangles)
        contentBottom <= 0 || // Completely above canvas
        text.x >= canvasWidth || // Completely to the right of canvas
        text.x + textBlockWidth <= 0
      ) // Completely to the left of canvas
    );
  };

  // More granular helper function to check if content has any visible portion
  const hasVisibleContent = (contentY, contentHeight) => {
    // CRITICAL: Off-canvas elements should always be visible
    // This fixes the issue where text elements in selectedObjectsArray are not visible when dragged off-canvas
    if (isOffCanvas) {
      return true;
    }

    const contentTop = text.y + contentY;
    const contentBottom = contentTop + contentHeight;

    // Check if any part of the content is within canvas boundaries
    // Allow partial visibility - content should be visible if any part overlaps with canvas
    // Use effectiveCanvasHeight for visibility check (clipping), but full height for boundary detection
    const hasVerticalOverlap =
      contentTop < effectiveCanvasHeight && contentBottom > 0;
    const hasHorizontalOverlap =
      text.x < canvasWidth && text.x + textBlockWidth > 0;

    // Remove the strict withinCanvasHeight check to allow partial visibility
    // Content should be visible if it has any overlap with the canvas area
    const result = hasVerticalOverlap && hasHorizontalOverlap;

    if (!isOffCanvas && !result && textVisibilityDebugEnabled) {
    }

    return result;
  };

  // Strict visibility function for note section - requires full content to fit within canvas
  const hasVisibleContentStrict = (contentY, contentHeight) => {
    // CRITICAL: Off-canvas elements should always be visible
    if (isOffCanvas) {
      return true;
    }

    const contentTop = text.y + contentY;
    const contentBottom = contentTop + contentHeight;

    // Check if any part of the content is within canvas boundaries
    // Use effectiveCanvasHeight for visibility/clipping check
    const hasVerticalOverlap =
      contentTop < effectiveCanvasHeight && contentBottom > 0;
    const hasHorizontalOverlap =
      text.x < canvasWidth && text.x + textBlockWidth > 0;

    // Strict check: ensure content doesn't extend beyond canvas bottom (use effective height for clipping)
    const withinCanvasHeight = contentBottom <= effectiveCanvasHeight;

    const result =
      hasVerticalOverlap && hasHorizontalOverlap && withinCanvasHeight;

    // Debug logging for visibility calculations
    if (text.id && canvasResizeTrigger > 0) {
    }

    return result;
  };

  // Moderately strict visibility function for note section - hides earlier than regular content
  const hasVisibleContentStrictForNotes = (contentY, contentHeight) => {
    // CRITICAL: Off-canvas elements should always be visible
    if (isOffCanvas) {
      return true;
    }

    const contentTop = text.y + contentY;
    const contentBottom = contentTop + contentHeight;

    // Check if any part of the content is within canvas boundaries
    // Use effectiveCanvasHeight for visibility/clipping check
    const hasVerticalOverlap =
      contentTop < effectiveCanvasHeight && contentBottom > 0;
    const hasHorizontalOverlap =
      text.x < canvasWidth && text.x + textBlockWidth > 0;

    // More strict check: hide if any significant portion extends beyond canvas (use effective height for clipping)
    const contentOverflow = Math.max(0, contentBottom - effectiveCanvasHeight);
    const overflowPercentage = contentOverflow / contentHeight;
    // Only allow very small overflow (5% instead of 20%) or hide completely
    const withinAcceptableOverflow = overflowPercentage <= 0.05; // Allow up to 5% overflow

    const result =
      hasVerticalOverlap && hasHorizontalOverlap && withinAcceptableOverflow;

    // Debug logging for visibility calculations
    if (text.id && canvasResizeTrigger > 0) {
    }

    return result;
  };

  // Helper function to get the visible portion of content
  const getVisibleContentBounds = (contentY, contentHeight) => {
    const contentTop = text.y + contentY;
    const contentBottom = contentTop + contentHeight;

    // Calculate visible bounds with strict canvas boundary enforcement
    // Use effectiveCanvasHeight for clipping (matching rectangle text clipping)
    const visibleTop = Math.max(0, contentTop);
    const visibleBottom = Math.min(effectiveCanvasHeight, contentBottom);
    const visibleLeft = Math.max(0, text.x);
    const visibleRight = Math.min(canvasWidth, text.x + textBlockWidth);

    // Ensure bounds don't exceed canvas dimensions (use effective height for clipping)
    const clampedVisibleTop = Math.max(
      0,
      Math.min(visibleTop, effectiveCanvasHeight),
    );
    const clampedVisibleBottom = Math.max(
      0,
      Math.min(visibleBottom, effectiveCanvasHeight),
    );
    const clampedVisibleLeft = Math.max(0, Math.min(visibleLeft, canvasWidth));
    const clampedVisibleRight = Math.max(
      0,
      Math.min(visibleRight, canvasWidth),
    );

    const bounds = {
      visibleTop: clampedVisibleTop,
      visibleBottom: clampedVisibleBottom,
      visibleLeft: clampedVisibleLeft,
      visibleRight: clampedVisibleRight,
      visibleHeight: Math.max(0, clampedVisibleBottom - clampedVisibleTop),
      visibleWidth: Math.max(0, clampedVisibleRight - clampedVisibleLeft),
      isPartiallyVisible:
        clampedVisibleTop < contentBottom && clampedVisibleBottom > contentTop,
    };

    // Debug logging for bounds calculations
    if (text.id && canvasResizeTrigger > 0) {
    }

    return bounds;
  };

  // Determine stroke color based on overflow
  const getStrokeColor = () => {
    return "#d9d9d9";
    // Don't show red overflow indicator in read-only/preview mode (when isDraggable is false)
    if (!isDraggableProp) {
      return "#d9d9d9";
    }
    return isContentOverflowing() ? "red" : "#d9d9d9";
  };

  // Optionally, you can also increase stroke width for better visibility
  const getStrokeWidth = () => {
    // Always respect user's strokeWidth setting, but ensure minimum visibility for overflow
    const userStrokeWidth = text.strokeWidth ?? 0;

    // If content is overflowing AND we're in edit mode (isDraggable), show at least 2px stroke for visibility (red border)
    // Otherwise, respect the user's setting (including 0)
    if (isContentOverflowing() && isDraggableProp) {
      // When overflowing, always show at least 2px for the red border indicator
      const overflowWidth = Math.max(2, userStrokeWidth);
      // Scale with zoom level
      return overflowWidth / Math.max(0.3, effectiveZoomLevel || 1);
    }

    // Not overflowing or in read-only mode: respect user's setting, including 0
    if (userStrokeWidth === 0) {
      return 0;
    }

    // Scale stroke width with zoom level to maintain consistent appearance at low zoom levels
    const scaledWidth =
      userStrokeWidth / Math.max(0.3, effectiveZoomLevel || 1);

    // Ensure minimum of 1px for visibility when stroke is set
    return Math.max(1, scaledWidth);
  };

  // Don't render if completely outside canvas
  if (isOutsideCanvas()) {
    return null;
  }

  const isInSelection =
    isSelected ||
    (selectedObjects?.some(
      (o) =>
        o.id === text.id && (o.type === "Text" || o.type?.includes("Text")),
    ) ??
      false);

  return (
    <Group
      ref={shapeRef}
      name={text.name}
      key={text.id}
      x={groupPosition.x}
      listening={!isSelecting} // Disable listening during selection to allow selection box to be drawn over it
      y={groupPosition.y}
      id={text.id}
      width={textBlockWidth}
      height={effectiveContentHeight} // Use effective height so stroke follows transformer after resize
      rotation={text.rotation || 0}
      scaleX={1}
      perfectDrawEnabled={false} // CRITICAL FIX: Disable perfect drawing to allow drag to continue when element moves outside stage bounds
      clipFunc={
        isContentOverflowing()
          ? undefined
          : (ctx) => {
              // Only apply clipping when not overflowing to prevent stroke fading
              // When overflowing (red stroke), disable clipping to ensure full stroke visibility
              const strokeWidth = getStrokeWidth();
              const padding = Math.ceil(strokeWidth / 2);
              ctx.rect(
                -padding,
                -padding,
                textBlockWidth + padding * 2,
                effectiveContentHeight + padding * 2,
              );
            }
      }
      scaleY={1}
      fontSize={
        text.fontSize === 11
          ? "small"
          : text.fontSize === "14"
            ? "medium"
            : "big"
      }
      showBackground={text.showBackground}
      strokeWidth={text.strokeWidth}
      dragDistance={10}
      dragBoundFunc={(pos) => {
        // Allow free drag beyond stage bounds; clamp on drag-end only.
        return pos;
      }}
      draggable={(() => {
        // CRITICAL: Disable dragging when selection box is being drawn to prevent cursor interference
        if (isSelecting) {
          return false;
        }
        // CRITICAL: Always enable dragging when text is in multi-selection
        // This ensures group dragging works properly
        if (selectedObjects && selectedObjects.length > 1) {
          const isInMultiSelection = selectedObjects.some(
            (obj) =>
              obj.id === text.id &&
              (obj.type === "Text" || obj.type?.includes("Text")),
          );
          if (isInMultiSelection) {
            // CRITICAL FIX: Only allow dragging if we're actually in a drag state
            // This prevents elements from following cursor after a simple click
            return isDraggableProp && !isDraggingDisabled();
          }
        }
        // Normal draggable logic
        return isDraggableProp && !isDraggingDisabled();
      })()}
      onTransformStart={(e) => {
        // Set transforming flag immediately when transform starts
        isTransformingRef.current = true;
        setShowTextBox(true);
        transformActiveAnchorRef.current =
          typeof transformerRef?.current?.getActiveAnchor === "function"
            ? transformerRef.current.getActiveAnchor()
            : null;
        if (!transformActiveAnchorRef.current) {
          const trNode = e?.target?.getLayer?.()?.findOne?.("Transformer");
          if (trNode && typeof trNode.getActiveAnchor === "function") {
            transformActiveAnchorRef.current = trNode.getActiveAnchor();
          }
        }
        if (isOffCanvas && shapeRef.current) {
          const node = shapeRef.current;
          const st = node.getStage?.();
          const sw = st?.width?.() ?? 0;
          if (sw > 0) {
            const ext = getAbsoluteHorizontalExtent(
              {
                ...text,
                x: node.x(),
                y: node.y(),
                width: Math.max(1, Number(node.width()) || text.width || 0),
                height: Math.max(1, Number(node.height()) || text.height || 0),
              },
              "Text",
              node.x(),
            );
            offCanvasDragStripSideRef.current = getOffCanvasHorizontalStripSide(
              ext.minX,
              ext.maxX,
              sw,
            );
          } else {
            offCanvasDragStripSideRef.current = null;
          }
        } else {
          offCanvasDragStripSideRef.current = null;
        }
      }}
      onTransform={(e) => {
        isTransformingRef.current = true;
        const node = e.target;
        const activeAnchor =
          transformActiveAnchorRef.current ||
          (typeof transformerRef?.current?.getActiveAnchor === "function"
            ? transformerRef.current.getActiveAnchor()
            : null);
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const rotation = node.rotation();
        // Match RectangleElement: apply new dimensions to node so stroke follows transformer anchor
        const width = Math.min(
          Math.max(40, textBlockWidth * scaleX),
          MAX_WIDTH,
        );
        const height = Math.max(40, effectiveContentHeight * scaleY);

        node.scaleX(1);
        node.scaleY(1);
        node.width(width);
        node.height(height);

        // Keep the resized edge fixed: only the opposite edge should move (like RectangleElement).
        // Text has middle-left / middle-right: right resize => left edge fixed (x = text.x); left resize => right edge fixed (x = text.x + textBlockWidth - width).
        const rightEdgeFixedX = text.x;
        const leftEdgeFixedX = text.x + textBlockWidth - width;
        const x =
          activeAnchor === "middle-left"
            ? leftEdgeFixedX
            : activeAnchor === "middle-right"
              ? rightEdgeFixedX
              : Math.abs(node.x() - rightEdgeFixedX) <=
                    Math.abs(node.x() - leftEdgeFixedX)
                ? rightEdgeFixedX
                : leftEdgeFixedX;
        const y = text.y;
        node.x(x);
        node.y(y);

        // Ensure TextBoxWithButtons remains visible during transform
        setShowTextBox(true);

        setSelectedText((prev) =>
          prev?.id === text.id
            ? {
                ...prev,
                ...text,
                x,
                y,
                width,
                height,
                rotation,
                _heightFromResize: true,
              }
            : prev,
        );

        // Keep stage array geometry in sync during live transform.
        // Text renderer prefers geometry from texts/offCanvasTexts arrays, so
        // without this main-canvas transforms appear to update only onTransformEnd.
        setTexts((prevTexts) =>
          prevTexts.map((t) =>
            t.id === text.id
              ? {
                  ...t,
                  x,
                  y,
                  width,
                  height,
                  rotation,
                  _heightFromResize: true,
                }
              : t,
          ),
        );
      }}
      onDragStart={(e) => {
        // CRITICAL SAFETY CHECK: Verify mouse button is actually pressed
        // This prevents drag from starting if button was already released
        if (e?.evt?.buttons !== undefined && e.evt.buttons === 0) {
          // Mouse button is not pressed - prevent drag from starting
          const node = e.target;
          if (node) {
            node.stopDrag();
          }
          return;
        }

        // Set dragging flag immediately when drag starts
        isDraggingRef.current = true;
        setShowTextBox(true);

        const node = e.target;
        crossStageGestureBegin?.(
          typeof node?.getStage === "function" ? node.getStage() : null,
          e.evt,
        );
        if (isOffCanvas && node) {
          const st = node.getStage?.();
          const sw = st?.width?.() ?? 0;
          if (sw > 0) {
            const ext = getAbsoluteHorizontalExtent(text, "Text", node.x());
            offCanvasDragStripSideRef.current = getOffCanvasHorizontalStripSide(
              ext.minX,
              ext.maxX,
              sw,
            );
          } else {
            offCanvasDragStripSideRef.current = null;
          }
        } else {
          offCanvasDragStripSideRef.current = null;
        }
        const isInMultiSelection =
          selectedObjects &&
          selectedObjects.length > 1 &&
          selectedObjects.some(
            (obj) =>
              obj.id === text.id &&
              (obj.type === "Text" || obj.type?.includes("Text")),
          );

        if (isInMultiSelection) {
          // Store initial mouse position for delta calculation
          const stage = node.getStage();
          if (stage && e.evt) {
            const pointerPos = stage.getPointerPosition();
            if (pointerPos) {
              dragStartMousePosRef.current = {
                x: pointerPos.x,
                y: pointerPos.y,
              };
            }
          }

          // CRITICAL: Store initial element position BEFORE group drag starts
          // This ensures we capture the exact position that matches what handleGroupDragStart will read
          // Use the node from the event (e.target) which is the actual Konva node being dragged
          dragStartElementPosRef.current = {
            x: node.x(),
            y: node.y(),
          };

          // Call handleGroupDragStart immediately on drag start so initial positions are
          // captured before any movement occurs. This prevents the "little move first"
          // jitter caused by capturing positions on the first onDragMove (when the node
          // has already shifted by Konva's dragDistance threshold).
          handleGroupDragStart?.(text.id, "Text");
        } else {
          // CRITICAL FIX: For single element drags, set up document-level mouse tracking
          // to continue drag when element moves outside stage bounds
          // This prevents Konva from ending the drag prematurely when dragging to off-canvas
          const stage = node.getStage();
          if (stage && e.evt) {
            const stageBox = stage.container().getBoundingClientRect();
            const stageScaleX = stage.scaleX();
            const stageScaleY = stage.scaleY();
            const stageX = stage.x();
            const stageY = stage.y();

            const startStagePos = {
              x: (e.evt.clientX - stageBox.left) / stageScaleX - stageX,
              y: (e.evt.clientY - stageBox.top) / stageScaleY - stageY,
            };
            const startGroupPos = { x: node.x(), y: node.y() };

            // Store for use in document-level handlers
            dragStartMousePosRef.current = {
              x: e.evt.clientX,
              y: e.evt.clientY,
            };
            dragStartElementPosRef.current = startGroupPos;

            const handleDocumentMouseMove = (moveEvent) => {
              if (!isDraggingRef.current) {
                document.removeEventListener(
                  "mousemove",
                  handleDocumentMouseMove,
                );
                document.removeEventListener("mouseup", handleDocumentMouseUp);
                return;
              }

              // CRITICAL: Only update position when pointer is OUTSIDE the stage. When inside, let Konva's drag handle it.
              // Otherwise we fight with Konva (two sources updating the node) and the element fluctuates.
              const pointerInStage =
                moveEvent.clientX >= stageBox.left &&
                moveEvent.clientX <= stageBox.right &&
                moveEvent.clientY >= stageBox.top &&
                moveEvent.clientY <= stageBox.bottom;
              if (pointerInStage) {
                return;
              }

              // Pointer outside stage - Konva may not fire; we drive the drag
              const currentStagePos = {
                x: (moveEvent.clientX - stageBox.left) / stageScaleX - stageX,
                y: (moveEvent.clientY - stageBox.top) / stageScaleY - stageY,
              };

              const deltaX = currentStagePos.x - startStagePos.x;
              const deltaY = currentStagePos.y - startStagePos.y;

              let newPos = {
                x: startGroupPos.x + deltaX,
                y: startGroupPos.y + deltaY,
              };

              // Allow free drag outside stage while pointer is outside;
              // final clamp is applied on drag end.

              node.position(newPos);
              syncHtmlPositionToNode();

              const newAttrs = {
                ...text,
                id: text.id,
                x: newPos.x,
                y: newPos.y,
                _stageX: newPos.x,
                _stageY: newPos.y,
                width: text.width,
                height: text.height,
                rotation: text.rotation || 0,
                text: text.text,
                type: "Text",
                strokeWidth: text.strokeWidth ?? 0,
              };

              const syntheticEvent = { evt: moveEvent, target: node };
              onDragMove?.(newAttrs, syntheticEvent);

              stage.getLayer()?.batchDraw();
            };

            const handleDocumentMouseUp = (upEvent) => {
              document.removeEventListener(
                "mousemove",
                handleDocumentMouseMove,
              );
              document.removeEventListener("mouseup", handleDocumentMouseUp);
              resetHtmlVisibilityAfterDrag();

              const finalX = node.x();
              const finalY = node.y();

              let nodeRotation = node.rotation();
              nodeRotation = nodeRotation % 360;
              if (nodeRotation < 0) nodeRotation += 360;

              const currentText = {
                ...text,
                x: finalX,
                y: finalY,
                rotation: nodeRotation,
                scaleX: node.scaleX(),
                scaleY: node.scaleY(),
              };

              offCanvasDragStripSideRef.current = null;
              onDragEnd(
                text.id,
                finalX,
                finalY,
                upEvent.clientX,
                upEvent.clientY,
                currentText,
              );

              crossStageGestureEnd?.();

              setTimeout(() => {
                isDraggingRef.current = false;
                dragStartMousePosRef.current = null;
                dragStartElementPosRef.current = null;
              }, 100);
            };

            // Set up document-level tracking to continue drag when mouse leaves stage
            document.addEventListener("mousemove", handleDocumentMouseMove);
            document.addEventListener("mouseup", handleDocumentMouseUp);
          }
        }
      }}
      onDragMove={(e) => {
        // Let Konva's internal dragDistance logic handle whether this is a real drag;
        // don't forcibly stop the drag based on e.evt.buttons here, as that can fire
        // spuriously when crossing between main and off-canvas stages.
        isDraggingRef.current = true;
        setShowTextBox(true);
        const node = shapeRef.current;
        if (!node) return;

        // CRITICAL: When in multi-selection, use the mouse position from the event
        // The group drag handler will calculate the correct position based on initial positions
        const isInMultiSelection =
          selectedObjects &&
          selectedObjects.length > 1 &&
          selectedObjects.some(
            (obj) =>
              obj.id === text.id &&
              (obj.type === "Text" || obj.type?.includes("Text")),
          );

        let nodeX, nodeY;

        if (isInMultiSelection) {
          // CRITICAL: In multi-selection, calculate position from mouse delta
          // This avoids using the negative node position when dragging left
          // The node position is in main canvas coordinates and can be negative
          // We need to calculate from mouse movement to get the correct position
          const stage = node.getStage();
          if (
            stage &&
            e.evt &&
            dragStartMousePosRef.current &&
            dragStartElementPosRef.current
          ) {
            const pointerPos = stage.getPointerPosition();
            if (pointerPos) {
              // Calculate delta from initial mouse position
              const mouseDeltaX = pointerPos.x - dragStartMousePosRef.current.x;
              const mouseDeltaY = pointerPos.y - dragStartMousePosRef.current.y;

              // Calculate new position: initial element position + mouse delta
              // Use the initial position that was stored (which should match handleGroupDragStart's capture)
              nodeX = dragStartElementPosRef.current.x + mouseDeltaX;
              nodeY = dragStartElementPosRef.current.y + mouseDeltaY;
            } else {
              // Fallback: use node position
              nodeX = node.x();
              nodeY = node.y();
            }
          } else {
            // Fallback: if refs aren't set, use node position
            // PERFORMANCE: Removed console.warn during drag for better performance
            nodeX = node.x();
            nodeY = node.y();
          }
        } else {
          // Normal drag - use node position (Konva has moved it correctly)
          nodeX = node.x();
          nodeY = node.y();
        }

        // Validate position - if it's invalid (NaN or undefined), don't update
        if (
          nodeX === undefined ||
          nodeY === undefined ||
          isNaN(nodeX) ||
          isNaN(nodeY)
        ) {
          // PERFORMANCE: Removed console.warn during drag for better performance
          return;
        }

        const constrainedX = nodeX;
        const constrainedY = nodeY;

        // Sync HTML to node (like RectangleElement) so text follows smoothly without re-render
        syncHtmlPositionToNode();

        const newAttrs = {
          ...text,
          id: text.id,
          x: constrainedX,
          y: constrainedY,
          // CRITICAL: Pass raw node coordinates for renderer conversion logic
          _stageX: constrainedX,
          _stageY: constrainedY,
          width: text.width,
          height: text.height,
          rotation: text.rotation || 0,
          text: text.text,
          type: "Text",
          strokeWidth: text.strokeWidth ?? 0,
        };
        onDragMove?.(newAttrs, e);

        // Don't update selectedText during single-element drag - syncHtmlPositionToNode keeps HTML in sync; state on drag end.
        // getGroupPosition() uses the node position when isDraggingRef is true, so the visual follows the node smoothly.
        // State is updated on drag end so the final position is correct.
        // saveToHistory && saveToHistory();
      }}
      onDragEnd={(e) => {
        crossStageGestureEnd?.();
        resetHtmlVisibilityAfterDrag();
        // CRITICAL FIX: For single element drags, document-level handlers will handle drag end
        const node = e.target;
        const isInMultiSelection =
          selectedObjects &&
          selectedObjects.length > 1 &&
          selectedObjects.some(
            (obj) =>
              obj.id === text.id &&
              (obj.type === "Text" || obj.type?.includes("Text")),
          );

        // If not in multi-selection and we set up document-level tracking, ignore this Konva drag end
        // The document-level mouseup handler will call onDragEnd instead
        if (!isInMultiSelection && dragStartMousePosRef.current !== null) {
          // Document-level handler will take care of this
          return;
        }

        const newX = node.x();
        const newY = node.y();

        // CRITICAL: Get current rotation from the Konva node, not from stale state
        // The node always has the current visual rotation
        let nodeRotation = node.rotation();
        nodeRotation = nodeRotation % 360;
        if (nodeRotation < 0) nodeRotation += 360;

        // Create currentText with properties from node (rotation, scaleX, scaleY, position)
        // CRITICAL: Set rotation AFTER spreading text to ensure it overrides any stale values
        const currentText = {
          ...text,
          x: newX,
          y: newY,
          // CRITICAL: These must come AFTER the spread to override any stale values from text
          rotation: nodeRotation, // Use the rotation from node
          scaleX: node.scaleX(),
          scaleY: node.scaleY(),
        };

        // Debug logging to verify rotation is preserved

        // Get actual mouse position from the event for accurate coordinate calculation
        const mouseClientX = e.evt?.clientX;
        const mouseClientY = e.evt?.clientY;
        // Ensure TextBoxWithButtons remains visible after drag ends
        setShowTextBox(true);
        // Pass mouse coordinates if available for better accuracy when dragging below visible area
        // Also pass currentText to preserve rotation
        offCanvasDragStripSideRef.current = null;
        onDragEnd(text.id, newX, newY, mouseClientX, mouseClientY, currentText);
        // CRITICAL: Don't save to history here - handleTextDragEnd will handle that

        setTimeout(() => {
          isDraggingRef.current = false;
          dragStartMousePosRef.current = null;
          dragStartElementPosRef.current = null;
        }, 100);
      }}
      onClick={(e) => {
        if (!isPrimaryMouseButton(e.evt)) {
          return;
        }
        // Disable selection if a tool is locked
        if (lockedAction) {
          return;
        }

        const currentIsMultiSelectMode =
          isMultiSelectModeRef?.current ?? isMultiSelectMode;

        // Check if clicking on the same element that's already selected
        const isSameElement = selectedObject && selectedObject.id === text.id;

        // If clicking on the same element, ensure showTextBox is true and return
        if (isSameElement) {
          setShowTextBox(true);
          if (currentIsMultiSelectMode) {
            if (toggleThisTextInMultiSelection(e)) {
              return;
            }
          }
          if (currentIsMultiSelectMode && isDraggableProp && onClick) {
            setTexts((prevTexts) =>
              prevTexts.map((t) => ({ ...t, selectedContent: null })),
            );
            setEditTextBoxContent(false);
            setShowEditTask(false);
            setShowEditNote(false);
            e.target.attrs.type = "Text";
            e.target.attrs.id = text.id;
            e.target.attrs.name = text.name;
            e.target.attrs.x = text.x || 0;
            e.target.attrs.y = text.y || 0;
            e.target.attrs.width = text.width || 0;
            e.target.attrs.height = totalContentHeight || 0;
            e.target.attrs.fontSize = text.fontSize || 0;
            markSelectedNow();
            onClick(e);
            e.cancelBubble = true;
          }
          return;
        }

        // CRITICAL: Two-click selection behavior when multi-select is NOT enabled
        // First click: Deselect the currently selected element and return early
        // Second click: Select the new element
        // When multi-select mode is enabled, don't clear selection on first click
        // Let the canvas click handler manage adding to selection instead
        if (
          selectedObject &&
          !e.evt.shiftKey &&
          !isSameElement &&
          !currentIsMultiSelectMode // Don't clear if multi-select mode is enabled
        ) {
          // Clear the other selection and return early - don't select this text element yet
          // Second click will select it
          setSelectedObject(null);
          setSelectedObjects([]);
          setSelectedText(null);
          setSelectedTextId(null);
          setShowTextBox(false);
          // CRITICAL: Stop event propagation to prevent canvas click handler from selecting the new element
          e.evt?.stopPropagation?.();
          e.evt?.preventDefault?.();
          return;
        }

        if (!isDraggableProp) {
          return;
        }

        // ALWAYS clear selectedContent from ALL texts when selecting a text element
        setTexts((prevTexts) => {
          return prevTexts.map((t) => ({
            ...t,
            selectedContent: null,
          }));
        });

        // Clear all editing states
        setEditTextBoxContent(false);
        setShowEditTask(false);
        setShowEditNote(false);

        setSelectedImage(null);
        transformerRef?.current?.nodes([]);
        setShowTextBox(true);

        // Get the latest text from texts array to ensure we have up-to-date data (including new parts/notes)
        const latestText = texts?.find((t) => t.id === text.id) || text;

        setSelectedTextId(latestText.id);
        setSelectedText({
          ...latestText,
          selectedContent: null,
        });

        // In multi-select mode, don't handle selection here - let canvas click handler do it
        // Just prepare the event attributes for the canvas handler
        if (currentIsMultiSelectMode) {
          // Don't set selectedObject or selectedObjects - let canvas click handler manage selection
          // Just set text-specific UI state
        } else {
          // Normal mode - handle selection here
          setSelectedObject({
            ...latestText,
            type: "Text",
            selectedContent: null,
          });
          //if (!e.evt.shiftKey) {
          setSelectedObjects([]);
        }
        e.target.attrs.type = "Text";
        e.target.attrs.id = text.id;
        e.target.attrs.name = text.name;
        e.target.attrs.x = text.x || 0;
        e.target.attrs.y = text.y || 0;
        e.target.attrs.width = text.width || 0;
        e.target.attrs.height = totalContentHeight || 0;
        e.target.attrs.fontSize = text.fontSize || 0;
        markSelectedNow();
        onClick(e);
        if (currentIsMultiSelectMode) {
          e.cancelBubble = true;
        }
        //}

        // setAction(ACTIONS.SELECT);
        //setShowEditNote(false);
        //setShowEditTask(false);
      }}
      onTransformEnd={(e) => {
        const node = shapeRef.current;
        const activeAnchor =
          transformActiveAnchorRef.current ||
          (typeof transformerRef?.current?.getActiveAnchor === "function"
            ? transformerRef.current.getActiveAnchor()
            : null);
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const elementStage = node.getStage?.();
        const stageWidth = elementStage?.width?.() || window.innerWidth;
        const stageHeight = elementStage?.height?.() || window.innerHeight;
        const minTransformWidth = 40;
        const stageSafeMaxWidth = Math.max(
          minTransformWidth,
          stageWidth - TRANSFORMER_ANCHOR_BUFFER_PX * 2,
        );

        const rawNewWidth = node.width() * scaleX;
        let newWidth = Math.min(
          Math.max(minTransformWidth, rawNewWidth),
          Math.min(MAX_WIDTH, stageSafeMaxWidth),
        );
        const newHeight = Math.max(40, node.height() * scaleY);

        const rightEdgeFixedX = text.x;
        const initialLeftEdgeFixedX = text.x + textBlockWidth - newWidth;
        const keepLeftEdgeFixed =
          activeAnchor === "middle-right"
            ? true
            : activeAnchor === "middle-left"
              ? false
              : Math.abs(node.x() - rightEdgeFixedX) <=
                Math.abs(node.x() - initialLeftEdgeFixedX);
        const fixedLeftX = text.x;
        const fixedRightX = text.x + textBlockWidth;

        const resolveMaxWidthForFixedEdge = () => {
          if (!isOffCanvas) {
            return keepLeftEdgeFixed ? stageWidth - fixedLeftX : fixedRightX;
          }

          const { mainZoneStart, mainZoneEnd } =
            getOffCanvasMainColumnBounds(stageWidth);
          const midpoint = (mainZoneStart + mainZoneEnd) / 2;
          if (keepLeftEdgeFixed) {
            const anchorX = fixedLeftX;
            if (anchorX <= mainZoneStart) {
              return mainZoneStart - anchorX;
            }
            if (anchorX >= mainZoneEnd) {
              return stageWidth - anchorX;
            }
            return anchorX < midpoint
              ? mainZoneStart - anchorX
              : stageWidth - anchorX;
          }

          const anchorRight = fixedRightX;
          if (anchorRight <= mainZoneStart) {
            return anchorRight;
          }
          if (anchorRight >= mainZoneEnd) {
            return anchorRight - mainZoneEnd;
          }
          return anchorRight < midpoint
            ? anchorRight
            : anchorRight - mainZoneEnd;
        };

        const maxWidthFromFixedEdge = Math.max(
          minTransformWidth,
          resolveMaxWidthForFixedEdge(),
        );
        newWidth = Math.min(newWidth, maxWidthFromFixedEdge);
        if (isOffCanvas) {
          const { mainZoneStart } = getOffCanvasMainColumnBounds(stageWidth);
          const maxWidthByLeftStrip = Math.max(
            minTransformWidth,
            mainZoneStart - TRANSFORMER_ANCHOR_BUFFER_PX * 2,
          );
          newWidth = Math.min(newWidth, maxWidthByLeftStrip);
        }

        // Reset scale and set final dimensions on node (match RectangleElement so stroke stays correct)
        node.scaleX(1);
        node.scaleY(1);
        node.width(newWidth);
        node.height(newHeight);

        // Keep the resized edge fixed (same logic as onTransform)
        const leftEdgeFixedX = text.x + textBlockWidth - newWidth;
        let finalX = keepLeftEdgeFixed ? rightEdgeFixedX : leftEdgeFixedX;
        let finalY = text.y;
        if (isOffCanvas) {
          if (elementStage) {
            const { mainZoneStart } = getOffCanvasMainColumnBounds(stageWidth);
            const insetX = Math.max(
              0,
              Math.min(
                TRANSFORMER_ANCHOR_BUFFER_PX,
                (stageWidth - newWidth) / 2,
              ),
            );
            const insetY = Math.max(
              0,
              Math.min(
                TRANSFORMER_ANCHOR_BUFFER_PX,
                (stageHeight - newHeight) / 2,
              ),
            );
            const minX = insetX;
            const maxX = Math.max(minX, mainZoneStart - newWidth - insetX);
            finalX = Math.max(minX, Math.min(maxX, finalX));
            finalY = Math.max(
              insetY,
              Math.min(stageHeight - newHeight - insetY, finalY),
            );
          }
        } else if (elementStage) {
          const insetX = Math.max(
            0,
            Math.min(
              TRANSFORMER_ANCHOR_BUFFER_PX,
              (stageWidth - newWidth) / 2,
            ),
          );
          const insetY = Math.max(
            0,
            Math.min(
              TRANSFORMER_ANCHOR_BUFFER_PX,
              (stageHeight - newHeight) / 2,
            ),
          );
          finalX = Math.max(insetX, Math.min(stageWidth - newWidth - insetX, finalX));
          finalY = Math.max(insetY, Math.min(stageHeight - newHeight - insetY, finalY));
        }
        node.x(finalX);
        node.y(finalY);
        if (isOffCanvas) {
          const elementStage = node.getStage?.();
          if (elementStage) {
            const stripClamped = clampNodeInsideOffCanvasStripsByClientRect(
              node,
              elementStage.width() || window.innerWidth,
              elementStage.height() || window.innerHeight,
              TRANSFORMER_ANCHOR_BUFFER_PX,
            );
            finalX = stripClamped.x;
            finalY = stripClamped.y;
          }
        }
        offCanvasDragStripSideRef.current = null;
        transformActiveAnchorRef.current = null;

        // Ensure TextBoxWithButtons remains visible after transform
        setShowTextBox(true);

        onTransformEnd?.({
          id: text.id,
          x: finalX,
          y: finalY,
          width: newWidth,
          height: newHeight,
          rotation: node.rotation(),
          type: "Text",
          text: text.text,
          _heightFromResize: true,
        });

        // CRITICAL: Mark that there are pending changes so history will be saved on unselection
        if (isElementBeingEdited) {
          setPendingChanges(true);
        }

        // CRITICAL: Don't save to history here - history will be saved when element is unselected
        // This groups all transform changes (width, height, rotation, etc.) into a single history entry

        // Reset transforming flag after a small delay to prevent onClick interference
        setTimeout(() => {
          isTransformingRef.current = false;
        }, 100);
      }}
    >
      {/* Background Rectangle for the Text Block */}
      <Rect
        draggable={false}
        x={0} // Relative to Group's x
        y={0} // Relative to Group's y
        width={textBlockWidth}
        height={effectiveContentHeight}
        fill={
          text.showBackground === true
            ? text.backgroundColor || "white"
            : "transparent"
        }
        cornerRadius={5}
        shadowColor={isInSelection ? "rgba(0, 0, 0, 0.15)" : undefined}
        shadowOffset={isInSelection ? { x: 0, y: 5 } : undefined}
        shadowBlur={isInSelection ? 15 : 0}
        shadowOpacity={isInSelection ? 1 : 0}
        stroke={getStrokeColor()} // Dynamic stroke color based on overflow
        strokeWidth={getStrokeWidth()} // Dynamic stroke width based on overflow
        strokeScaleEnabled={false} // Prevent stroke from scaling with zoom
        strokeOpacity={1} // Ensure full opacity for consistent rendering
        perfectDrawEnabled={false} // Enable perfect drawing for crisp edges
        shadowForStrokeEnabled={false} // Disable shadow for stroke to prevent fading
        id={`${text.id}-whiteRect`}
        name={text.name}
        listening={!isSelecting}
        fontSize={
          text.fontSize === 11
            ? "small"
            : text.fontSize === "14"
              ? "medium"
              : "big"
        }
        showBackground={text.showBackground}
        onMouseDown={(e) => {
          // CRITICAL: Store mouse position for drag detection but let event bubble to Group
          // This allows the Group's natural drag system to work when clicking on white Rect

          const stage = e.target.getStage();
          if (stage) {
            const pointerPos = stage.getPointerPosition();
            if (pointerPos) {
              dragStartMousePosRef.current = {
                x: pointerPos.x,
                y: pointerPos.y,
              };
            }
          }

          // Don't prevent default or stop propagation - let the event bubble to the Group
          // The Group will handle the drag if it's draggable
        }}
        onClick={(e) => {
          // CRITICAL: Only handle onClick if this wasn't a drag operation
          // Simply check if we were in a drag state - more reliable than mouse position
          const wasDrag = isDraggingRef.current;

          if (wasDrag) {
            return; // Don't handle as click
          }

          // Disable selection if a tool is locked
          if (lockedAction) {
            return;
          }

          // Ignore onClick if we just finished dragging or transforming
          // This prevents showTextBox from being toggled during drag/transform operations
          if (isDraggingRef.current || isTransformingRef.current) {
            setShowTextBox(true);
            return;
          }

          const currentIsMultiSelectMode =
            isMultiSelectModeRef?.current ?? isMultiSelectMode;

          // Check if clicking on the same element that's already selected
          const isSameElement = selectedObject && selectedObject.id === text.id;

          // If clicking on the same element, ensure showTextBox is true and return
          if (isSameElement) {
            setShowTextBox(true);
            if (currentIsMultiSelectMode) {
              if (toggleThisTextInMultiSelection(e)) {
                return;
              }
            }
            if (currentIsMultiSelectMode && onClick) {
              setTexts((prevTexts) =>
                prevTexts.map((t) => ({ ...t, selectedContent: null })),
              );
              setEditTextBoxContent(false);
              setShowEditTask(false);
              setShowEditNote(false);
              e.target.attrs.type = "Text";
              e.target.attrs.id = text.id;
              e.target.attrs.name = text.name;
              e.target.attrs.x = text.x;
              e.target.attrs.y = text.y;
              e.target.attrs.width = text.width;
              e.target.attrs.height = totalContentHeight;
              e.target.attrs.fontSize = text.fontSize;
              markSelectedNow();
              onClick(e);
              e.cancelBubble = true;
            }
            return;
          }

          // CRITICAL: Two-click selection behavior when multi-select is NOT enabled
          // First click: Deselect the currently selected element and return early
          // Second click: Select the new element
          // When multi-select mode is enabled, don't clear selection on first click
          // Let the canvas click handler manage adding to selection instead
          // CRITICAL: Use ref to get current value and avoid stale closures

          if (
            selectedObject &&
            !e.evt.shiftKey &&
            !isSameElement &&
            !currentIsMultiSelectMode // Don't clear if multi-select mode is enabled
          ) {
            // Clear the other selection and return early - don't select this text element yet
            // Second click will select it
            setSelectedObject(null);
            setSelectedObjects([]);
            setSelectedText(null);
            setSelectedTextId(null);
            setShowTextBox(false);
            // CRITICAL: Stop event propagation to prevent canvas click handler from selecting the new element
            e.evt?.stopPropagation?.();
            e.evt?.preventDefault?.();
            return;
          }

          // In multi-select mode, don't stop propagation - let canvas click handler manage selection
          if (!currentIsMultiSelectMode) {
            e.evt.stopPropagation(); // Prevent event bubbling to parent Group
          }

          // ALWAYS clear selectedContent from ALL texts when selecting a text element
          setTexts((prevTexts) =>
            prevTexts.map((t) => ({
              ...t,
              selectedContent: null,
            })),
          );

          // Clear all editing states
          setEditTextBoxContent(false);
          setShowEditTask(false);
          setShowEditNote(false);

          setSelectedImage(null);
          transformerRef?.current?.nodes([]);
          setShowTextBox(true);
          setSelectedTextId(text.id);
          setSelectedText({
            ...text,
            selectedContent: null,
          });

          // In multi-select mode, let canvas click handler manage selection
          // Just set the text-specific states here
          if (currentIsMultiSelectMode) {
            // Don't set selectedObject or selectedObjects here - let canvas click handler do it
            // Just prepare the event for the canvas handler
            e.target.attrs.type = "Text";
            e.target.attrs.id = text.id;
            e.target.attrs.name = text.name;
            e.target.attrs.x = text.x;
            e.target.attrs.y = text.y;
            e.target.attrs.width = text.width;
            e.target.attrs.height = totalContentHeight;
            e.target.attrs.fontSize = text.fontSize;
            markSelectedNow();
            // Don't stop propagation - let canvas click handler run
            onClick(e);
            e.cancelBubble = true;
          } else {
            // Normal mode - handle selection here
            setSelectedObject({
              ...text,
              type: "Text",
              selectedContent: null,
            });
            setSelectedObjects([]);
            e.target.attrs.type = "Text";
            e.target.attrs.id = text.id;
            e.target.attrs.name = text.name;
            e.target.attrs.x = text.x;
            e.target.attrs.y = text.y;
            e.target.attrs.width = text.width;
            e.target.attrs.height = totalContentHeight;
            e.target.attrs.fontSize = text.fontSize;
            markSelectedNow();
            onClick(e);
          }
        }}
      />

      {/* Content Section */}
      {hasUserContent(text) &&
        (() => {
          const contentFontSize =
            text.fontSize || getTextStyle(text, "content").fontSize || 14;
          const contentFontFamily = "Roboto";

          const yContentBody = currentYOffset;

          const rawContent = text.content || "";
          // Don't use cleanHtmlContent for content section as it strips span tags and styles
          // Only clean div tags to preserve inline styles and colors

          // CRITICAL PERFORMANCE FIX: Use cached flattened content during group drag to prevent expensive re-renders
          // During group drag, only Konva node positions are updated, not the HTML content
          let flattenedContent;
          if (isInGroupDragRef.current && flattenedContentRef.current) {
            flattenedContent = flattenedContentRef.current;
          } else {
            const safeContent = rawContent
              .replace(/\n/g, "<br>")
              .replace(/<div>/g, "<br>")
              .replace(/<\/div>/g, "")
              .replace(/^<br>/, "")
              .replace(/(<br>\s*){2,}/g, "<br><br>")
              .trim();
            flattenedContent = flattenNestedLists(safeContent);
          }

          // Calculate actual available width considering canvas boundary
          const actualContentWidth = textBlockWidth - 20; // Use full width - let container clip-path handle clipping

          const renderedContentHeight = measureHtmlHeight(
            flattenedContent,
            actualContentWidth,
            contentFontSize,
            contentFontFamily,
          );

          // Check if content section should be visible (using partial visibility)
          const isContentVisible = hasVisibleContent(
            yContentBody,
            renderedContentHeight,
          );

          // Get visible bounds for more precise rendering
          const contentBounds = getVisibleContentBounds(
            yContentBody,
            renderedContentHeight,
          );

          // Update currentYOffset AFTER calculating height
          currentYOffset += renderedContentHeight; // Add consistent spacing

          // Debug logging for content visibility decision
          if (text.id && canvasResizeTrigger > 0) {
          }

          // Don't render if content is not visible
          if (!isContentVisible) {
            if (!isOffCanvas && textVisibilityDebugEnabled) {
            }
            return null;
          } else {
          }

          return (
            <Group
              onClick={(e) => {
                const currentIsMultiSelectMode =
                  isMultiSelectModeRef?.current ?? isMultiSelectMode;
                // Disable selection if a tool is locked
                if (lockedAction) {
                  return;
                }

                // Check if clicking on the same element that's already selected
                const isSameElement =
                  selectedObject && selectedObject.id === text.id;

                // If clicking on the same element, ensure showTextBox is true and return
                if (isSameElement) {
                  setShowTextBox(true);
                  return;
                }

                // If there's a selected object and it's not this text element, clear selection and return
                // This requires a second click to actually select the text element
                // CRITICAL: When multi-select mode is enabled, don't clear selection on first click
                // Let the canvas click handler manage adding to selection instead
                if (
                  selectedObject &&
                  !e.evt.shiftKey &&
                  !isSameElement &&
                  !currentIsMultiSelectMode // Don't clear if multi-select mode is enabled
                ) {
                  setSelectedObject(null);
                  setSelectedObjects([]);
                  setSelectedText(null);
                  setSelectedTextId(null);
                  setShowTextBox(false);
                  e.evt.stopPropagation(); // Prevent event bubbling
                  return; // Return early - don't select the text element on first click
                }

                // In multi-select mode, don't stop propagation - let canvas click handler manage selection
                if (!currentIsMultiSelectMode) {
                  e.evt.stopPropagation(); // Prevent event bubbling to parent Group
                }

                // ALWAYS clear selectedContent from ALL texts when selecting a text element
                setTexts((prevTexts) =>
                  prevTexts.map((t) => ({
                    ...t,
                    selectedContent: null,
                  })),
                );

                // Clear all editing states
                setEditTextBoxContent(false);
                setShowEditTask(false);
                setShowEditNote(false);

                // Trigger selection
                setSelectedImage(null);
                transformerRef?.current?.nodes([]);
                setShowTextBox(true);
                setSelectedTextId(text.id);
                setSelectedText({
                  ...text,
                  selectedContent: null,
                });

                // In multi-select mode, don't set selectedObject - let canvas click handler do it
                if (!currentIsMultiSelectMode) {
                  setSelectedObject({
                    ...text,
                    type: "Text",
                    selectedContent: null,
                  });
                  setSelectedObjects([]);
                }
                // Create a Konva-like event object for HTML events
                const konvaEvent = {
                  target: {
                    getClassName: () => "Group",
                    attrs: {
                      id: text.id,
                      type: "Text",
                      name: text.name,
                      x: text.x || 0,
                      y: text.y || 0,
                      width: text.width || 0,
                      height: textGroupHeight || 0,
                      fontSize: text.fontSize || 0,
                    },
                  },
                  evt: e.nativeEvent || e,
                };
                markSelectedNow();
                onClick(konvaEvent);
              }}
              onDblClick={(e) => {
                if (!allowDbl()) return;
                e.evt.stopPropagation();
                e.evt.preventDefault();

                // Set flag to prevent onClick from interfering
                doubleClickPendingRef.current = true;

                // CRITICAL: Use flushSync to force editTextBoxContent to update IMMEDIATELY before other state updates
                // This ensures TextBoxWithButtons sees editTextBoxContent=true on the next render
                flushSync(() => {
                  setEditTextBoxContent(true);
                  setShowEditTask(false);
                  setShowEditNote(false);
                });

                // Now update selectedText with cleared selectedContent
                setSelectedText((prev) => {
                  if (!prev || prev.id !== text.id) return prev;
                  return { ...prev, selectedContent: null };
                });

                // Clear selectedContent in texts array
                setTexts((prevTexts) =>
                  prevTexts.map((t) =>
                    t.id === text.id ? { ...t, selectedContent: null } : t,
                  ),
                );

                setShowTextBox(true);
                setSelectedTextId(text.id);
                setSelectedObject({
                  ...text,
                  type: "Text",
                  selectedContent: null,
                });

                // Clear flag after a short delay
                setTimeout(() => {
                  doubleClickPendingRef.current = false;
                }, 300);
              }}
            >
              <Html
                divProps={{
                  "data-text-id": text.id,
                  "data-offset-x": 0,
                  "data-offset-y": 0,
                  "data-primary-content": "true",
                  style: {
                    zIndex:
                      isDraggingRef.current || isTextSelected() ? 35 : 10, // Keep dragged text above top-layer images
                    width: `${textBlockWidth}px`,
                    height: `${totalContentHeight}px`,
                    // CRITICAL: Disable pointer events for HTML content when in multi-selection
                    // This prevents text selection and allows group dragging to work properly
                    // CRITICAL: Also disable pointer events when selection box is being drawn to allow selection box drawing
                    pointerEvents: (() => {
                      // If selection box is being drawn, disable pointer events to allow selection box to be drawn over the element
                      if (isSelecting) {
                        return "none";
                      }
                      // If text is in multi-selection, disable all pointer events on HTML content
                      if (selectedObjects && selectedObjects.length > 1) {
                        const isInMultiSelection = selectedObjects.some(
                          (obj) =>
                            obj.id === text.id &&
                            (obj.type === "Text" || obj.type?.includes("Text")),
                        );
                        if (isInMultiSelection) {
                          return "none"; // Disable all pointer events for HTML content
                        }
                      }
                      // Normal behavior: let clicks pass through to inner divs
                      return "none";
                    })(),
                    position: "absolute", // Ensure z-index works
                    // IMPORTANT: Html overlay must respect element opacity (used to keep Konva nodes mounted but invisible during drag previews)
                    opacity: text?.opacity !== undefined ? text.opacity : 1,
                    visibility:
                      text?.opacity !== undefined && text.opacity <= 0
                        ? "hidden"
                        : "visible",
                    // CRITICAL: Clip content that extends outside canvas boundaries (only for main canvas)
                    ...(isOffCanvas
                      ? {}
                      : (() => {
                          // Use actual pixel width (same as RectangleElement and CircleElement)
                          const canvasPixelWidth =
                            canvasWidth || window.innerWidth * 0.45;

                          // Calculate visible bounds within canvas
                          const textLeft = text.x || 0;
                          const textTop = text.y || 0;
                          const textRight = textLeft + textBlockWidth;
                          const textBottom = textTop + totalContentHeight;

                          // Only clip if content is ACTUALLY outside the canvas boundaries
                          // Calculate actual overflow amounts (how much extends outside)
                          const actualOverflowLeft = Math.max(0, -textLeft);
                          const actualOverflowTop = Math.max(0, -textTop);
                          const actualOverflowRight = Math.max(
                            0,
                            textRight - canvasPixelWidth,
                          );
                          const actualOverflowBottom = Math.max(
                            0,
                            textBottom - effectiveCanvasHeight,
                          );

                          // Only apply clipping if there's actual overflow (content extends outside)
                          if (
                            actualOverflowLeft > 0 ||
                            actualOverflowTop > 0 ||
                            actualOverflowRight > 0 ||
                            actualOverflowBottom > 0
                          ) {
                            // Keep full width/height - only use clip-path to hide overflow
                            // This allows content to remain visible until it actually extends outside
                            // clip-path inset() takes: top, right, bottom, left (distances from edges)
                            return {
                              overflow: "hidden",
                              clipPath: `inset(${actualOverflowTop}px ${actualOverflowRight}px ${actualOverflowBottom}px ${actualOverflowLeft}px)`,
                              // Keep original width and height - clip-path handles the clipping
                              width: `${textBlockWidth}px`,
                              height: `${totalContentHeight}px`,
                            };
                          }
                          // CRITICAL: Explicitly remove clipping when element is fully inside canvas
                          // This ensures clip-path is removed when element comes back into canvas
                          return {
                            overflow: "visible",
                            clipPath: "none",
                            width: `${textBlockWidth}px`,
                            height: `${totalContentHeight}px`,
                          };
                        })()),
                  },
                }}
                onDblClick={(e) => {
                  if (!allowDbl()) return;
                  e.evt.stopPropagation();
                  e.evt.preventDefault();

                  // Set flag to prevent onClick from interfering
                  doubleClickPendingRef.current = true;

                  // CRITICAL: Use flushSync to force editTextBoxContent to update IMMEDIATELY before other state updates
                  // This ensures TextBoxWithButtons sees editTextBoxContent=true on the next render
                  flushSync(() => {
                    setEditTextBoxContent(true);
                    setShowEditTask(false);
                    setShowEditNote(false);
                  });

                  // Now update selectedText with cleared selectedContent
                  setSelectedText((prev) => {
                    if (!prev || prev.id !== text.id) return prev;
                    return { ...prev, selectedContent: null };
                  });

                  // Clear selectedContent in texts array
                  setTexts((prevTexts) =>
                    prevTexts.map((t) =>
                      t.id === text.id ? { ...t, selectedContent: null } : t,
                    ),
                  );

                  setShowTextBox(true);
                  setSelectedTextId(text.id);
                  setSelectedObject({
                    ...text,
                    type: "Text",
                    selectedContent: null,
                  });

                  // Clear flag after a short delay
                  setTimeout(() => {
                    doubleClickPendingRef.current = false;
                  }, 300);
                }}
              >
                <div
                  id={`content-div-${text.id}`}
                  data-export="true"
                  data-content-type="content"
                  data-groupid={text.id}
                  dangerouslySetInnerHTML={{
                    __html: flattenedContent || "",
                  }}
                  onDoubleClick={(e) => {
                    // CRITICAL: If text is in multi-selection, don't handle double-click
                    if (selectedObjects && selectedObjects.length > 1) {
                      const isInMultiSelection = selectedObjects.some(
                        (obj) =>
                          obj.id === text.id &&
                          (obj.type === "Text" || obj.type?.includes("Text")),
                      );
                      if (isInMultiSelection) {
                        return;
                      }
                    }

                    // Check if double-click is allowed
                    if (!allowDbl()) {
                      return;
                    }

                    e.stopPropagation();
                    e.preventDefault();

                    // Set flag to prevent onClick from interfering
                    doubleClickPendingRef.current = true;

                    // CRITICAL: Use flushSync to force editTextBoxContent to update IMMEDIATELY before other state updates
                    // This ensures TextBoxWithButtons sees editTextBoxContent=true on the next render
                    flushSync(() => {
                      setEditTextBoxContent(true);
                      setShowEditTask(false);
                      setShowEditNote(false);
                    });

                    // Now update selectedText with cleared selectedContent
                    setSelectedText((prev) => {
                      if (!prev || prev.id !== text.id) return prev;
                      return { ...prev, selectedContent: null };
                    });

                    // Clear selectedContent in texts array
                    setTexts((prevTexts) =>
                      prevTexts.map((t) =>
                        t.id === text.id ? { ...t, selectedContent: null } : t,
                      ),
                    );

                    setShowTextBox(true);
                    setSelectedTextId(text.id);
                    setSelectedObject({
                      ...text,
                      type: "Text",
                      selectedContent: null,
                    });

                    // Clear flag after a short delay
                    setTimeout(() => {
                      doubleClickPendingRef.current = false;
                    }, 300);
                  }}
                  onMouseDown={(e) => {
                    // CRITICAL: If text is in multi-selection, don't handle mouse events
                    // Let them pass through to the Konva Group for group dragging
                    if (selectedObjects && selectedObjects.length > 1) {
                      const isInMultiSelection = selectedObjects.some(
                        (obj) =>
                          obj.id === text.id &&
                          (obj.type === "Text" || obj.type?.includes("Text")),
                      );
                      if (isInMultiSelection) {
                        e.preventDefault();
                        e.stopPropagation();
                        return; // Don't handle the event
                      }
                    }

                    // Check if there's a Konva element (like an orb) at this position
                    // If so, let the event pass through to allow dragging the orb
                    const stage = shapeRef.current?.getStage();
                    if (stage) {
                      // Convert client coordinates to stage coordinates
                      const stageBox = stage
                        .container()
                        .getBoundingClientRect();
                      const stageScaleX = stage.scaleX();
                      const stageScaleY = stage.scaleY();
                      const stageX = stage.x();
                      const stageY = stage.y();

                      const pointerPos = {
                        x: (e.clientX - stageBox.left) / stageScaleX - stageX,
                        y: (e.clientY - stageBox.top) / stageScaleY - stageY,
                      };

                      // Check if there's a Konva shape at this position
                      const shape = stage.getIntersection(pointerPos);
                      // If the shape is a Circle that's draggable (likely an orb from UnifiedLineArrowElement), let it pass through
                      if (
                        shape &&
                        shape.getType() === "Circle" &&
                        shape.draggable()
                      ) {
                        // Check if it's part of a unified element (Arrow/Line/DoubleSidedArrow)
                        const parent = shape.getParent();
                        if (parent) {
                          const parentType =
                            parent.attrs?.type || parent.attrs?.name;
                          if (
                            parentType === "Arrow" ||
                            parentType === "Line" ||
                            parentType === "DoubleSidedArrow"
                          ) {
                            // Prevent this DOM element from handling the event, let it pass through to Konva
                            e.stopPropagation();
                            e.preventDefault();
                            return; // Let the event pass through to the Konva orb
                          }
                        }
                      }
                    }
                    handleHtmlMouseDown(e);
                  }}
                  onClick={(e) => {
                    const currentIsMultiSelectMode =
                      isMultiSelectModeRef?.current ?? isMultiSelectMode;
                    // CRITICAL: If text is in multi-selection, don't handle click events
                    // Let them pass through to the Konva Group for selection management
                    if (selectedObjects && selectedObjects.length > 1) {
                      const isInMultiSelection = selectedObjects.some(
                        (obj) =>
                          obj.id === text.id &&
                          (obj.type === "Text" || obj.type?.includes("Text")),
                      );
                      if (isInMultiSelection) {
                        e.preventDefault();
                        e.stopPropagation();
                        return; // Don't handle the event
                      }
                    }

                    // Check if there's a Konva element (like an orb) at this position
                    // If so, let the event pass through to allow clicking the orb
                    const stage = shapeRef.current?.getStage();
                    if (stage) {
                      // Convert client coordinates to stage coordinates
                      const stageBox = stage
                        .container()
                        .getBoundingClientRect();
                      const stageScaleX = stage.scaleX();
                      const stageScaleY = stage.scaleY();
                      const stageX = stage.x();
                      const stageY = stage.y();

                      const pointerPos = {
                        x: (e.clientX - stageBox.left) / stageScaleX - stageX,
                        y: (e.clientY - stageBox.top) / stageScaleY - stageY,
                      };

                      // Check if there's a Konva shape at this position
                      const shape = stage.getIntersection(pointerPos);
                      // If the shape is a Circle that's draggable (likely an orb from UnifiedLineArrowElement), let it pass through
                      if (
                        shape &&
                        shape.getType() === "Circle" &&
                        shape.draggable()
                      ) {
                        // Check if it's part of a unified element (Arrow/Line/DoubleSidedArrow)
                        const parent = shape.getParent();
                        if (parent) {
                          const parentType =
                            parent.attrs?.type || parent.attrs?.name;
                          if (
                            parentType === "Arrow" ||
                            parentType === "Line" ||
                            parentType === "DoubleSidedArrow"
                          ) {
                            // Prevent this DOM element from handling the event, let it pass through to Konva
                            e.stopPropagation();
                            e.preventDefault();
                            return; // Let the event pass through to the Konva orb
                          }
                        }
                      }
                    }

                    // Check if click is on a TYPE heading area
                    // TYPE headings are Konva Groups that might be blocked by HTML overlay
                    // So we need to handle clicks on TYPE heading areas here

                    if (
                      text.partsByType &&
                      Object.keys(text.partsByType || {}).some(
                        (key) =>
                          key && text.partsByType[key]?.parts?.length > 0,
                      )
                    ) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      // Account for the div's top position (yContentBody) in the click Y calculation
                      const clickY = e.clientY - rect.top;

                      // Calculate TYPE heading positions (same logic as in render)
                      // Note: clickY is relative to the div, which starts at yContentBody
                      // So we need to subtract yContentBody to get the position relative to content start
                      let currentYOffset = 10; // Initial padding (relative to content start, not div start)

                      // Add content section height if it exists
                      if (hasUserContent(text)) {
                        const contentFontSize =
                          text.fontSize ||
                          getTextStyle(text, "content").fontSize ||
                          14;
                        const contentFontFamily = "Roboto";
                        const rawContent = text.content || "";
                        const safeContent = rawContent
                          .replace(/\n/g, "<br>")
                          .replace(/<div>/g, "<br>")
                          .replace(/<\/div>/g, "")
                          .replace(/^<br>/, "")
                          .replace(/(<br>\s*){2,}/g, "<br><br>")
                          .trim();
                        const flattenedContent =
                          flattenNestedLists(safeContent);
                        const actualContentWidth = Math.floor(
                          textBlockWidth - 20,
                        ); // Use full width - let container clip-path handle clipping;
                        const renderedContentHeight = measureHtmlHeight(
                          flattenedContent,
                          actualContentWidth,
                          contentFontSize,
                          contentFontFamily,
                        );
                        currentYOffset += renderedContentHeight;
                      }

                      // Check each TYPE heading
                      const typeEntries = Object.entries(text.partsByType).sort(
                        (a, b) => (a[1].order || 9999) - (b[1].order || 9999),
                      );

                      for (const [typeName, typeData] of typeEntries) {
                        if (!typeData.parts || typeData.parts.length === 0)
                          continue;

                        const typeFontSize =
                          text.fontSize ||
                          text.textStyles?.type?.fontSize ||
                          16;
                        const typeFontFamily = "Roboto";
                        const typeTextHeight = estimateKonvaTextHeight(
                          typeName,
                          typeFontSize,
                          typeFontFamily,
                          textBlockWidth - 20,
                        );

                        const typeY = currentYOffset;
                        const typeHeight = typeTextHeight + 8;
                        const typeLeft = 10;
                        const typeWidth = textBlockWidth - 20;

                        // Check if click is within this TYPE heading area
                        if (
                          clickX >= typeLeft &&
                          clickX <= typeLeft + typeWidth &&
                          clickY >= typeY &&
                          clickY <= typeY + typeHeight
                        ) {
                          // Stop propagation to prevent other handlers
                          e.stopPropagation();

                          // If text is not selected, trigger selection directly
                          const isSameElement =
                            selectedObject && selectedObject.id === text.id;
                          if (!isSameElement && !selectedObject) {
                            // Get the latest text from texts array
                            const latestText =
                              texts?.find((t) => t.id === text.id) || text;

                            // Clear selectedContent from all texts
                            setTexts((prevTexts) =>
                              prevTexts.map((t) => ({
                                ...t,
                                selectedContent: null,
                              })),
                            );

                            // Clear all editing states
                            setEditTextBoxContent(false);
                            setShowEditTask(false);
                            setShowEditNote(false);

                            setSelectedImage(null);
                            transformerRef?.current?.nodes([]);
                            setShowTextBox(true);

                            setSelectedTextId(latestText.id);
                            setSelectedText({
                              ...latestText,
                              selectedContent: null,
                            });

                            if (!currentIsMultiSelectMode) {
                              setSelectedObject({
                                ...latestText,
                                type: "Text",
                                selectedContent: null,
                              });
                              setSelectedObjects([]);
                            }

                            // Create a Konva-like event object for the onClick callback
                            const konvaEvent = {
                              target: {
                                getClassName: () => "Group",
                                attrs: {
                                  id: text.id,
                                  type: "Text",
                                  name: text.name,
                                  x: text.x || 0,
                                  y: text.y || 0,
                                  width: text.width || 0,
                                  height: totalContentHeight || 0,
                                  fontSize: text.fontSize || 0,
                                },
                              },
                              evt: e.nativeEvent || e,
                            };
                            markSelectedNow();
                            onClick(konvaEvent);
                            return; // Exit early, don't continue with content logic
                          }

                          if (delegateMultiSelectMergeText(e)) {
                            return; // Exit early
                          }

                          // If text is already selected, just ensure showTextBox is true
                          if (isSameElement) {
                            setShowTextBox(true);
                            return; // Exit early
                          }

                          // For other cases, let it fall through to normal handling
                          break; // Exit loop, continue with normal handling
                        }

                        // Update offset for next TYPE heading
                        const spacingReduction = text.fontSize > 14 ? 16 : 5;
                        currentYOffset += typeTextHeight - spacingReduction;
                      }
                    }

                    // If in drawing mode, allow events to pass through to Background
                    if (shouldAllowPointerEventsThrough()) {
                      return; // Don't handle the event, let it pass through
                    }

                    // Disable selection if a tool is locked
                    if (lockedAction) {
                      return;
                    }

                    // If double-click is pending, ignore single click to prevent interference
                    if (doubleClickPendingRef.current) {
                      doubleClickPendingRef.current = false;
                      return;
                    }

                    // Ignore onClick if we just finished dragging or transforming
                    if (isDraggingRef.current || isTransformingRef.current) {
                      setShowTextBox(true);
                      return;
                    }

                    // Check if clicking on the same element that's already selected
                    const isSameElement =
                      selectedObject && selectedObject.id === text.id;

                    // If clicking on the same element and already editing, don't reset editing states
                    if (
                      isSameElement &&
                      (editTextBoxContent ||
                        showEditTask ||
                        showEditNote ||
                        text.selectedContent)
                    ) {
                      e.stopPropagation(); // Prevent event bubbling to parent Group
                      setShowTextBox(true);
                      return;
                    }

                    // If clicking on the same element, ensure showTextBox is true and return
                    // CRITICAL: Stop propagation to prevent parent Group from clearing selectedContent
                    // This allows double-click to work properly when clicking on content while part is selected
                    if (isSameElement) {
                      e.stopPropagation(); // Prevent event bubbling to parent Group
                      setShowTextBox(true);
                      return;
                    }

                    // CRITICAL: If text element is NOT selected, trigger selection directly
                    // Html components don't bubble to Konva Groups, so we need to handle it here
                    if (!isSameElement && !selectedObject) {
                      // Get the latest text from texts array
                      const latestText =
                        texts?.find((t) => t.id === text.id) || text;

                      // Clear selectedContent from all texts
                      setTexts((prevTexts) =>
                        prevTexts.map((t) => ({
                          ...t,
                          selectedContent: null,
                        })),
                      );

                      // Clear all editing states
                      setEditTextBoxContent(false);
                      setShowEditTask(false);
                      setShowEditNote(false);

                      setSelectedImage(null);
                      transformerRef?.current?.nodes([]);
                      setShowTextBox(true);

                      setSelectedTextId(latestText.id);
                      setSelectedText({
                        ...latestText,
                        selectedContent: null,
                      });

                      setSelectedObject({
                        ...latestText,
                        type: "Text",
                        selectedContent: null,
                      });
                      setSelectedObjects([]);

                      // Create a Konva-like event object for the onClick callback
                      const konvaEvent = {
                        target: {
                          getClassName: () => "Group",
                          attrs: {
                            id: text.id,
                            type: "Text",
                            name: text.name,
                            x: text.x || 0,
                            y: text.y || 0,
                            width: text.width || 0,
                            height: totalContentHeight || 0,
                            fontSize: text.fontSize || 0,
                          },
                        },
                        evt: e.nativeEvent || e,
                      };
                      markSelectedNow();
                      onClick(konvaEvent);

                      return; // Don't continue with content-specific logic
                    }

                    // CRITICAL: Two-click selection behavior when multi-select is NOT enabled
                    // First click: Deselect the currently selected element and return early
                    // Second click: Select the new element
                    // When multi-select mode is enabled, don't clear selection on first click
                    // Let the canvas click handler manage adding to selection instead
                    if (
                      selectedObject &&
                      !e.shiftKey &&
                      !isSameElement &&
                      !currentIsMultiSelectMode // Don't clear if multi-select mode is enabled
                    ) {
                      // Clear the other selection and return early - don't select this text element yet
                      // Second click will select it
                      setSelectedObject(null);
                      setSelectedObjects([]);
                      setSelectedText(null);
                      setSelectedTextId(null);
                      setShowTextBox(false);
                      // CRITICAL: Stop event propagation to prevent canvas click handler from selecting the new element
                      e.stopPropagation?.();
                      e.preventDefault?.();
                      return;
                    }

                    e.stopPropagation(); // Prevent event bubbling to parent Group

                    // ALWAYS clear selectedContent from ALL texts when selecting a text element
                    setTexts((prevTexts) =>
                      prevTexts.map((t) => ({
                        ...t,
                        selectedContent: null,
                      })),
                    );

                    // Clear all editing states
                    setEditTextBoxContent(false);
                    setShowEditTask(false);
                    setShowEditNote(false);

                    // Trigger selection
                    setSelectedImage(null);
                    transformerRef?.current?.nodes([]);
                    setShowTextBox(true);
                    setSelectedTextId(text.id);
                    setSelectedText({
                      ...text,
                      selectedContent: null,
                    });
                    // Match Konva Group onClick: in multi-select, canvas onClick merges selection — do not replace/clear here
                    if (!currentIsMultiSelectMode) {
                      setSelectedObject({
                        ...text,
                        type: "Text",
                        selectedContent: null,
                      });
                      setSelectedObjects([]);
                    }
                    // Create a Konva-like event object for HTML events
                    const konvaEvent = {
                      target: {
                        getClassName: () => "Group",
                        attrs: {
                          id: text.id,
                          type: "Text",
                          name: text.name,
                          x: text.x || 0,
                          y: text.y || 0,
                          width: text.width || 0,
                          height: textGroupHeight || 0,
                          fontSize: text.fontSize || 0,
                        },
                      },
                      evt: e.nativeEvent || e,
                    };
                    markSelectedNow();
                    onClick(konvaEvent);
                  }}
                  style={getConsistentHtmlStyles({
                    position: "absolute",
                    top: yContentBody,
                    left: 10,
                    width: `${textBlockWidth - 20}px`, // Use full width - let container clip-path handle clipping
                    fontFamily: contentFontFamily,
                    fontSize: `${contentFontSize}px`,
                    cursor: getHtmlHoverCursor(),
                    marginLeft: "0px",
                    textAlign: "left",
                    overflowX: "hidden",
                    overflowY: "hidden",
                    maxWidth: `${textBlockWidth - 20}px`, // Use full max width - let container clip-path handle clipping
                    maxHeight: `${Math.max(
                      0,
                      Math.min(
                        renderedContentHeight,
                        effectiveCanvasHeight - (text.y + yContentBody),
                      ),
                    )}px`,
                    // Let content determine its own height naturally
                    minHeight: "auto",
                    userSelect: isTextSelectionDisabled() ? "none" : "auto",
                    WebkitUserSelect: isTextSelectionDisabled()
                      ? "none"
                      : "auto",
                    MozUserSelect: isTextSelectionDisabled() ? "none" : "auto",
                    msUserSelect: isTextSelectionDisabled() ? "none" : "auto",
                    // CRITICAL: Disable pointer events when in multi-selection to prevent text selection
                    // When not selected, set to "none" so other elements (like UnifiedLineArrowElement orbs) can interact
                    pointerEvents: getHtmlPointerEvents(),
                    WebkitTouchCallout: isTextSelectionDisabled()
                      ? "none"
                      : "default",
                    WebkitUserDrag: isTextSelectionDisabled() ? "none" : "auto",
                    opacity: text.opacity !== undefined ? text.opacity : 1, // Apply element opacity
                    clipPath: "none",
                    WebkitClipPath: "none",
                  })}
                  className="content-text"
                />
              </Html>
            </Group>
          );
        })()}

      {/* PartsByType Section */}
      {text.partsByType &&
        Object.keys(text.partsByType || {}).some(
          (key) => key && text.partsByType[key]?.parts?.length > 0,
        ) &&
        (() => {
          const partGroups = [];
          const typeHeadingHtmlOverlays = []; // Collect TYPE heading HTML overlays separately

          Object.entries(text.partsByType)
            .sort((a, b) => (a[1].order || 9999) - (b[1].order || 9999))
            .forEach(([typeName, typeData]) => {
              if (!typeData.parts || typeData.parts.length === 0) return;

              const typeFontSize =
                text.fontSize || text.textStyles?.type?.fontSize || 16;
              const typeFontFamily = "Roboto";

              const typeTextHeight = estimateKonvaTextHeight(
                typeName,
                typeFontSize,
                typeFontFamily,
                textBlockWidth - 20,
              );

              const typeY = currentYOffset;

              // Check if type section should be visible
              // FINAL FIX: Always render TYPE sections while the TextElement itself
              // is rendered. Canvas boundary and overflow (red stroke) are handled
              // at the Group level. Per-section clipping is handled via CSS/clip-path,
              // so we avoid extra visibility gates here that can hide partobjs when
              // the text touches the canvas border.
              const isTypeVisible = true;

              // Add TYPE heading HTML overlay to partElements FIRST so it's on top
              // Position it relative to the PARTS Group (x=10, y=typeY relative to PARTS Group)
              // CRITICAL: Only cover the TYPE heading area, not the entire content area
              const typeHeadingWidth = textBlockWidth - 20;
              const typeHeadingHeight = typeTextHeight + 8;
              const typeHeadingHtmlOverlay = isTypeVisible ? (
                <Html
                  key={`type-heading-html-${typeName}`}
                  x={10}
                  y={typeY}
                  divProps={{
                    "data-type-heading": "true",
                    "data-text-id": text.id,
                    "data-offset-x": 10,
                    "data-offset-y": typeY,
                    style: {
                      zIndex: 99999, // Very high z-index to ensure it's above everything
                      width: `${typeHeadingWidth}px`, // Only cover the TYPE heading width
                      height: `${typeHeadingHeight}px`, // Only cover the TYPE heading height
                      // CRITICAL: Disable pointer events when in multi-selection to prevent text selection
                      // When not selected, set to "none" so other elements (like UnifiedLineArrowElement orbs) can interact
                      pointerEvents: getHtmlPointerEvents(),
                      position: "absolute",
                      visibility: "visible",
                      opacity: 1,
                      backgroundColor: "transparent", // Invisible but clickable
                      cursor: getHtmlHoverCursor(),
                      // CRITICAL: Clip content that extends outside canvas boundaries (only for main canvas)
                      ...(isOffCanvas
                        ? {}
                        : (() => {
                            // Use actual pixel width (same as RectangleElement and CircleElement)
                            const canvasPixelWidth =
                              canvasWidth || window.innerWidth * 0.45;

                            // Calculate visible bounds within canvas using TYPE heading bounds
                            const textLeft = text.x || 0;
                            const textTop = text.y || 0;
                            // TYPE heading is positioned at x=10 relative to text, so absolute x = textLeft + 10
                            const typeHeadingLeft = textLeft + 10;
                            const typeHeadingTop = textTop + typeY;
                            const typeHeadingRight =
                              typeHeadingLeft + typeHeadingWidth;
                            const typeHeadingBottom =
                              typeHeadingTop + typeHeadingHeight;

                            // Only clip if content is ACTUALLY outside the canvas boundaries
                            // Calculate actual overflow amounts (how much extends outside)
                            const actualOverflowLeft = Math.max(
                              0,
                              -typeHeadingLeft,
                            );
                            const actualOverflowTop = Math.max(
                              0,
                              -typeHeadingTop,
                            );
                            const actualOverflowRight = Math.max(
                              0,
                              typeHeadingRight - canvasPixelWidth,
                            );
                            const actualOverflowBottom = Math.max(
                              0,
                              typeHeadingBottom - effectiveCanvasHeight,
                            );

                            // Only apply clipping if there's actual overflow (content extends outside)
                            if (
                              actualOverflowLeft > 0 ||
                              actualOverflowTop > 0 ||
                              actualOverflowRight > 0 ||
                              actualOverflowBottom > 0
                            ) {
                              // Use clip-path to hide overflow
                              // clip-path inset() takes: top, right, bottom, left (distances from edges)
                              return {
                                overflow: "hidden",
                                clipPath: `inset(${actualOverflowTop}px ${actualOverflowRight}px ${actualOverflowBottom}px ${actualOverflowLeft}px)`,
                                // Keep original width and height - clip-path handles the clipping
                                width: `${typeHeadingWidth}px`,
                                height: `${typeHeadingHeight}px`,
                              };
                            }
                            // CRITICAL: Explicitly remove clipping when element is fully inside canvas
                            // This ensures clip-path is removed when element comes back into canvas
                            return {
                              overflow: "visible",
                              clipPath: "none",
                              width: `${typeHeadingWidth}px`,
                              height: `${typeHeadingHeight}px`,
                            };
                          })()),
                    },
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                    }}
                    onClick={(e) => {
                      // CRITICAL: Verify click is actually within the TYPE heading bounds
                      // The TYPE heading starts at typeY (relative to text element Group)
                      // We need to check if the click is actually on the TYPE heading, not on content above it
                      const rect = e.currentTarget.getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      const clickY = e.clientY - rect.top;

                      // The div should be exactly typeHeadingWidth x typeHeadingHeight
                      // If click is outside these bounds, let it pass through to content handler
                      // CRITICAL: clickY < 0 means click is above the TYPE heading (on content)
                      // CRITICAL: clickY > typeHeadingHeight means click is below the TYPE heading
                      // CRITICAL: clickX outside bounds means click is to the side
                      const isOutsideBounds =
                        clickX < 0 ||
                        clickX > typeHeadingWidth ||
                        clickY < 0 ||
                        clickY > typeHeadingHeight;

                      if (isOutsideBounds) {
                        // CRITICAL: Don't stop propagation or prevent default - let the click pass through to content handler
                        // This allows double-click on content lines 2 and 3 to work properly
                        // The click will bubble up to the content HTML overlay handler
                        return;
                      }

                      e.stopPropagation();
                      e.preventDefault();

                      // Disable selection if a tool is locked
                      if (lockedAction) {
                        return;
                      }

                      // If double-click is pending, ignore single click
                      if (doubleClickPendingRef.current) {
                        return;
                      }

                      // Ignore onClick if we just finished dragging or transforming
                      if (isDraggingRef.current || isTransformingRef.current) {
                        setShowTextBox(true);
                        return;
                      }

                      // If text is not selected, trigger selection directly
                      const isSameElement =
                        selectedObject && selectedObject.id === text.id;
                      if (!isSameElement && !selectedObject) {
                        // Get the latest text from texts array
                        const latestText =
                          texts?.find((t) => t.id === text.id) || text;

                        // Clear selectedContent from all texts
                        setTexts((prevTexts) =>
                          prevTexts.map((t) => ({
                            ...t,
                            selectedContent: null,
                          })),
                        );

                        // Clear all editing states
                        setEditTextBoxContent(false);
                        setShowEditTask(false);
                        setShowEditNote(false);

                        setSelectedImage(null);
                        transformerRef?.current?.nodes([]);
                        setShowTextBox(true);

                        setSelectedTextId(latestText.id);
                        setSelectedText({
                          ...latestText,
                          selectedContent: null,
                        });

                        setSelectedObject({
                          ...latestText,
                          type: "Text",
                          selectedContent: null,
                        });
                        setSelectedObjects([]);

                        // Create a Konva-like event object for the onClick callback
                        const konvaEvent = {
                          target: {
                            getClassName: () => "Group",
                            attrs: {
                              id: text.id,
                              type: "Text",
                              name: text.name,
                              x: text.x || 0,
                              y: text.y || 0,
                              width: text.width || 0,
                              height: totalContentHeight || 0,
                              fontSize: text.fontSize || 0,
                            },
                          },
                          evt: e.nativeEvent || e,
                        };
                        markSelectedNow();
                        onClick(konvaEvent);
                        return;
                      }

                      if (delegateMultiSelectMergeText(e)) {
                        return;
                      }

                      // If text is already selected, just ensure showTextBox is true
                      if (isSameElement) {
                        setShowTextBox(true);
                        return;
                      }
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();

                      // Set flag to prevent onClick from interfering
                      doubleClickPendingRef.current = true;

                      // CRITICAL: Use flushSync to force editTextBoxContent to update IMMEDIATELY before other state updates
                      // This ensures TextBoxWithButtons sees editTextBoxContent=true on the next render
                      flushSync(() => {
                        setEditTextBoxContent(true);
                        setShowEditTask(false);
                        setShowEditNote(false);
                      });

                      // Now update selectedText with cleared selectedContent
                      setSelectedText((prev) => {
                        if (!prev || prev.id !== text.id) return prev;
                        return { ...prev, selectedContent: null };
                      });

                      // Clear selectedContent in texts array
                      setTexts((prevTexts) =>
                        prevTexts.map((t) =>
                          t.id === text.id
                            ? { ...t, selectedContent: null }
                            : t,
                        ),
                      );

                      setShowTextBox(true);
                      setSelectedTextId(text.id);
                      setSelectedObject({
                        ...text,
                        type: "Text",
                        selectedContent: null,
                      });

                      // Clear flag after a short delay
                      setTimeout(() => {
                        doubleClickPendingRef.current = false;
                      }, 300);
                    }}
                    onMouseDown={handleHtmlMouseDown}
                    onMouseUp={(e) => {
                      // CRITICAL: If we were dragging, don't interfere - let drag end event reach text element
                      if (isDraggingRef.current) {
                        // Don't stop propagation - let the drag end event pass through to the text element
                        return;
                      }
                    }}
                    onMouseMove={(e) => {
                      // CRITICAL: If mouse moves with button pressed, we're dragging - let it pass through
                      if (e.buttons === 1) {
                        // Don't interfere with drag operations - let events pass through to text element
                        return;
                      }
                    }}
                  />
                </Html>
              ) : null;

              const typeElement = isTypeVisible ? (
                <Group
                  key={`type-group-${typeName}`}
                  x={10}
                  y={typeY}
                  width={textBlockWidth - 20}
                  height={typeTextHeight + 8}
                  listening={true}
                  onClick={(e) => {
                    // CRITICAL: Stop propagation FIRST to prevent PARTS Group from intercepting
                    e.evt.stopPropagation();
                    e.evt.preventDefault();

                    // Disable selection if a tool is locked
                    if (lockedAction) {
                      return;
                    }

                    // If double-click is pending, ignore single click
                    if (doubleClickPendingRef.current) {
                      return;
                    }

                    // Ignore onClick if we just finished dragging or transforming
                    if (isDraggingRef.current || isTransformingRef.current) {
                      setShowTextBox(true);
                      return;
                    }

                    // If text is not selected, trigger selection directly
                    const isSameElement =
                      selectedObject && selectedObject.id === text.id;
                    if (!isSameElement && !selectedObject) {
                      // Get the latest text from texts array
                      const latestText =
                        texts?.find((t) => t.id === text.id) || text;

                      // Clear selectedContent from all texts
                      setTexts((prevTexts) =>
                        prevTexts.map((t) => ({
                          ...t,
                          selectedContent: null,
                        })),
                      );

                      // Clear all editing states
                      setEditTextBoxContent(false);
                      setShowEditTask(false);
                      setShowEditNote(false);

                      setSelectedImage(null);
                      transformerRef?.current?.nodes([]);
                      setShowTextBox(true);

                      setSelectedTextId(latestText.id);
                      setSelectedText({
                        ...latestText,
                        selectedContent: null,
                      });

                      setSelectedObject({
                        ...latestText,
                        type: "Text",
                        selectedContent: null,
                      });
                      setSelectedObjects([]);

                      // Create a Konva-like event object for the onClick callback
                      const konvaEvent = {
                        target: {
                          getClassName: () => "Group",
                          attrs: {
                            id: text.id,
                            type: "Text",
                            name: text.name,
                            x: text.x || 0,
                            y: text.y || 0,
                            width: text.width || 0,
                            height: totalContentHeight || 0,
                            fontSize: text.fontSize || 0,
                          },
                        },
                        evt: e.evt || e,
                      };
                      markSelectedNow();
                      onClick(konvaEvent);

                      return; // stopPropagation already called at start
                    }

                    if (delegateMultiSelectMergeText(e)) {
                      return;
                    }

                    // If text is already selected, just ensure showTextBox is true
                    if (isSameElement) {
                      setShowTextBox(true);
                      return;
                    }
                  }}
                >
                  {/* Transparent Rectangle to ensure click events are captured - CRITICAL for click detection */}
                  <Rect
                    x={0}
                    y={0}
                    width={textBlockWidth - 20}
                    height={typeTextHeight + 8}
                    fill="transparent"
                    listening={true}
                    perfectDrawEnabled={false}
                    hitStrokeWidth={20} // Large hit area to ensure clicks are captured
                    onClick={(e) => {
                      // Stop propagation and handle click here
                      e.evt.stopPropagation();
                      e.evt.preventDefault();

                      // Trigger selection
                      const isSameElement =
                        selectedObject && selectedObject.id === text.id;
                      if (!isSameElement && !selectedObject) {
                        const latestText =
                          texts?.find((t) => t.id === text.id) || text;
                        setTexts((prevTexts) =>
                          prevTexts.map((t) => ({
                            ...t,
                            selectedContent: null,
                          })),
                        );
                        setEditTextBoxContent(false);
                        setShowEditTask(false);
                        setShowEditNote(false);
                        setSelectedImage(null);
                        transformerRef?.current?.nodes([]);
                        setShowTextBox(true);
                        setSelectedTextId(latestText.id);
                        setSelectedText({
                          ...latestText,
                          selectedContent: null,
                        });
                        setSelectedObject({
                          ...latestText,
                          type: "Text",
                          selectedContent: null,
                        });
                        setSelectedObjects([]);
                        const konvaEvent = {
                          target: {
                            getClassName: () => "Group",
                            attrs: {
                              id: text.id,
                              type: "Text",
                              name: text.name,
                              x: text.x || 0,
                              y: text.y || 0,
                              width: text.width || 0,
                              height: totalContentHeight || 0,
                              fontSize: text.fontSize || 0,
                            },
                          },
                          evt: e.evt || e,
                        };
                        markSelectedNow();
                        onClick(konvaEvent);
                      } else if (delegateMultiSelectMergeText(e)) {
                        // merged via canvas handler
                      } else if (isSameElement) {
                        setShowTextBox(true);
                      }
                    }}
                  />
                  <Text
                    listening={true} // Enable listening so Text can receive clicks
                    draggable={false}
                    y={0}
                    x={0}
                    text={typeName}
                    fontSize={typeFontSize}
                    fontFamily={typeFontFamily}
                    fontStyle="bold"
                    fill={text.textStyles?.type?.color || "black"}
                    width={textBlockWidth - 20}
                    ellipsis
                    wrap="word"
                    opacity={text.opacity !== undefined ? text.opacity : 1} // Apply element opacity
                    onClick={(e) => {
                      // Stop propagation to prevent PARTS Group from intercepting
                      e.evt.stopPropagation();
                      e.evt.preventDefault();

                      // Disable selection if a tool is locked
                      if (lockedAction) {
                        return;
                      }

                      // If double-click is pending, ignore single click
                      if (doubleClickPendingRef.current) {
                        return;
                      }

                      // Ignore onClick if we just finished dragging or transforming
                      if (isDraggingRef.current || isTransformingRef.current) {
                        setShowTextBox(true);
                        return;
                      }

                      // If text is not selected, trigger selection directly
                      const isSameElement =
                        selectedObject && selectedObject.id === text.id;
                      if (!isSameElement && !selectedObject) {
                        // Get the latest text from texts array
                        const latestText =
                          texts?.find((t) => t.id === text.id) || text;

                        // Clear selectedContent from all texts
                        setTexts((prevTexts) =>
                          prevTexts.map((t) => ({
                            ...t,
                            selectedContent: null,
                          })),
                        );

                        // Clear all editing states
                        setEditTextBoxContent(false);
                        setShowEditTask(false);
                        setShowEditNote(false);

                        setSelectedImage(null);
                        transformerRef?.current?.nodes([]);
                        setShowTextBox(true);

                        setSelectedTextId(latestText.id);
                        setSelectedText({
                          ...latestText,
                          selectedContent: null,
                        });

                        setSelectedObject({
                          ...latestText,
                          type: "Text",
                          selectedContent: null,
                        });
                        setSelectedObjects([]);

                        // Create a Konva-like event object for the onClick callback
                        const konvaEvent = {
                          target: {
                            getClassName: () => "Group",
                            attrs: {
                              id: text.id,
                              type: "Text",
                              name: text.name,
                              x: text.x || 0,
                              y: text.y || 0,
                              width: text.width || 0,
                              height: totalContentHeight || 0,
                              fontSize: text.fontSize || 0,
                            },
                          },
                          evt: e.evt || e,
                        };
                        markSelectedNow();
                        onClick(konvaEvent);
                        return;
                      }

                      if (delegateMultiSelectMergeText(e)) {
                        return;
                      }

                      // If text is already selected, just ensure showTextBox is true
                      if (isSameElement) {
                        setShowTextBox(true);
                        return;
                      }
                    }}
                  />
                </Group>
              ) : null;

              // Reduce spacing more for big font size
              const spacingReduction = text.fontSize > 14 ? 16 : 5;
              currentYOffset += typeTextHeight - spacingReduction;

              // Filter out null entries before sorting
              const sortedParts = typeData.parts
                .filter((part) => part !== null && part !== undefined)
                .slice()
                .sort((a, b) => {
                  if (!a || !b) return 0; // Handle null entries
                  const partA = a.part?.match(/\(([^)]+)\)/)?.[1] || "";
                  const partB = b.part?.match(/\(([^)]+)\)/)?.[1] || "";
                  return partA.localeCompare(partB, undefined, {
                    numeric: true,
                    sensitivity: "base",
                  });
                });

              const partElements = [];

              // Add TYPE heading HTML overlay to separate array (not partElements)
              if (typeHeadingHtmlOverlay) {
                typeHeadingHtmlOverlays.push(typeHeadingHtmlOverlay);
              } else {
              }

              sortedParts.forEach((partData, index) => {
                // Skip null or undefined entries
                if (!partData) return;

                const {
                  id,
                  part,
                  qty,
                  unit,
                  highlighted,
                  itemCodeNote, // System-generated note from Item Code Table
                  note, // User's note
                  noteIsHtml,
                  noteListType,
                } = partData;
                // CRITICAL: Always use text prop directly since textRenderer now passes latestText
                // The textRenderer has already synced the text prop with the latest from texts array
                // So we can trust the text prop here - it should always be up-to-date
                const displayTextForSelection = text;

                const partFontSize =
                  text.fontSize || text.textStyles?.part?.fontSize || 14;
                const partFontFamily = "Roboto";
                const qtySuffix =
                  qty && parseInt(qty) > 0
                    ? ` - Qty-${qty} ${unit || ""}`
                    : ` ${unit || ""}`;
                const sanitizedPartHtml = sanitizeAllowMark(part || "");
                const hasExistingMark = /<mark/i.test(part || "");
                const partHtmlSegment = hasExistingMark
                  ? sanitizedPartHtml
                  : highlighted
                    ? `<mark>${sanitizedPartHtml}</mark>`
                    : sanitizedPartHtml;
                // Add zero-width space before qty to create a break opportunity
                // This allows qty to wrap to next line if part text is too long
                const qtySeparator =
                  qtySuffix && qtySuffix.trim() !== "" ? " \u200B" : ""; // Zero-width space + regular space
                const content = `${part || ""}${qtySeparator}${qtySuffix}`;
                // Wrap qty in span to ensure it can break/wrap properly
                const qtyHtml =
                  qtySuffix && qtySuffix.trim() !== ""
                    ? `<span style="display: inline; white-space: normal; word-break: break-word; overflow-wrap: break-word;">${escapeHtml(
                        qtySuffix,
                      )}</span>`
                    : "";
                const contentHtml = `${partHtmlSegment}${qtySeparator}${qtyHtml}`;
                const normalizedContentHtml = normalizeMarkColor(
                  contentHtml,
                  "yellow",
                );
                const flattenedPartHtml = flattenNestedLists(
                  normalizedContentHtml,
                );

                // Clean part HTML to remove trailing whitespace/newlines that create extra spacing
                let cleanedPartHtml = flattenedPartHtml
                  .replace(/\n/g, "<br>")
                  .replace(/<div>/g, "<br>")
                  .replace(/<\/div>/g, "")
                  .replace(/^<br>/, "")
                  .replace(/(<br>\s*){2,}/g, "<br><br>")
                  .trim();

                // Remove trailing paragraph tags and their content that might add spacing
                cleanedPartHtml = cleanedPartHtml
                  .replace(/<\/p>$/i, "")
                  .replace(/^<p[^>]*>/i, "")
                  .replace(/^<p[^>]*><\/p>/i, "")
                  .trim();

                // Remove any trailing <br> tags that might create visual padding below
                cleanedPartHtml = cleanedPartHtml
                  .replace(/(<br\s*\/?>)+$/i, "")
                  .trim();

                // Remove any trailing whitespace characters (spaces, tabs, non-breaking spaces) that create spacing
                // This is critical because whiteSpace: "pre-wrap" will render these as visual space
                cleanedPartHtml = cleanedPartHtml
                  .replace(/[\s\u00A0\u200B\u2009\u2008]+$/, "") // Remove trailing whitespace including zero-width spaces and thin spaces
                  .trim();

                // CRITICAL: Final pass to ensure absolutely no trailing content that could create spacing
                // Remove any remaining trailing tags or whitespace
                cleanedPartHtml = cleanedPartHtml
                  .replace(/(<br\s*\/?>)+$/i, "") // Remove any remaining trailing <br> tags
                  .replace(/[\s\u00A0\u200B\u2009\u2008]+$/, "") // Remove any remaining trailing whitespace
                  .trim();

                // CRITICAL: Final aggressive cleanup - remove any trailing whitespace that could create spacing
                // This includes all types of whitespace, zero-width spaces, or invisible characters
                // NOTE: We do NOT remove closing HTML tags (like </span>, </mark>) as they are necessary for structure
                // The issue is trailing whitespace AFTER closing tags, not the tags themselves
                cleanedPartHtml = cleanedPartHtml
                  .replace(
                    /[\s\u00A0\u200B\u2009\u2008\u200C\u200D\uFEFF]+$/,
                    "",
                  ) // Remove all types of trailing whitespace (including after closing tags)
                  .replace(/(<br\s*[^>]*>)+$/i, "") // Remove any remaining <br> tags with attributes
                  .trim();

                // CRITICAL: Remove trailing whitespace from nested elements (like spans, marks, etc.)
                // This handles cases where whitespace is inside closing tags: <span>text </span> -> <span>text</span>
                // Do this multiple times to catch nested elements
                for (let i = 0; i < 5; i++) {
                  // Remove whitespace before closing tags (handles nested elements)
                  cleanedPartHtml = cleanedPartHtml.replace(
                    /([^>\s])([\s\u00A0\u200B\u2009\u2008\u200C\u200D\uFEFF\n\r\t]+)(<\/[^>]+>)/g,
                    "$1$3",
                  );
                  // Also handle cases where there might be whitespace between tags
                  cleanedPartHtml = cleanedPartHtml.replace(
                    /(<\/[^>]+>)([\s\u00A0\u200B\u2009\u2008\u200C\u200D\uFEFF\n\r\t]+)(<\/[^>]+>)/g,
                    "$1$3",
                  );
                }

                // CRITICAL: Remove any trailing newlines or spaces that might create an extra line with pre-wrap
                // This must be done after all other cleaning to catch any remaining whitespace
                cleanedPartHtml = cleanedPartHtml
                  .replace(/\s+$/, "") // Remove any trailing whitespace of any kind
                  .replace(/\n+$/, "") // Remove any trailing newlines
                  .replace(/\r+$/, "") // Remove any trailing carriage returns
                  .trim();

                // CRITICAL: Final pass - remove any remaining trailing content that could create spacing
                // This includes any whitespace, newlines, or invisible characters at the very end
                while (
                  cleanedPartHtml.endsWith(" ") ||
                  cleanedPartHtml.endsWith("\n") ||
                  cleanedPartHtml.endsWith("\r") ||
                  cleanedPartHtml.endsWith("\t") ||
                  cleanedPartHtml.match(
                    /[\s\u00A0\u200B\u2009\u2008\u200C\u200D\uFEFF]$/,
                  )
                ) {
                  cleanedPartHtml = cleanedPartHtml
                    .replace(
                      /[\s\u00A0\u200B\u2009\u2008\u200C\u200D\uFEFF\n\r\t]+$/,
                      "",
                    )
                    .trim();
                }

                // Calculate actual available width for parts considering canvas boundary
                // ✅ Use integer width for consistency (must match rendering width exactly)
                const actualPartWidth = Math.floor(textBlockWidth - 10); // Use full width - let container clip-path handle clipping;

                // Use the cleaned HTML version for accurate height measurement that matches rendering
                // This ensures qty wrapping is properly accounted for in height calculation
                const safeContentForMeasurement = cleanedPartHtml;
                // CRITICAL: Measure height, but account for Math.ceil rounding that adds extra space
                // measureHtmlHeight uses Math.ceil which rounds up (e.g., 19.6px -> 20px)
                // For single-line parts, this can add ~0.4px extra. We'll use Math.floor to match actual rendered height
                const measuredHeight = measureHtmlHeight(
                  safeContentForMeasurement, // Use HTML version to match rendered output
                  actualPartWidth,
                  partFontSize,
                  partFontFamily,
                );
                // CRITICAL: For parts, use Math.floor to prevent extra height from Math.ceil rounding
                // This matches the actual rendered height more closely
                const lineHeight1_4 = partFontSize * 1.4;
                const estimatedLines = Math.ceil(
                  measuredHeight / lineHeight1_4,
                );
                // If it's a single line, use the actual line height instead of rounded-up measurement
                const partTextHeight =
                  estimatedLines === 1
                    ? Math.floor(lineHeight1_4) // Single line: use floor of line-height 1.4
                    : measuredHeight; // Multi-line: use measured height as-is

                // DEBUG: Log the calculation to verify it's working

                const partY = currentYOffset;

                // 🔍 DEBUG: Log part HTML and positioning

                // Boundary hiding is handled by transformFunc on the Html component
                // (moves the portal off-screen when attrs.x/y are negative so
                // overflow:hidden on .konvajs-content clips it completely).
                // isPartVisible is kept only to gate highlight rects and notes.
                const isPartVisible = true;

                // Get visible bounds for this part
                const partBounds = getVisibleContentBounds(
                  partY,
                  partTextHeight,
                );

                // ✅ Use id-based selection
                // CRITICAL: Use displayTextForSelection instead of text to get the latest selectedContent from texts array
                // This ensures highlighting updates immediately when selectedContent changes
                const isSelected =
                  id && id === displayTextForSelection?.selectedContent?.id;
                const isHighlighted = highlighted;
                const isBoth = isSelected && isHighlighted;

                // Debug logging to track selection state
                if (isSelected) {
                }

                // isPartVisible gates highlight rects and notes, but the Html is
                // always pushed below (with display:none when out-of-bounds) so the
                // portal stays mounted and CSS hides it synchronously.
                // currentYOffset is incremented once at the bottom of this iteration.

                // NOTE: Keep part highlight on HTML layer (instead of Konva Rect) so it
                // stays visible above top-layer images, same as part notes.

                // CRITICAL: Only cover the part area, not the entire content area
                // This prevents the part overlay from intercepting clicks on content above it
                const partWidth = textBlockWidth - 10;
                const partHighlightWidth =
                  isSelected || isHighlighted
                    ? Math.min(
                        partWidth,
                        measureHtmlContentWidth(
                          cleanedPartHtml,
                          partFontSize,
                          partFontFamily,
                          partWidth,
                        ),
                      )
                    : partWidth;
                partElements.push(
                  <Html
                    key={`part-html-${id}-${index}-${text._lastUpdated}`}
                    x={10}
                    y={partY}
                    divProps={{
                      // Add data attribute for CSS targeting
                      style: {
                        zIndex: isDraggingRef.current || isTextSelected() ? 35 : 10,
                        width: `${partWidth}px`,
                        height: `${partTextHeight}px`,
                        pointerEvents: getHtmlPointerEvents(),
                        position: "absolute",
                        visibility: "visible",
                        opacity: 1,
                        overflow: "visible",
                        clipPath: "none",
                      },
                      onClick: (e) => {
                        // Don't stop propagation - let inner div handle it
                      },
                    }}
                  >
                    <div
                      data-export="true"
                      data-content-type="part"
                      data-groupid={text.id}
                      dangerouslySetInnerHTML={{
                        __html: isHighlighted
                          ? stripNeutralBackgroundsForHighlight(
                              stripInlineColorForPartDescription(
                                cleanedPartHtml,
                              ),
                            )
                          : stripInlineColorForPartDescription(cleanedPartHtml),
                      }}
                      onMouseDown={(e) => {
                        handleHtmlMouseDown(e);
                      }}
                      onMouseEnter={() => {}}
                      onMouseLeave={() => {}}
                      style={getConsistentHtmlStyles({
                        position: "absolute",
                        top: partY,
                        left: 10,
                        width: `${partHighlightWidth}px`, // For highlighted single-line parts, keep background tight to content width
                        fontFamily: partFontFamily,
                        fontSize: `${partFontSize}px`,
                        color: text.textStyles?.part?.color || "#000",
                        backgroundColor:
                          isSelected || isHighlighted
                            ? isHighlighted
                              ? "yellow"
                              : "#EBEBEB"
                            : "transparent",
                        border: isBoth ? "1px solid #888" : "none",
                        borderRadius:
                          isSelected || isHighlighted ? "4px" : undefined,
                        boxShadow: isBoth
                          ? "0px 3px 6px rgba(0, 0, 0, 0.15)"
                          : "none",
                        cursor: getHtmlHoverCursor(),
                        maxWidth: `${textBlockWidth - 10}px`, // Use full max width - let container clip-path handle clipping
                        // CRITICAL FIX: Always use partTextHeight-based max height for parts so they
                        // remain visible even when the text element is touching the canvas border
                        // (red stroke state). Vertical overflow is handled visually by the canvas,
                        // no need to hard-clip against effectiveCanvasHeight here.
                        maxHeight: `${partTextHeight * 1.2}px`,
                        // Let content determine its own height naturally
                        minHeight: "auto",
                        // CRITICAL: Ensure no extra spacing from display properties
                        verticalAlign: "top", // Align to top to prevent baseline spacing
                        display: "block", // Explicit block display
                        // CRITICAL: Prevent any extra spacing from line-height by using exact pixel calculation
                        // This ensures the height matches exactly what was measured
                        // CRITICAL: Clip any overflow to prevent extra spacing from trailing content
                        overflowY: "hidden", // Clip vertical overflow - CRITICAL for preventing extra spacing
                        overflowX: "hidden", // Clip horizontal overflow
                        // CRITICAL: Ensure content doesn't create extra line-height space
                        // Set text-overflow to prevent any visual artifacts
                        textOverflow: "clip", // Clip text overflow instead of ellipsis
                        // CRITICAL: Use box-sizing to ensure height includes padding/border correctly
                        boxSizing: "border-box", // Ensure height calculation is correct
                        // CRITICAL: Prevent any trailing whitespace from creating visual space
                        // The explicit height + overflow hidden should clip it, but ensure it's enforced
                        // Note: We can't use CSS to remove trailing whitespace, so HTML cleaning is critical
                        opacity: text.opacity !== undefined ? text.opacity : 1, // Apply element opacity
                        userSelect: isTextSelectionDisabled() ? "none" : "auto",
                        WebkitUserSelect: isTextSelectionDisabled()
                          ? "none"
                          : "auto",
                        MozUserSelect: isTextSelectionDisabled()
                          ? "none"
                          : "auto",
                        msUserSelect: isTextSelectionDisabled()
                          ? "none"
                          : "auto",
                        // CRITICAL: Disable pointer events when in multi-selection to prevent text selection
                        // When not selected, set to "none" so other elements (like UnifiedLineArrowElement orbs) can interact
                        pointerEvents: getHtmlPointerEvents(),
                        WebkitTouchCallout: isTextSelectionDisabled()
                          ? "none"
                          : "default",
                        WebkitUserDrag: isTextSelectionDisabled()
                          ? "none"
                          : "auto",
                        // Clip overflow letters at canvas boundary (same inset() approach
                        // as text.content/task/note). Applied here on the inner div so the
                        // clip-path coordinate space matches the div's actual position.
                        ...(isOffCanvas
                          ? { clipPath: "none", WebkitClipPath: "none" }
                          : (() => {
                              const cpw =
                                canvasWidth || window.innerWidth * 0.45;
                              // Inner div is at (text.x+10, text.y+partY) in stage space
                              const pl = (text.x || 0) + 10;
                              const pt = (text.y || 0) + partY;
                              const pr = pl + (textBlockWidth - 10);
                              const pb = pt + partTextHeight;
                              const ol = Math.max(0, -pl);
                              const ot = Math.max(0, -pt);
                              const or_ = Math.max(0, pr - cpw);
                              const ob = Math.max(
                                0,
                                pb - effectiveCanvasHeight,
                              );
                              if (ol > 0 || ot > 0 || or_ > 0 || ob > 0) {
                                const cp = `inset(${ot}px ${or_}px ${ob}px ${ol}px)`;
                                return {
                                  clipPath: cp,
                                  WebkitClipPath: cp,
                                };
                              }
                              return {
                                clipPath: "none",
                                WebkitClipPath: "none",
                              };
                            })()),
                      })}
                      onClick={(e) => {
                        // CRITICAL: Verify click is actually within the part bounds
                        // The part overlay should only cover the part area, but add check for safety
                        const rect = e.currentTarget.getBoundingClientRect();
                        const clickX = e.clientX - rect.left;
                        const clickY = e.clientY - rect.top;

                        // If click is outside part bounds, let it pass through to content handler
                        // This prevents parts from intercepting clicks on content above them
                        if (
                          clickX < 0 ||
                          clickX > partWidth ||
                          clickY < 0 ||
                          clickY > partTextHeight
                        ) {
                          // Don't stop propagation - let the click pass through to content handler
                          return;
                        }

                        // If double-click is pending, ignore single click to prevent interference
                        if (doubleClickPendingRef.current) {
                          doubleClickPendingRef.current = false;
                          return;
                        }

                        const currentIsMultiSelectMode =
                          isMultiSelectModeRef?.current ?? isMultiSelectMode;

                        // Check if clicking on the same element that's already selected
                        const isSameElement =
                          selectedObject && selectedObject.id === text.id;

                        // CRITICAL: If clicking a different part in the same element that has selectedContent,
                        // preserve selectedContent to allow double-click to work
                        // This fixes the issue where first double-click doesn't work
                        const isDifferentPartInSameElement =
                          isSameElement &&
                          text?.selectedContent &&
                          id !== text?.selectedContent?.id;

                        if (isDifferentPartInSameElement) {
                          e.stopPropagation(); // CRITICAL: Stop propagation to prevent parent Group from clearing selectedContent
                          setShowTextBox(true);
                          return; // Don't clear selectedContent - let double-click handler manage it
                        }

                        // CRITICAL: Check if clicking on the same PART that's already selected
                        // For single click on same part: clear selectedContent to select text element
                        // For double click: the double-click handler will manage the state
                        const isSamePart =
                          id && id === text?.selectedContent?.id;
                        if (isSamePart) {
                          // Single click on selected part: clear selectedContent to select text element
                          // Double-click will still work because it uses allowDbl() check and happens before this
                          e.stopPropagation(); // Prevent event bubbling to parent Group

                          // Clear selectedContent to select the text element itself
                          setTexts((prevTexts) =>
                            prevTexts.map((t) =>
                              t.id === text.id
                                ? { ...t, selectedContent: null }
                                : t,
                            ),
                          );

                          setSelectedText((prev) => {
                            if (!prev || prev.id !== text.id) return prev;
                            return { ...prev, selectedContent: null };
                          });

                          setSelectedObject((prev) => {
                            if (!prev || prev.id !== text.id) return prev;
                            return { ...prev, selectedContent: null };
                          });

                          setShowTextBox(true);
                          setEditTextBoxContent(false);
                          setShowEditNote(false);
                          setShowEditTask(false);
                          return; // Done - text element is now selected without selectedContent
                        }

                        // If clicking on the same element and already editing, don't reset editing states
                        if (
                          isSameElement &&
                          (editTextBoxContent ||
                            showEditTask ||
                            showEditNote ||
                            text.selectedContent)
                        ) {
                          e.stopPropagation(); // Prevent event bubbling to parent Group
                          setShowTextBox(true);
                          return;
                        }

                        // If there's a selected object and it's not this text element, reset selection
                        // When multi-select is on, let canvas onClick merge — do not clear (same as main HTML content)
                        if (
                          selectedObject &&
                          !e.shiftKey &&
                          !isSameElement &&
                          !currentIsMultiSelectMode
                        ) {
                          setSelectedObject(null);
                          setSelectedObjects([]);
                          setSelectedText(null);
                          setSelectedTextId(null);
                          setShowTextBox(false);
                          return;
                        }

                        // Multi-select: e.g. off-canvas rect selected — clicking a part adds this text
                        if (
                          currentIsMultiSelectMode &&
                          selectedObject &&
                          !isSameElement
                        ) {
                          const latestText =
                            texts?.find((t) => t.id === text.id) || text;
                          setTexts((prevTexts) =>
                            prevTexts.map((t) => ({
                              ...t,
                              selectedContent: null,
                            })),
                          );
                          setEditTextBoxContent(false);
                          setShowEditTask(false);
                          setShowEditNote(false);
                          setSelectedImage(null);
                          transformerRef?.current?.nodes([]);
                          setShowTextBox(true);
                          setSelectedTextId(latestText.id);
                          setSelectedText({
                            ...latestText,
                            selectedContent: null,
                          });
                          const konvaEvent = {
                            target: {
                              getClassName: () => "Group",
                              attrs: {
                                id: text.id,
                                type: "Text",
                                name: text.name,
                                x: text.x || 0,
                                y: text.y || 0,
                                width: text.width || 0,
                                height: totalContentHeight || 0,
                                fontSize: text.fontSize || 0,
                              },
                            },
                            evt: e.nativeEvent || e,
                          };
                          markSelectedNow();
                          onClick(konvaEvent);
                          e.stopPropagation();
                          return;
                        }

                        // If clicking on the same element, ensure showTextBox is true and return
                        // CRITICAL: Stop propagation to prevent parent Group from clearing selectedContent
                        // This allows double-click to work properly when clicking on different parts
                        if (isSameElement) {
                          e.stopPropagation(); // Prevent event bubbling to parent Group
                          setShowTextBox(true);
                          return;
                        }

                        // CRITICAL: If text element is NOT selected, trigger selection directly
                        // Html components don't bubble to Konva Groups, so we need to handle it here
                        if (!selectedObject) {
                          // Get the latest text from texts array
                          const latestText =
                            texts?.find((t) => t.id === text.id) || text;

                          // Clear selectedContent from all texts
                          setTexts((prevTexts) =>
                            prevTexts.map((t) => ({
                              ...t,
                              selectedContent: null,
                            })),
                          );

                          // Clear all editing states
                          setEditTextBoxContent(false);
                          setShowEditTask(false);
                          setShowEditNote(false);

                          setSelectedImage(null);
                          transformerRef?.current?.nodes([]);
                          setShowTextBox(true);

                          setSelectedTextId(latestText.id);
                          setSelectedText({
                            ...latestText,
                            selectedContent: null,
                          });

                          setSelectedObject({
                            ...latestText,
                            type: "Text",
                            selectedContent: null,
                          });
                          setSelectedObjects([]);

                          // Create a Konva-like event object for the onClick callback
                          const konvaEvent = {
                            target: {
                              getClassName: () => "Group",
                              attrs: {
                                id: text.id,
                                type: "Text",
                                name: text.name,
                                x: text.x || 0,
                                y: text.y || 0,
                                width: text.width || 0,
                                height: totalContentHeight || 0,
                                fontSize: text.fontSize || 0,
                              },
                            },
                            evt: e.nativeEvent || e,
                          };
                          markSelectedNow();
                          onClick(konvaEvent);

                          return; // Don't continue with part-specific logic
                        }

                        e.stopPropagation(); // Prevent event bubbling to parent Group
                        // Trigger selection when no object is selected (fallback - shouldn't reach here if !selectedObject)
                        if (!selectedObject) {
                          setSelectedImage(null);
                          transformerRef?.current?.nodes([]);
                          setShowTextBox(true);
                          setSelectedTextId(text.id);
                          setSelectedText((prev) => ({
                            ...prev,
                            ...text,
                            selectedContent: null,
                          }));
                          setSelectedObject((prev) => ({
                            ...prev,
                            ...text,
                            type: "Text",
                            selectedContent: null,
                          }));
                          setEditTextBoxContent(false);
                          setShowEditNote(false);
                          setShowEditTask(false);
                          setSelectedObjects([]);
                          // Create a Konva-like event object for HTML events
                          const konvaEvent = {
                            target: {
                              getClassName: () => "Group",
                              attrs: {
                                id: text.id,
                                type: "Text",
                                name: text.name,
                                x: text.x || 0,
                                y: text.y || 0,
                                width: text.width || 0,
                                height: textGroupHeight || 0,
                                fontSize: text.fontSize || 0,
                              },
                            },
                            evt: e.nativeEvent || e,
                          };
                          markSelectedNow();
                          onClick(konvaEvent);
                        }
                      }}
                      onDoubleClick={(e) => {
                        // CRITICAL: Check allowDbl, passing the clicked part ID and isNoteClick=false
                        // This allows double-click when clicking different parts OR switching from note view to part view
                        const canDbl = allowDbl(id, false); // false = this is a part click (not note)
                        if (!canDbl) {
                          return;
                        }

                        e.stopPropagation();
                        e.preventDefault();

                        // CRITICAL: Set flag SYNCHRONOUSLY at the start to prevent onClick interference
                        doubleClickPendingRef.current = true;

                        // CRITICAL: If text is not selected, select it first before handling part click
                        const isTextSelected = selectedObject?.id === text.id;
                        if (!isTextSelected) {
                          // Get the latest text from texts array
                          const latestText =
                            texts?.find((t) => t.id === text.id) || text;

                          // CRITICAL: Use flushSync to ensure text selection completes before handling part click
                          flushSync(() => {
                            // Clear selectedContent from all texts
                            setTexts((prevTexts) =>
                              prevTexts.map((t) => ({
                                ...t,
                                selectedContent: null,
                              })),
                            );

                            // Clear all editing states
                            setEditTextBoxContent(false);
                            setShowEditTask(false);
                            setShowEditNote(false);

                            setSelectedImage(null);
                            transformerRef?.current?.nodes([]);
                            setShowTextBox(true);

                            setSelectedTextId(latestText.id);
                            setSelectedText({
                              ...latestText,
                              selectedContent: null,
                            });

                            setSelectedObject({
                              ...latestText,
                              type: "Text",
                              selectedContent: null,
                            });
                            setSelectedObjects([]);
                          });
                        }

                        // CRITICAL: Use flushSync to force state updates IMMEDIATELY
                        // handlePartClick will set selectedContent, showEditNote, and editTextBoxContent synchronously
                        // CRITICAL: DON'T call setSelectedText(text) here - it has stale selectedContent!
                        // Let handlePartClick update both texts array AND selectedText with the NEW selectedContent
                        // CRITICAL: Don't set showEditNote or editTextBoxContent here - handlePartClick will set them synchronously
                        flushSync(() => {
                          setShowTextBox(true);
                          setSelectedTextId(text.id);
                          // DON'T set selectedText here - it has stale selectedContent (might be null or old part)
                          // handlePartClick will set selectedText with the NEW selectedContent
                          // DON'T set showEditNote or editTextBoxContent here - handlePartClick will manage them synchronously
                          setShowEditTask(false);
                          // DON'T update texts array here - let handlePartClick do it to avoid preserving old selectedContent
                        });

                        // handlePartClick will update both texts array and selectedText with the new selectedContent
                        // This ensures selectedText always has the latest selectedContent when TextBoxWithButtons renders
                        // CRITICAL: Pass isNote=false to ensure we show part view (not note edit view)
                        // handlePartClick now sets showEditNote=false synchronously in flushSync
                        handlePartClick(
                          { ...typeData.parts[index] },
                          typeName,
                          false, // isNote=false: clicking part, not note
                          text.id,
                        );

                        // Clear flag after a short delay
                        setTimeout(() => {
                          doubleClickPendingRef.current = false;
                        }, 300);
                      }}
                      className="content-text"
                      ref={(el) => {
                        if (el && id) {
                          // 🔍 DEBUG: Log computed styles of part div
                          const computed = window.getComputedStyle(el);
                        }
                      }}
                    />
                  </Html>,
                );

                const partEndY = partY + partTextHeight;
                // CRITICAL: Use partTextHeight directly (matches optimize_chirag branch)
                currentYOffset += partTextHeight;

                // 🔍 DEBUG: Log part end position

                // Helper function to render a note
                const renderNote = (
                  noteContent,
                  noteType,
                  isItemCodeNote = false,
                ) => {
                  if (!noteContent) return;

                  // Store part end position for debug logging
                  const partEndPosition = partEndY;

                  // Clean note content to remove leading/trailing whitespace and block elements
                  let safeNoteContent = noteContent
                    .replace(/\n/g, "<br>")
                    .replace(/<div>/g, "<br>")
                    .replace(/<\/div>/g, "")
                    .replace(/^<br>/, "")
                    .replace(/(<br>\s*){2,}/g, "<br><br>")
                    .trim();

                  // CRITICAL: Check if note is only <br> tags (with or without attributes) - these create unwanted spacing
                  // Remove all <br> tags if they're the only content
                  const onlyBrTagsPattern = /^(<br\s*[^>]*>\s*)+$/i;
                  if (onlyBrTagsPattern.test(safeNoteContent.trim())) {
                    return; // Skip rendering notes that only contain <br> tags
                  }

                  // Remove leading paragraph tags and their content that might add spacing
                  safeNoteContent = safeNoteContent
                    .replace(/^<p[^>]*>/i, "")
                    .replace(/<\/p>$/i, "")
                    .replace(/^<p[^>]*><\/p>/i, "")
                    .trim();

                  // Remove any leading/trailing <br> tags that might create visual padding
                  // Also remove leading whitespace characters (spaces, tabs) that might create spacing above
                  // CRITICAL: Handle <br> tags with attributes (e.g., <br data-text="">)
                  safeNoteContent = safeNoteContent
                    .replace(/^(<br\s*[^>]*>)+/i, "") // Remove leading <br> tags (with any attributes)
                    .replace(/(<br\s*[^>]*>)+$/i, "") // Remove trailing <br> tags (with any attributes)
                    .replace(/^[\s\u00A0\u200B]+/, "") // Remove leading spaces, tabs, non-breaking spaces, zero-width spaces
                    .replace(/[\s\u00A0\u200B]+$/, "") // Remove trailing spaces, tabs, non-breaking spaces, zero-width spaces
                    .trim();

                  // Flatten nested lists after cleaning
                  const flattenedNoteContent =
                    flattenNestedLists(safeNoteContent);

                  // Final cleanup after flattening to ensure no leading/trailing whitespace
                  let finalCleanedNoteContent = flattenedNoteContent
                    .replace(/^[\s\u00A0\u200B]+/, "") // Remove any leading whitespace that might have been introduced
                    .replace(/[\s\u00A0\u200B]+$/, "") // Remove any trailing whitespace that might have been introduced
                    .trim();

                  // CRITICAL: Remove any leading <br> or whitespace that might create spacing above note
                  // This ensures the note starts immediately after the part with no gap
                  // NOTE: We do NOT remove leading closing HTML tags as they are necessary for structure
                  // The issue is leading whitespace or <br> tags, not the tags themselves
                  finalCleanedNoteContent = finalCleanedNoteContent
                    .replace(/^(<br\s*[^>]*>)+/i, "") // Remove leading <br> tags (with any attributes)
                    .replace(
                      /^[\s\u00A0\u200B\u2009\u2008\u200C\u200D\uFEFF]+/,
                      "",
                    ) // Remove any leading whitespace characters including zero-width spaces
                    .trim();

                  // CRITICAL: Final aggressive cleanup for leading content - ensure no spacing above note
                  finalCleanedNoteContent = finalCleanedNoteContent
                    .replace(/^(<br\s*[^>]*>)+/i, "") // Remove any remaining leading <br> tags
                    .replace(
                      /^[\s\u00A0\u200B\u2009\u2008\u200C\u200D\uFEFF]+/,
                      "",
                    ) // Remove any remaining leading whitespace
                    .trim();

                  // CRITICAL: If after all cleaning, the note is empty or only contains <br> tags, skip rendering
                  const isEmptyAfterCleaning =
                    !finalCleanedNoteContent ||
                    finalCleanedNoteContent.trim() === "" ||
                    /^(<br\s*[^>]*>)+$/i.test(finalCleanedNoteContent.trim());
                  if (isEmptyAfterCleaning) {
                    return; // Skip rendering empty notes
                  }

                  const noteFontSize =
                    text.fontSize || getTextStyle(text, "note").fontSize || 14;
                  const noteFontFamily = "Roboto";

                  // Calculate actual available width for part notes considering canvas boundary
                  const actualPartNoteWidth = textBlockWidth - 10; // Use full width - let container clip-path handle clipping;

                  const noteHeight = measureHtmlHeight(
                    finalCleanedNoteContent,
                    actualPartNoteWidth,
                    noteFontSize,
                    noteFontFamily,
                  );

                  // Position note exactly where part ends - no gap
                  // If there's any visual spacing, it's from HTML content, so we position it precisely
                  const noteY = currentYOffset;

                  // 🔍 DEBUG: Log note HTML and positioning

                  // Check if note should be visible
                  const isNoteVisible = hasVisibleContent(noteY, noteHeight);

                  // Get visible bounds for this note
                  const noteBounds = getVisibleContentBounds(noteY, noteHeight);

                  if (isNoteVisible) {
                    // CRITICAL: Only cover the note area, not the entire content area
                    // This prevents the note overlay from intercepting clicks on content above it
                    const noteWidth = textBlockWidth - 10;
                    const noteElement = (
                      <Html
                        key={`html-${noteType}-${id}-${index}-${text._lastUpdated}`}
                        x={10}
                        y={noteY}
                        divProps={{
                          // Add data attribute for CSS targeting
                          style: {
                            zIndex:
                              isDraggingRef.current || isTextSelected()
                                ? 35
                                : 10, // Keep dragged text above top-layer images
                            width: `${noteWidth}px`, // Only cover the note width
                            height: `${noteHeight}px`, // Only cover the note height, not entire content
                            // CRITICAL: Disable pointer events when in multi-selection to prevent text selection
                            // When not selected, set to "none" so other elements (like UnifiedLineArrowElement orbs) can interact
                            pointerEvents: getHtmlPointerEvents(),
                            position: "absolute", // Ensure z-index works
                            visibility: "visible", // Explicitly ensure visibility
                            opacity: 1, // Explicitly ensure opacity
                            // Keep visible so the inner div (positioned at top:noteY
                            // outside the wrapper box) still renders. Clipping of
                            // canvas-boundary overflow is handled on the inner div below.
                            overflow: "visible",
                            clipPath: "none",
                          },
                        }}
                      >
                        <div
                          className="content-text"
                          data-export="true"
                          data-content-type={
                            isItemCodeNote ? "itemcodenote" : "partnote"
                          }
                          data-groupid={text.id}
                          dangerouslySetInnerHTML={{
                            __html: finalCleanedNoteContent,
                          }}
                          ref={(el) => {
                            if (el && id) {
                              // 🔍 DEBUG: Log computed styles of note div
                              const computed = window.getComputedStyle(el);
                            }
                          }}
                          onMouseDown={
                            isSelecting || isItemCodeNote
                              ? undefined
                              : handleHtmlMouseDown // Disable mouse down during selection
                          }
                          style={getConsistentHtmlStyles({
                            position: "absolute",
                            // Use 0,0 relative to wrapper so snapshot (html2canvas) captures part note; wrapper is already at x=10, y=noteY
                            top: noteY,
                            left: 10, // List markers visible with overflowX: visible
                            width: `${textBlockWidth - 10}px`, // Use full width - let container clip-path handle clipping
                            fontFamily: noteFontFamily,
                            fontSize: `${noteFontSize}px`,
                            color: getTextStyle(text, "note").color || "#000",
                            cursor: getHtmlHoverCursor(Boolean(isItemCodeNote)),
                            overflowX: "visible", // Allow list markers to be visible (changed from "hidden")
                            overflowY: "visible", // Allow vertical overflow to show all content
                            maxWidth: `${textBlockWidth - 10}px`, // Use full max width - let container clip-path handle clipping
                            // Remove maxHeight restriction to allow full content display
                            // Height will be determined by content naturally
                            minHeight: "auto",
                            height: "auto", // Let content determine height
                            // Remove any top padding/margin to prevent unwanted spacing
                            marginTop: "0",
                            paddingTop: "0",
                            marginBottom: "0",
                            paddingBottom: "0",
                            // Note: padding-left for lists is handled by listAlignmentStyles CSS
                            // which applies to .content-text ul and .content-text ol
                            marginLeft: "0",
                            userSelect: isItemCodeNote
                              ? "none"
                              : isTextSelectionDisabled()
                                ? "none"
                                : "auto", // Item code note is read-only
                            WebkitUserSelect: isItemCodeNote
                              ? "none"
                              : isTextSelectionDisabled()
                                ? "none"
                                : "auto",
                            MozUserSelect: isItemCodeNote
                              ? "none"
                              : isTextSelectionDisabled()
                                ? "none"
                                : "auto",
                            msUserSelect: isItemCodeNote
                              ? "none"
                              : isTextSelectionDisabled()
                                ? "none"
                                : "auto",
                            pointerEvents: isItemCodeNote
                              ? "none"
                              : isSelecting
                                ? "none" // Disable pointer events during selection to allow selection box drawing
                                : shouldAllowPointerEventsThrough()
                                  ? "none"
                                  : isTextSelected()
                                    ? "auto" // Allow interaction when text is selected
                                    : "none", // Allow selection box drawing when text is not selected
                            WebkitTouchCallout: isItemCodeNote
                              ? "none"
                              : isTextSelectionDisabled()
                                ? "none"
                                : "default",
                            WebkitUserDrag: isItemCodeNote
                              ? "none"
                              : isTextSelectionDisabled()
                                ? "none"
                                : "auto",
                            opacity:
                              text.opacity !== undefined ? text.opacity : 1, // Apply element opacity
                            // Clip overflow letters at canvas boundary.
                            // Applied on the inner div (same pattern as part rows) so
                            // the clip-path coordinate space matches this div's position.
                            // Inner div is at (text.x+10, text.y+noteY) in stage space.
                            ...(isOffCanvas
                              ? {
                                  clipPath: "none",
                                  WebkitClipPath: "none",
                                }
                              : (() => {
                                  const cpw =
                                    canvasWidth || window.innerWidth * 0.45;
                                  const nl = (text.x || 0) + 10;
                                  const nt = (text.y || 0) + noteY;
                                  const nr = nl + (textBlockWidth - 10);
                                  const nb = nt + noteHeight;
                                  const ol = Math.max(0, -nl);
                                  const ot = Math.max(0, -nt);
                                  const or_ = Math.max(0, nr - cpw);
                                  const ob = Math.max(
                                    0,
                                    nb - effectiveCanvasHeight,
                                  );
                                  if (ol > 0 || ot > 0 || or_ > 0 || ob > 0) {
                                    const cp = `inset(${ot}px ${or_}px ${ob}px ${ol}px)`;
                                    return {
                                      clipPath: cp,
                                      WebkitClipPath: cp,
                                    };
                                  }
                                  return {
                                    clipPath: "none",
                                    WebkitClipPath: "none",
                                  };
                                })()),
                          })}
                          onClick={
                            !isItemCodeNote
                              ? (e) => {
                                  // CRITICAL: Verify click is actually within the note bounds
                                  // The note overlay should only cover the note area, but add check for safety
                                  const rect =
                                    e.currentTarget.getBoundingClientRect();
                                  const clickX = e.clientX - rect.left;
                                  const clickY = e.clientY - rect.top;

                                  // If click is outside note bounds, let it pass through to content handler
                                  // This prevents notes from intercepting clicks on content above them
                                  if (
                                    clickX < 0 ||
                                    clickX > noteWidth ||
                                    clickY < 0 ||
                                    clickY > noteHeight
                                  ) {
                                    // Don't stop propagation - let the click pass through to content handler
                                    return;
                                  }

                                  // If double-click is pending, ignore single click to prevent interference
                                  if (doubleClickPendingRef.current) {
                                    doubleClickPendingRef.current = false;
                                    return;
                                  }

                                  if (removeThisTextFromMultiSelectionIfGrouped(e)) {
                                    return;
                                  }

                                  const currentIsMultiSelectMode =
                                    isMultiSelectModeRef?.current ??
                                    isMultiSelectMode;

                                  // Check if clicking on the same element that's already selected
                                  const isSameElement =
                                    selectedObject &&
                                    selectedObject.id === text.id;

                                  // CRITICAL: If text element is NOT selected, trigger selection directly
                                  // Html components don't bubble to Konva Groups, so we need to handle it here
                                  if (!isSameElement && !selectedObject) {
                                    // Get the latest text from texts array
                                    const latestText =
                                      texts?.find((t) => t.id === text.id) ||
                                      text;

                                    // Clear selectedContent from all texts
                                    setTexts((prevTexts) =>
                                      prevTexts.map((t) => ({
                                        ...t,
                                        selectedContent: null,
                                      })),
                                    );

                                    // Clear all editing states
                                    setEditTextBoxContent(false);
                                    setShowEditTask(false);
                                    setShowEditNote(false);

                                    setSelectedImage(null);
                                    transformerRef?.current?.nodes([]);
                                    setShowTextBox(true);

                                    setSelectedTextId(latestText.id);
                                    setSelectedText({
                                      ...latestText,
                                      selectedContent: null,
                                    });

                                    setSelectedObject({
                                      ...latestText,
                                      type: "Text",
                                      selectedContent: null,
                                    });
                                    setSelectedObjects([]);

                                    // Create a Konva-like event object for the onClick callback
                                    const konvaEvent = {
                                      target: {
                                        getClassName: () => "Group",
                                        attrs: {
                                          id: text.id,
                                          type: "Text",
                                          name: text.name,
                                          x: text.x || 0,
                                          y: text.y || 0,
                                          width: text.width || 0,
                                          height: totalContentHeight || 0,
                                          fontSize: text.fontSize || 0,
                                        },
                                      },
                                      evt: e.nativeEvent || e,
                                    };
                                    markSelectedNow();
                                    onClick(konvaEvent);

                                    return; // Don't continue with note-specific logic
                                  }

                                  if (delegateMultiSelectMergeText(e)) {
                                    e.stopPropagation();
                                    return;
                                  }

                                  // CRITICAL: Check if clicking on the same PART's note that's already selected
                                  // This prevents deselection when double-clicking the same part's note
                                  const isSamePart =
                                    id && id === text?.selectedContent?.id;
                                  if (
                                    isSamePart &&
                                    !e.shiftKey &&
                                    !e.ctrlKey &&
                                    !e.metaKey
                                  ) {
                                    // Single click on selected part's note: clear selectedContent to select text element
                                    // Double-click will still work because it uses allowDbl() check and happens before this
                                    e.stopPropagation(); // Prevent event bubbling to parent Group

                                    // Clear selectedContent to select the text element itself
                                    setTexts((prevTexts) =>
                                      prevTexts.map((t) =>
                                        t.id === text.id
                                          ? { ...t, selectedContent: null }
                                          : t,
                                      ),
                                    );

                                    setSelectedText((prev) => {
                                      if (!prev || prev.id !== text.id)
                                        return prev;
                                      return {
                                        ...prev,
                                        selectedContent: null,
                                      };
                                    });

                                    setSelectedObject((prev) => {
                                      if (!prev || prev.id !== text.id)
                                        return prev;
                                      return {
                                        ...prev,
                                        selectedContent: null,
                                      };
                                    });

                                    setShowTextBox(true);
                                    setEditTextBoxContent(false);
                                    setShowEditNote(false);
                                    setShowEditTask(false);
                                    if (!currentIsMultiSelectMode) {
                                      setSelectedObjects([]);
                                    }
                                    // Create a Konva-like event object for HTML events
                                    const konvaEvent = {
                                      target: {
                                        getClassName: () => "Group",
                                        attrs: {
                                          id: text.id,
                                          type: "Text",
                                          name: text.name,
                                          x: text.x || 0,
                                          y: text.y || 0,
                                          width: text.width || 0,
                                          height: textGroupHeight || 0,
                                          fontSize: text.fontSize || 0,
                                        },
                                      },
                                      evt: e.nativeEvent || e,
                                    };
                                    markSelectedNow();
                                    onClick(konvaEvent);
                                  }
                                }
                              : undefined
                          }
                          onDoubleClick={(e) => {
                            // CRITICAL: Both item code note and part note open edit note view on double-click
                            // Check allowDbl, passing the clicked part ID and isNoteClick=true
                            const canDbl = allowDbl(id, true); // true = this is a note click
                            if (!canDbl) {
                              return;
                            }
                            e.stopPropagation();
                            e.preventDefault();

                            // CRITICAL: Set flag SYNCHRONOUSLY at the start to prevent onClick interference
                            doubleClickPendingRef.current = true;

                            // CRITICAL: Use flushSync to force state updates IMMEDIATELY
                            flushSync(() => {
                              setShowTextBox(true);
                              setSelectedTextId(text.id);
                              setShowEditTask(false);
                            });

                            // handlePartClick will update both texts array and selectedText with the new selectedContent
                            // CRITICAL: Pass isNote=true to ensure we show note edit view (not part view)
                            // handlePartClick also clears showQtyUnitOptions when isNote=true
                            handlePartClick(
                              { ...typeData.parts[index] },
                              typeName,
                              true, // isNote=true: clicking note, not part
                              text.id,
                            );

                            // Clear flag after a short delay
                            setTimeout(() => {
                              doubleClickPendingRef.current = false;
                            }, 300);
                          }}
                        />
                      </Html>
                    );

                    partElements.push(noteElement);
                    currentYOffset += noteHeight;

                    // 🔍 DEBUG: Log note final position
                  }
                };

                // Render notes only when the part row itself is visible
                if (isPartVisible) {
                  if (itemCodeNote) {
                    renderNote(itemCodeNote, "itemcodenote", true);
                  }
                  if (note) {
                    renderNote(note, "partnote", false);
                  }
                }
              });

              partGroups.push(
                <React.Fragment key={typeName}>
                  {typeElement}
                  {partElements}
                </React.Fragment>,
              );

              // No spacing between different part types
              // currentYOffset += 0;
            });

          return (
            <>
              <Group
                onClick={(e) => {
                  // Disable selection if a tool is locked
                  if (lockedAction) {
                    return;
                  }

                  const currentIsMultiSelectMode =
                    isMultiSelectModeRef?.current ?? isMultiSelectMode;

                  // Check if click is on a TYPE heading
                  // Get the click position relative to the PARTS Group
                  const stage = e.target.getStage();
                  if (stage) {
                    const pointerPos = stage.getPointerPosition();
                    if (pointerPos) {
                      // Get the PARTS Group's position
                      const partsGroup = e.target.getParent();
                      const partsGroupX = partsGroup ? partsGroup.x() : 0;
                      const partsGroupY = partsGroup ? partsGroup.y() : 0;

                      // Calculate click position relative to PARTS Group
                      const clickX = pointerPos.x - (text.x + partsGroupX);
                      const clickY = pointerPos.y - (text.y + partsGroupY);

                      // Calculate TYPE heading positions (same logic as in render)
                      let currentYOffset = 10; // Initial padding

                      // Add content section height if it exists
                      if (hasUserContent(text)) {
                        const contentFontSize =
                          text.fontSize ||
                          getTextStyle(text, "content").fontSize ||
                          14;
                        const contentFontFamily = "Roboto";
                        const rawContent = text.content || "";
                        const safeContent = rawContent
                          .replace(/\n/g, "<br>")
                          .replace(/<div>/g, "<br>")
                          .replace(/<\/div>/g, "")
                          .replace(/^<br>/, "")
                          .replace(/(<br>\s*){2,}/g, "<br><br>")
                          .trim();
                        const flattenedContent =
                          flattenNestedLists(safeContent);
                        const actualContentWidth = Math.floor(
                          textBlockWidth - 20,
                        ); // Use full width - let container clip-path handle clipping;
                        const renderedContentHeight = measureHtmlHeight(
                          flattenedContent,
                          actualContentWidth,
                          contentFontSize,
                          contentFontFamily,
                        );
                        currentYOffset += renderedContentHeight;
                      }

                      // Check each TYPE heading
                      const typeEntries = Object.entries(
                        text.partsByType || {},
                      ).sort(
                        (a, b) => (a[1].order || 9999) - (b[1].order || 9999),
                      );

                      for (const [typeName, typeData] of typeEntries) {
                        if (!typeData.parts || typeData.parts.length === 0)
                          continue;

                        const typeFontSize =
                          text.fontSize ||
                          text.textStyles?.type?.fontSize ||
                          16;
                        const typeFontFamily = "Roboto";
                        const typeTextHeight = estimateKonvaTextHeight(
                          typeName,
                          typeFontSize,
                          typeFontFamily,
                          textBlockWidth - 20,
                        );

                        const typeY = currentYOffset;
                        const typeHeight = typeTextHeight + 8;
                        const typeLeft = 10;
                        const typeWidth = textBlockWidth - 20;

                        // Check if click is within this TYPE heading area
                        if (
                          clickX >= typeLeft &&
                          clickX <= typeLeft + typeWidth &&
                          clickY >= typeY &&
                          clickY <= typeY + typeHeight
                        ) {
                          // Stop propagation to prevent other handlers
                          e.evt.stopPropagation();

                          // If text is not selected, trigger selection directly
                          const isSameElement =
                            selectedObject && selectedObject.id === text.id;
                          if (!isSameElement && !selectedObject) {
                            // Get the latest text from texts array
                            const latestText =
                              texts?.find((t) => t.id === text.id) || text;

                            // Clear selectedContent from all texts
                            setTexts((prevTexts) =>
                              prevTexts.map((t) => ({
                                ...t,
                                selectedContent: null,
                              })),
                            );

                            // Clear all editing states
                            setEditTextBoxContent(false);
                            setShowEditTask(false);
                            setShowEditNote(false);

                            setSelectedImage(null);
                            transformerRef?.current?.nodes([]);
                            setShowTextBox(true);

                            setSelectedTextId(latestText.id);
                            setSelectedText({
                              ...latestText,
                              selectedContent: null,
                            });

                            setSelectedObject({
                              ...latestText,
                              type: "Text",
                              selectedContent: null,
                            });
                            setSelectedObjects([]);

                            // Create a Konva-like event object for the onClick callback
                            const konvaEvent = {
                              target: {
                                getClassName: () => "Group",
                                attrs: {
                                  id: text.id,
                                  type: "Text",
                                  name: text.name,
                                  x: text.x || 0,
                                  y: text.y || 0,
                                  width: text.width || 0,
                                  height: totalContentHeight || 0,
                                  fontSize: text.fontSize || 0,
                                },
                              },
                              evt: e.evt || e,
                            };
                            markSelectedNow();
                            onClick(konvaEvent);
                            return; // Exit early
                          }

                          if (delegateMultiSelectMergeText(e)) {
                            return; // Exit early
                          }

                          // If text is already selected, just ensure showTextBox is true
                          if (isSameElement) {
                            setShowTextBox(true);
                            return; // Exit early
                          }

                          // For other cases, break and continue with normal handling
                          break;
                        }

                        // Update offset for next TYPE heading
                        const spacingReduction = text.fontSize > 14 ? 16 : 5;
                        currentYOffset += typeTextHeight - spacingReduction;
                      }
                    }
                  }

                  // CRITICAL: Check if double-click is pending BEFORE clearing selectedContent
                  // This prevents onClick from interfering with double-click handlers on child elements (parts)
                  if (doubleClickPendingRef.current) {
                    return; // Let the double-click handler manage state
                  }

                  // Check if clicking on the same element that's already selected
                  const isSameElement =
                    selectedObject && selectedObject.id === text.id;

                  // CRITICAL: If clicking on the same element with selectedContent, preserve it
                  // This is a safety net in case child part onClick didn't catch it
                  // This ensures double-click can always work on the first try
                  if (isSameElement && text?.selectedContent) {
                    e.evt.stopPropagation(); // Stop propagation to prevent any parent handlers
                    setShowTextBox(true);
                    return; // Don't clear selectedContent - preserve it for double-click
                  }

                  // If clicking on the same element, ensure showTextBox is true and return
                  // CRITICAL: If there's selectedContent, preserve it for potential double-click
                  // Don't clear it on single click - let double-click handler manage the transition
                  if (isSameElement) {
                    e.evt.stopPropagation(); // Stop propagation to prevent any parent handlers
                    setShowTextBox(true);
                    return; // Don't clear selectedContent - preserve it for double-click
                  }

                  // CRITICAL: Two-click selection behavior when multi-select is NOT enabled
                  // First click: Deselect the currently selected element and return early
                  // Second click: Select the new element
                  // When multi-select mode is enabled, don't clear selection on first click
                  // Let the canvas click handler manage adding to selection instead
                  if (
                    selectedObject &&
                    !e.evt.shiftKey &&
                    !isSameElement &&
                    !currentIsMultiSelectMode // Don't clear if multi-select mode is enabled
                  ) {
                    setSelectedObject(null);
                    setSelectedObjects([]);
                    setSelectedText(null);
                    setSelectedTextId(null);
                    setShowTextBox(false);
                    // CRITICAL: Stop event propagation to prevent canvas click handler from selecting the new element
                    e.evt?.stopPropagation?.();
                    e.evt?.preventDefault?.();
                    return;
                  }

                  e.evt.stopPropagation(); // Prevent event bubbling to parent Group

                  // ALWAYS clear selectedContent from ALL texts when selecting a text element
                  setTexts((prevTexts) =>
                    prevTexts.map((t) => ({
                      ...t,
                      selectedContent: null,
                    })),
                  );

                  // Clear all editing states
                  setEditTextBoxContent(false);
                  setShowEditTask(false);
                  setShowEditNote(false);

                  // Trigger selection
                  setSelectedImage(null);
                  transformerRef?.current?.nodes([]);
                  setShowTextBox(true);
                  // Get the latest text from texts array to ensure we have up-to-date data
                  const latestText =
                    texts?.find((t) => t.id === text.id) || text;

                  setSelectedTextId(latestText.id);
                  setSelectedText({
                    ...latestText,
                    selectedContent: null,
                  });
                  if (!currentIsMultiSelectMode) {
                    setSelectedObject({
                      ...latestText,
                      type: "Text",
                      selectedContent: null,
                    });
                    setSelectedObjects([]);
                  }
                  // Create a Konva-like event object for HTML events
                  const konvaEvent = {
                    target: {
                      getClassName: () => "Group",
                      attrs: {
                        id: text.id,
                        type: "Text",
                        name: text.name,
                        x: text.x || 0,
                        y: text.y || 0,
                        width: text.width || 0,
                        height: textGroupHeight || 0,
                        fontSize: text.fontSize || 0,
                      },
                    },
                    evt: e.evt || e,
                  };
                  markSelectedNow();
                  onClick(konvaEvent);
                }}
              >
                {partGroups}
              </Group>
              {/* Render TYPE heading HTML overlays as siblings to PARTS Group */}
              {typeHeadingHtmlOverlays}
            </>
          );
        })()}

      {/* Task Section */}
      {hasTaskContent(text) &&
        (() => {
          const taskFontSize =
            text.fontSize || getTextStyle(text, "task").fontSize || 14;
          const taskFontFamily =
            getTextStyle(text, "task").fontFamily || "Roboto";

          const yTaskLabel = currentYOffset;
          currentYOffset += 15; // Reduced space for "Task" label

          const yTaskContent = currentYOffset;

          const safeTaskContent = text.task
            .replace(/\n/g, "<br>")
            .replace(/<div>/g, "<br>")
            .replace(/<\/div>/g, "")
            .replace(/^<br>/, "")
            .replace(/(<br>\s*){2,}/g, "<br><br>")
            .trim();
          const flattenedTaskContent = flattenNestedLists(safeTaskContent);

          // Calculate actual available width for task considering canvas boundary
          const actualTaskWidth = textBlockWidth - 20; // Use full width - let container clip-path handle clipping;

          const renderedTaskHeight = measureHtmlHeight(
            flattenedTaskContent,
            actualTaskWidth,
            taskFontSize,
            taskFontFamily,
          );

          // Check if task heading should be visible (using strict note visibility)
          const isTaskHeadingVisible = hasVisibleContentStrictForNotes(
            yTaskLabel,
            25,
          );

          // Check if task content should be visible (using strict note visibility)
          const isTaskContentVisible = hasVisibleContentStrictForNotes(
            yTaskContent,
            renderedTaskHeight,
          );

          // Get visible bounds for task content
          const taskContentBounds = getVisibleContentBounds(
            yTaskContent,
            renderedTaskHeight,
          );

          currentYOffset += renderedTaskHeight; // Update offset after task

          // Don't render if neither heading nor content is visible
          if (!isTaskHeadingVisible && !isTaskContentVisible) {
            return null;
          }

          return (
            <Group
              onClick={(e) => {
                const currentIsMultiSelectMode =
                  isMultiSelectModeRef?.current ?? isMultiSelectMode;
                // Disable selection if a tool is locked
                if (lockedAction) {
                  return;
                }

                // CRITICAL: Check if double-click is pending BEFORE clearing selectedContent
                // This prevents onClick from interfering with double-click handlers on child elements (tasks)
                if (doubleClickPendingRef.current) {
                  return; // Let the double-click handler manage state
                }

                // Check if clicking on the same element that's already selected
                const isSameElement =
                  selectedObject && selectedObject.id === text.id;

                // If clicking on the same element, ensure showTextBox is true and return
                // CRITICAL: If there's selectedContent, preserve it for potential double-click
                // Don't clear it on single click - let double-click handler manage the transition
                if (isSameElement) {
                  e.evt.stopPropagation(); // Stop propagation to prevent any parent handlers
                  setShowTextBox(true);
                  return; // Don't clear selectedContent - preserve it for double-click
                }

                // CRITICAL: Two-click selection behavior when multi-select is NOT enabled
                // First click: Deselect the currently selected element and return early
                // Second click: Select the new element
                // When multi-select mode is enabled, don't clear selection on first click
                // Let the canvas click handler manage adding to selection instead
                if (
                  selectedObject &&
                  !e.evt.shiftKey &&
                  !isSameElement &&
                  !currentIsMultiSelectMode // Don't clear if multi-select mode is enabled
                ) {
                  // Clear the other selection and return early - don't select this text element yet
                  // Second click will select it
                  setSelectedObject(null);
                  setSelectedObjects([]);
                  setSelectedText(null);
                  setSelectedTextId(null);
                  setShowTextBox(false);
                  // CRITICAL: Stop event propagation to prevent canvas click handler from selecting the new element
                  e.evt?.stopPropagation?.();
                  e.evt?.preventDefault?.();
                  return;
                }

                if (!currentIsMultiSelectMode) {
                  e.evt.stopPropagation(); // Prevent event bubbling to parent Group
                }

                // ALWAYS clear selectedContent from ALL texts when selecting a text element
                setTexts((prevTexts) =>
                  prevTexts.map((t) => ({
                    ...t,
                    selectedContent: null,
                  })),
                );

                // Clear all editing states
                setEditTextBoxContent(false);
                setShowEditTask(false);
                setShowEditNote(false);

                // Trigger selection
                setSelectedImage(null);
                transformerRef?.current?.nodes([]);
                setShowTextBox(true);
                // Get the latest text from texts array to ensure we have up-to-date data
                const latestText = texts?.find((t) => t.id === text.id) || text;

                setSelectedTextId(latestText.id);
                setSelectedText({
                  ...latestText,
                  selectedContent: null,
                });
                if (!currentIsMultiSelectMode) {
                  setSelectedObject({
                    ...latestText,
                    type: "Text",
                    selectedContent: null,
                  });
                  setSelectedObjects([]);
                }
                // Create a Konva-like event object for HTML events
                const konvaEvent = {
                  target: {
                    getClassName: () => "Group",
                    attrs: {
                      id: text.id,
                      type: "Text",
                      name: text.name,
                      x: text.x || 0,
                      y: text.y || 0,
                      width: text.width || 0,
                      height: textGroupHeight || 0,
                      fontSize: text.fontSize || 0,
                    },
                  },
                  evt: currentIsMultiSelectMode
                    ? {
                        ...(e.evt || e),
                        shiftKey: true,
                      }
                    : e.evt || e,
                };
                markSelectedNow();
                onClick(konvaEvent);
              }}
              onDblClick={(e) => {
                if (!allowDbl()) return;

                e.evt.stopPropagation();
                e.evt.preventDefault();

                // CRITICAL: Set flag SYNCHRONOUSLY at the start to prevent onClick interference
                doubleClickPendingRef.current = true;

                // CRITICAL: Use flushSync to force showEditTask to update IMMEDIATELY before other state updates
                // This ensures TextBoxWithButtons sees showEditTask=true on the next render
                flushSync(() => {
                  setShowEditTask(true);
                  setShowEditNote(false);
                  setEditTextBoxContent(false);
                  setShowTextBox(true);
                  setSelectedTextId(text.id);
                });

                // Now update selectedText with cleared selectedContent
                setSelectedText((prev) => {
                  if (!prev || prev.id !== text.id) return prev;
                  return { ...prev, selectedContent: null };
                });

                // Clear selectedContent in texts array
                setTexts((prevTexts) =>
                  prevTexts.map((t) =>
                    t.id === text.id ? { ...t, selectedContent: null } : t,
                  ),
                );

                setSelectedObject({
                  ...text,
                  type: "Text",
                  selectedContent: null,
                });

                // Clear flag after a short delay
                setTimeout(() => {
                  doubleClickPendingRef.current = false;
                }, 300);
              }}
            >
              <Text
                listening={true}
                draggable={false}
                y={yTaskLabel}
                x={10}
                width={textBlockWidth - 20}
                text="Task"
                fontSize={text.fontSize || 18}
                fontFamily="Roboto"
                fontStyle="bold"
                fill="black"
                visible={isTaskHeadingVisible}
                opacity={text.opacity !== undefined ? text.opacity : 1} // Apply element opacity
              />

              <Html
                divProps={{
                  "data-text-id": text.id,
                  "data-offset-x": 0,
                  "data-offset-y": 0,
                  style: {
                    zIndex:
                      isDraggingRef.current || isTextSelected() ? 35 : 10, // Keep dragged text above top-layer images
                    width: `${textBlockWidth}px`,
                    height: `${totalContentHeight}px`,
                    pointerEvents: "none", // Let clicks pass through to inner divs
                    position: "absolute", // Ensure z-index works
                    visibility: "visible", // Explicitly ensure visibility
                    opacity: 1, // Explicitly ensure opacity
                    // CRITICAL: Clip content that extends outside canvas boundaries (only for main canvas)
                    ...(isOffCanvas
                      ? {}
                      : (() => {
                          // Use actual pixel width (same as RectangleElement and CircleElement)
                          const canvasPixelWidth =
                            canvasWidth || window.innerWidth * 0.45;

                          // Calculate visible bounds within canvas
                          const textLeft = text.x || 0;
                          const textTop = text.y || 0;
                          const textRight = textLeft + textBlockWidth;
                          const textBottom = textTop + totalContentHeight;

                          // Only clip if content is ACTUALLY outside the canvas boundaries
                          // Calculate actual overflow amounts (how much extends outside)
                          const actualOverflowLeft = Math.max(0, -textLeft);
                          const actualOverflowTop = Math.max(0, -textTop);
                          const actualOverflowRight = Math.max(
                            0,
                            textRight - canvasPixelWidth,
                          );
                          const actualOverflowBottom = Math.max(
                            0,
                            textBottom - effectiveCanvasHeight,
                          );

                          // Only apply clipping if there's actual overflow (content extends outside)
                          if (
                            actualOverflowLeft > 0 ||
                            actualOverflowTop > 0 ||
                            actualOverflowRight > 0 ||
                            actualOverflowBottom > 0
                          ) {
                            // Keep full width/height - only use clip-path to hide overflow
                            // This allows content to remain visible until it actually extends outside
                            // clip-path inset() takes: top, right, bottom, left (distances from edges)
                            return {
                              overflow: "hidden",
                              clipPath: `inset(${actualOverflowTop}px ${actualOverflowRight}px ${actualOverflowBottom}px ${actualOverflowLeft}px)`,
                              // Keep original width and height - clip-path handles the clipping
                              width: `${textBlockWidth}px`,
                              height: `${totalContentHeight}px`,
                            };
                          }
                          // CRITICAL: Explicitly remove clipping when element is fully inside canvas
                          // This ensures clip-path is removed when element comes back into canvas
                          return {
                            overflow: "visible",
                            clipPath: "none",
                            width: `${textBlockWidth}px`,
                            height: `${totalContentHeight}px`,
                          };
                        })()),
                  },
                }}
                onDblClick={(e) => {
                  if (!allowDbl()) return;
                  e.evt.stopPropagation(); // Prevent event bubbling to parent Group
                  e.evt.preventDefault(); // Prevent default double-click behavior

                  // Set flag to prevent onClick from interfering
                  doubleClickPendingRef.current = true;

                  // CRITICAL: Use flushSync to force showEditTask to update IMMEDIATELY before other state updates
                  // This ensures TextBoxWithButtons sees showEditTask=true on the next render
                  flushSync(() => {
                    setShowEditTask(true);
                    setShowEditNote(false);
                    setEditTextBoxContent(false);
                  });

                  // Now update selectedText with cleared selectedContent
                  setSelectedText((prev) => {
                    if (!prev || prev.id !== text.id) return prev;
                    return { ...prev, selectedContent: null };
                  });

                  // Clear selectedContent in texts array
                  setTexts((prevTexts) =>
                    prevTexts.map((t) =>
                      t.id === text.id ? { ...t, selectedContent: null } : t,
                    ),
                  );

                  setShowTextBox(true);
                  setSelectedTextId(text.id);
                  setSelectedObject({
                    ...text,
                    type: "Text",
                    selectedContent: null,
                  });

                  // Clear flag after a short delay
                  setTimeout(() => {
                    doubleClickPendingRef.current = false;
                  }, 300);
                }}
              >
                <div
                  id={`task-div-${text.id}`}
                  data-export="true"
                  data-content-type="task"
                  data-groupid={text.id}
                  dangerouslySetInnerHTML={{
                    __html: flattenedTaskContent || "",
                  }}
                  onMouseDown={handleHtmlMouseDown}
                  style={getConsistentHtmlStyles({
                    position: "absolute",
                    top: yTaskContent,
                    left: 10,
                    width: `${textBlockWidth - 20}px`, // Use full width - let container clip-path handle clipping
                    fontFamily: taskFontFamily,
                    fontSize: `${taskFontSize}px`,
                    cursor: getHtmlHoverCursor(),
                    marginLeft: "0px",
                    color: getTextStyle(text, "task").color || "#000",
                    overflow: "hidden",
                    maxWidth: `${textBlockWidth - 20}px`, // Use full max width - let container clip-path handle clipping
                    overflowX: "hidden",
                    overflowY: "hidden",
                    maxHeight: `${Math.max(
                      0,
                      Math.min(
                        renderedTaskHeight,
                        effectiveCanvasHeight - (text.y + yTaskContent),
                      ),
                    )}px`,
                    // Let content determine its own height naturally
                    minHeight: "auto",
                    userSelect: isTextSelectionDisabled() ? "none" : "auto",
                    WebkitUserSelect: isTextSelectionDisabled()
                      ? "none"
                      : "auto",
                    MozUserSelect: isTextSelectionDisabled() ? "none" : "auto",
                    msUserSelect: isTextSelectionDisabled() ? "none" : "auto",
                    // CRITICAL: Disable pointer events when in multi-selection to prevent text selection
                    // When not selected, set to "none" so other elements (like UnifiedLineArrowElement orbs) can interact
                    pointerEvents: getHtmlPointerEvents(),
                    WebkitTouchCallout: isTextSelectionDisabled()
                      ? "none"
                      : "default",
                    WebkitUserDrag: isTextSelectionDisabled() ? "none" : "auto",
                    opacity: text.opacity !== undefined ? text.opacity : 1, // Apply element opacity
                    clipPath: getInnerDivClipPath(
                      yTaskContent,
                      10,
                      textBlockWidth - 20,
                      renderedTaskHeight,
                    ),
                    WebkitClipPath: getInnerDivClipPath(
                      yTaskContent,
                      10,
                      textBlockWidth - 20,
                      renderedTaskHeight,
                    ),
                  })}
                  className="content-text"
                  onClick={(e) => {
                    const currentIsMultiSelectMode =
                      isMultiSelectModeRef?.current ?? isMultiSelectMode;
                    // Disable selection if a tool is locked
                    if (lockedAction) {
                      return;
                    }

                    // If double-click is pending, ignore single click to prevent interference
                    if (doubleClickPendingRef.current) {
                      doubleClickPendingRef.current = false;
                      return;
                    }

                    // Check if clicking on the same element that's already selected
                    const isSameElement =
                      selectedObject && selectedObject.id === text.id;

                    // If clicking on the same element and already editing, don't reset editing states
                    if (
                      isSameElement &&
                      (editTextBoxContent ||
                        showEditTask ||
                        showEditNote ||
                        text.selectedContent)
                    ) {
                      e.stopPropagation(); // Prevent event bubbling to parent Group
                      setShowTextBox(true);
                      return;
                    }

                    // If clicking on the same element, ensure showTextBox is true and return
                    // CRITICAL: Stop propagation to prevent parent Group from clearing selectedContent
                    // This allows double-click to work properly when clicking on task while part is selected
                    if (isSameElement) {
                      e.stopPropagation(); // Prevent event bubbling to parent Group
                      setShowTextBox(true);
                      return;
                    }

                    // CRITICAL: Two-click selection behavior when multi-select is NOT enabled
                    // First click: Deselect the currently selected element and return early
                    // Second click: Select the new element
                    // When multi-select mode is enabled, don't clear selection on first click
                    // Let the canvas click handler manage adding to selection instead
                    if (
                      selectedObject &&
                      !e.shiftKey &&
                      !isSameElement &&
                      !currentIsMultiSelectMode // Don't clear if multi-select mode is enabled
                    ) {
                      // Clear the other selection and return early - don't select this text element yet
                      // Second click will select it
                      setSelectedObject(null);
                      setSelectedObjects([]);
                      setSelectedText(null);
                      setSelectedTextId(null);
                      setShowTextBox(false);
                      // CRITICAL: Stop event propagation to prevent canvas click handler from selecting the new element
                      e.stopPropagation?.();
                      e.preventDefault?.();
                      return;
                    }

                    if (!currentIsMultiSelectMode) {
                      e.stopPropagation(); // Prevent event bubbling to parent Group
                    }

                    // ALWAYS clear selectedContent from ALL texts when selecting a text element
                    setTexts((prevTexts) =>
                      prevTexts.map((t) => ({
                        ...t,
                        selectedContent: null,
                      })),
                    );

                    // Clear all editing states
                    setEditTextBoxContent(false);
                    setShowEditTask(false);
                    setShowEditNote(false);

                    // Trigger selection
                    setSelectedImage(null);
                    transformerRef?.current?.nodes([]);
                    setShowTextBox(true);
                    setSelectedTextId(text.id);
                    setSelectedText({
                      ...text,
                      selectedContent: null,
                    });
                    setSelectedObject({
                      ...text,
                      type: "Text",
                      selectedContent: null,
                    });
                    if (!currentIsMultiSelectMode) {
                      setSelectedObjects([]);
                    }
                    // Create a Konva-like event object for HTML events
                    const konvaEvent = {
                      target: {
                        getClassName: () => "Group",
                        attrs: {
                          id: text.id,
                          type: "Text",
                          name: text.name,
                          x: text.x || 0,
                          y: text.y || 0,
                          width: text.width || 0,
                          height: textGroupHeight || 0,
                          fontSize: text.fontSize || 0,
                        },
                      },
                      evt: currentIsMultiSelectMode
                        ? {
                            ...(e.nativeEvent || e),
                            shiftKey: true,
                          }
                        : e.nativeEvent || e,
                    };
                    markSelectedNow();
                    onClick(konvaEvent);
                  }}
                  onDoubleClick={(e) => {
                    if (!allowDbl()) return;
                    e.stopPropagation(); // Prevent event bubbling to parent Group
                    e.preventDefault(); // Prevent default double-click behavior

                    // Set flag to prevent onClick from interfering
                    doubleClickPendingRef.current = true;

                    // CRITICAL: Use flushSync to force showEditTask to update IMMEDIATELY before other state updates
                    // This ensures TextBoxWithButtons sees showEditTask=true on the next render
                    flushSync(() => {
                      setShowEditTask(true);
                      setShowEditNote(false);
                      setEditTextBoxContent(false);
                    });

                    // Now update selectedText with cleared selectedContent
                    setSelectedText((prev) => {
                      if (!prev || prev.id !== text.id) return prev;
                      return { ...prev, selectedContent: null };
                    });

                    // Clear selectedContent in texts array
                    setTexts((prevTexts) =>
                      prevTexts.map((t) =>
                        t.id === text.id ? { ...t, selectedContent: null } : t,
                      ),
                    );

                    setShowTextBox(true);
                    setSelectedTextId(text.id);
                    setSelectedObject({
                      ...text,
                      type: "Text",
                      selectedContent: null,
                    });

                    // Clear flag after a short delay
                    setTimeout(() => {
                      doubleClickPendingRef.current = false;
                    }, 300);
                  }}
                />
              </Html>
            </Group>
          );
        })()}

      {/* Note Section */}
      {hasNoteContent(text) &&
        (() => {
          const noteFontSize =
            text.fontSize || getTextStyle(text, "note").fontSize || 14;
          const noteFontFamily =
            getTextStyle(text, "note").fontFamily || "Roboto";

          const yNoteLabel = currentYOffset;
          currentYOffset += 15; // Reduced space for "Note" label

          const yNoteContent = currentYOffset;

          const safeNoteContent = text.note
            .replace(/\n/g, "<br>")
            .replace(/<div>/g, "<br>")
            .replace(/<\/div>/g, "")
            .replace(/^<br>/, "")
            .replace(/(<br>\s*){2,}/g, "<br><br>")
            .trim();
          const flattenedNoteContent = flattenNestedLists(safeNoteContent);

          // Calculate actual available width for note considering canvas boundary
          const actualNoteWidth = textBlockWidth - 20; // Use full width - let container clip-path handle clipping;

          const renderedNoteHeight = measureHtmlHeight(
            flattenedNoteContent,
            actualNoteWidth,
            noteFontSize,
            noteFontFamily,
          );

          // Check if note heading should be visible (using strict note visibility)
          const isNoteHeadingVisible = hasVisibleContentStrictForNotes(
            yNoteLabel,
            25,
          );

          // Check if note content should be visible (using strict note visibility)
          const isNoteContentVisible = hasVisibleContentStrictForNotes(
            yNoteContent,
            renderedNoteHeight,
          );

          // Get visible bounds for note content
          const noteContentBounds = getVisibleContentBounds(
            yNoteContent,
            renderedNoteHeight,
          );

          currentYOffset += renderedNoteHeight; // Update offset after note

          // Don't render if neither heading nor content is visible
          if (!isNoteHeadingVisible && !isNoteContentVisible) {
            return null;
          }

          return (
            <Group
              onClick={(e) => {
                // Disable selection if a tool is locked
                if (lockedAction) {
                  return;
                }

                const currentIsMultiSelectMode =
                  isMultiSelectModeRef?.current ?? isMultiSelectMode;

                // Check if clicking on the same element that's already selected
                const isSameElement =
                  selectedObject && selectedObject.id === text.id;

                // If clicking on the same element, ensure showTextBox is true and return
                if (isSameElement) {
                  setShowTextBox(true);
                  return;
                }

                // CRITICAL: Two-click selection behavior when multi-select is NOT enabled
                // First click: Deselect the currently selected element and return early
                // Second click: Select the new element
                // When multi-select mode is enabled, don't clear selection on first click
                // Let the canvas click handler manage adding to selection instead
                if (
                  selectedObject &&
                  !e.evt.shiftKey &&
                  !isSameElement &&
                  !currentIsMultiSelectMode // Don't clear if multi-select mode is enabled
                ) {
                  // Clear the other selection and return early - don't select this text element yet
                  // Second click will select it
                  setSelectedObject(null);
                  setSelectedObjects([]);
                  setSelectedText(null);
                  setSelectedTextId(null);
                  setShowTextBox(false);
                  // CRITICAL: Stop event propagation to prevent canvas click handler from selecting the new element
                  e.evt?.stopPropagation?.();
                  e.evt?.preventDefault?.();
                  return;
                }

                e.evt.stopPropagation(); // Prevent event bubbling to parent Group

                // ALWAYS clear selectedContent from ALL texts when selecting a text element
                setTexts((prevTexts) =>
                  prevTexts.map((t) => ({
                    ...t,
                    selectedContent: null,
                  })),
                );

                // Clear all editing states
                setEditTextBoxContent(false);
                setShowEditTask(false);
                setShowEditNote(false);

                // Trigger selection
                setSelectedImage(null);
                transformerRef?.current?.nodes([]);
                setShowTextBox(true);
                // Get the latest text from texts array to ensure we have up-to-date data
                const latestText = texts?.find((t) => t.id === text.id) || text;

                setSelectedTextId(latestText.id);
                setSelectedText({
                  ...latestText,
                  selectedContent: null,
                });
                if (!currentIsMultiSelectMode) {
                  setSelectedObject({
                    ...latestText,
                    type: "Text",
                    selectedContent: null,
                  });
                  setSelectedObjects([]);
                }
                // Create a Konva-like event object for HTML events
                const konvaEvent = {
                  target: {
                    getClassName: () => "Group",
                    attrs: {
                      id: text.id,
                      type: "Text",
                      name: text.name,
                      x: text.x || 0,
                      y: text.y || 0,
                      width: text.width || 0,
                      height: textGroupHeight || 0,
                      fontSize: text.fontSize || 0,
                    },
                  },
                  evt: e.evt || e,
                };
                markSelectedNow();
                onClick(konvaEvent);
              }}
              onDblClick={(e) => {
                if (!allowDbl()) return;
                e.evt.stopPropagation(); // Prevent event bubbling to parent Group
                e.evt.preventDefault(); // Prevent default double-click behavior

                // Set flag to prevent onClick from interfering
                doubleClickPendingRef.current = true;

                // CRITICAL: Use flushSync to force showEditNote to update IMMEDIATELY before other state updates
                // This ensures TextBoxWithButtons sees showEditNote=true on the next render
                flushSync(() => {
                  setShowEditNote(true);
                  setShowEditTask(false);
                  setEditTextBoxContent(false);
                });

                // Now update selectedText with cleared selectedContent
                setSelectedText((prev) => {
                  if (!prev || prev.id !== text.id) return prev;
                  return { ...prev, selectedContent: null };
                });

                // Clear selectedContent in texts array
                setTexts((prevTexts) =>
                  prevTexts.map((t) =>
                    t.id === text.id ? { ...t, selectedContent: null } : t,
                  ),
                );

                setShowTextBox(true);
                setSelectedTextId(text.id);
                setSelectedObject({
                  ...text,
                  type: "Text",
                  selectedContent: null,
                });

                // Clear flag after a short delay
                setTimeout(() => {
                  doubleClickPendingRef.current = false;
                }, 300);
              }}
            >
              <Text
                listening={true}
                draggable={false}
                y={yNoteLabel}
                x={10}
                width={textBlockWidth - 20}
                text="Note"
                fontSize={text.fontSize || 18}
                fontFamily="Roboto"
                fontStyle="bold"
                fill="black"
                visible={isNoteHeadingVisible}
                opacity={text.opacity !== undefined ? text.opacity : 1} // Apply element opacity
              />

              <Html
                divProps={{
                  "data-text-id": text.id,
                  "data-offset-x": 0,
                  "data-offset-y": 0,
                  style: {
                    zIndex:
                      isDraggingRef.current || isTextSelected() ? 35 : 10, // Keep dragged text above top-layer images
                    width: `${textBlockWidth}px`,
                    height: `${totalContentHeight}px`,
                    pointerEvents: "none", // Let clicks pass through to inner divs
                    // CRITICAL: Clip content that extends outside canvas boundaries (only for main canvas)
                    ...(isOffCanvas
                      ? {}
                      : (() => {
                          // Use actual pixel width (same as RectangleElement and CircleElement)
                          const canvasPixelWidth =
                            canvasWidth || window.innerWidth * 0.45;

                          // Calculate visible bounds within canvas
                          const textLeft = text.x || 0;
                          const textTop = text.y || 0;
                          const textRight = textLeft + textBlockWidth;
                          const textBottom = textTop + totalContentHeight;

                          // Only clip if content is ACTUALLY outside the canvas boundaries
                          // Calculate actual overflow amounts (how much extends outside)
                          const actualOverflowLeft = Math.max(0, -textLeft);
                          const actualOverflowTop = Math.max(0, -textTop);
                          const actualOverflowRight = Math.max(
                            0,
                            textRight - canvasPixelWidth,
                          );
                          const actualOverflowBottom = Math.max(
                            0,
                            textBottom - effectiveCanvasHeight,
                          );

                          // Only apply clipping if there's actual overflow (content extends outside)
                          if (
                            actualOverflowLeft > 0 ||
                            actualOverflowTop > 0 ||
                            actualOverflowRight > 0 ||
                            actualOverflowBottom > 0
                          ) {
                            // Keep full width/height - only use clip-path to hide overflow
                            // This allows content to remain visible until it actually extends outside
                            // clip-path inset() takes: top, right, bottom, left (distances from edges)
                            return {
                              overflow: "hidden",
                              clipPath: `inset(${actualOverflowTop}px ${actualOverflowRight}px ${actualOverflowBottom}px ${actualOverflowLeft}px)`,
                              // Keep original width and height - clip-path handles the clipping
                              width: `${textBlockWidth}px`,
                              height: `${totalContentHeight}px`,
                            };
                          }
                          // CRITICAL: Explicitly remove clipping when element is fully inside canvas
                          // This ensures clip-path is removed when element comes back into canvas
                          return {
                            overflow: "visible",
                            clipPath: "none",
                            width: `${textBlockWidth}px`,
                            height: `${totalContentHeight}px`,
                          };
                        })()),
                  },
                }}
                onDblClick={(e) => {
                  if (!allowDbl()) return;
                  e.evt.stopPropagation(); // Prevent event bubbling to parent Group
                  setShowEditTask(false);
                  setEditTextBoxContent(false);
                  setSelectedText((prev) => ({
                    ...prev,
                    selectedContent: null,
                  }));
                  setShowEditNote(true);
                }}
              >
                <div
                  id={`note-div-${text.id}`}
                  data-export="true"
                  data-content-type="note"
                  data-groupid={text.id}
                  dangerouslySetInnerHTML={{
                    __html: flattenedNoteContent || "",
                  }}
                  onMouseDown={handleHtmlMouseDown}
                  style={getConsistentHtmlStyles({
                    position: "absolute",
                    top: yNoteContent,
                    left: 10,
                    width: `${textBlockWidth - 20}px`, // Use full width - let container clip-path handle clipping
                    fontFamily: noteFontFamily,
                    fontSize: `${noteFontSize}px`,
                    cursor: getHtmlHoverCursor(),
                    marginLeft: "0px",
                    color: getTextStyle(text, "note").color || "#000",
                    overflow: "hidden",
                    maxWidth: `${textBlockWidth - 20}px`, // Use full max width - let container clip-path handle clipping
                    overflowX: "hidden",
                    overflowY: "hidden",
                    maxHeight: `${Math.max(
                      0,
                      Math.min(
                        renderedNoteHeight,
                        effectiveCanvasHeight - (text.y + yNoteContent),
                      ),
                    )}px`,
                    // Let content determine its own height naturally
                    minHeight: "auto",
                    userSelect: isTextSelectionDisabled() ? "none" : "auto",
                    WebkitUserSelect: isTextSelectionDisabled()
                      ? "none"
                      : "auto",
                    MozUserSelect: isTextSelectionDisabled() ? "none" : "auto",
                    msUserSelect: isTextSelectionDisabled() ? "none" : "auto",
                    // CRITICAL: Disable pointer events when in multi-selection to prevent text selection
                    // When not selected, set to "none" so other elements (like UnifiedLineArrowElement orbs) can interact
                    pointerEvents: getHtmlPointerEvents(),
                    WebkitTouchCallout: isTextSelectionDisabled()
                      ? "none"
                      : "default",
                    WebkitUserDrag: isTextSelectionDisabled() ? "none" : "auto",
                    opacity: text.opacity !== undefined ? text.opacity : 1, // Apply element opacity
                    clipPath: getInnerDivClipPath(
                      yNoteContent,
                      10,
                      textBlockWidth - 20,
                      renderedNoteHeight,
                    ),
                    WebkitClipPath: getInnerDivClipPath(
                      yNoteContent,
                      10,
                      textBlockWidth - 20,
                      renderedNoteHeight,
                    ),
                  })}
                  className="content-text"
                  onClick={(e) => {
                    const currentIsMultiSelectMode =
                      isMultiSelectModeRef?.current ?? isMultiSelectMode;
                    // Disable selection if a tool is locked
                    if (lockedAction) {
                      return;
                    }

                    // If double-click is pending, ignore single click to prevent interference
                    if (doubleClickPendingRef.current) {
                      doubleClickPendingRef.current = false;
                      return;
                    }

                    // Check if clicking on the same element that's already selected
                    const isSameElement =
                      selectedObject && selectedObject.id === text.id;

                    // If clicking on the same element and already editing, don't reset editing states
                    if (
                      isSameElement &&
                      (editTextBoxContent ||
                        showEditTask ||
                        showEditNote ||
                        text.selectedContent)
                    ) {
                      setShowTextBox(true);
                      return;
                    }

                    // If clicking on the same element, ensure showTextBox is true and return
                    if (isSameElement) {
                      setShowTextBox(true);
                      return;
                    }

                    // CRITICAL: Two-click selection behavior when multi-select is NOT enabled
                    // First click: Deselect the currently selected element and return early
                    // Second click: Select the new element
                    // When multi-select mode is enabled, don't clear selection on first click
                    // Let the canvas click handler manage adding to selection instead
                    if (
                      selectedObject &&
                      !e.shiftKey &&
                      !isSameElement &&
                      !currentIsMultiSelectMode // Don't clear if multi-select mode is enabled
                    ) {
                      // Clear the other selection and return early - don't select this text element yet
                      // Second click will select it
                      setSelectedObject(null);
                      setSelectedObjects([]);
                      setSelectedText(null);
                      setSelectedTextId(null);
                      setShowTextBox(false);
                      // CRITICAL: Stop event propagation to prevent canvas click handler from selecting the new element
                      e.stopPropagation?.();
                      e.preventDefault?.();
                      return;
                    }

                    if (!currentIsMultiSelectMode) {
                      e.stopPropagation(); // Prevent event bubbling to parent Group
                    }

                    // ALWAYS clear selectedContent from ALL texts when selecting a text element
                    setTexts((prevTexts) =>
                      prevTexts.map((t) => ({
                        ...t,
                        selectedContent: null,
                      })),
                    );

                    // Clear all editing states
                    setEditTextBoxContent(false);
                    setShowEditTask(false);
                    setShowEditNote(false);

                    // Trigger selection
                    setSelectedImage(null);
                    transformerRef?.current?.nodes([]);
                    setShowTextBox(true);
                    setSelectedTextId(text.id);
                    setSelectedText({
                      ...text,
                      selectedContent: null,
                    });
                    setSelectedObject({
                      ...text,
                      type: "Text",
                      selectedContent: null,
                    });
                    if (!currentIsMultiSelectMode) {
                      setSelectedObjects([]);
                    }
                    // Create a Konva-like event object for HTML events
                    const konvaEvent = {
                      target: {
                        getClassName: () => "Group",
                        attrs: {
                          id: text.id,
                          type: "Text",
                          name: text.name,
                          x: text.x || 0,
                          y: text.y || 0,
                          width: text.width || 0,
                          height: textGroupHeight || 0,
                          fontSize: text.fontSize || 0,
                        },
                      },
                      evt: currentIsMultiSelectMode
                        ? {
                            ...(e.nativeEvent || e),
                            shiftKey: true,
                          }
                        : e.nativeEvent || e,
                    };
                    markSelectedNow();
                    onClick(konvaEvent);
                  }}
                  onDoubleClick={(e) => {
                    if (!allowDbl()) return;
                    e.stopPropagation(); // Prevent event bubbling to parent Group
                    e.preventDefault(); // Prevent default double-click behavior

                    // Set flag to prevent onClick from interfering
                    doubleClickPendingRef.current = true;

                    // CRITICAL: Use flushSync to force showEditNote to update IMMEDIATELY before other state updates
                    // This ensures TextBoxWithButtons sees showEditNote=true on the next render
                    flushSync(() => {
                      setShowEditNote(true);
                      setShowEditTask(false);
                      setEditTextBoxContent(false);
                    });

                    // Now update selectedText with cleared selectedContent
                    setSelectedText((prev) => {
                      if (!prev || prev.id !== text.id) return prev;
                      return { ...prev, selectedContent: null };
                    });

                    // Clear selectedContent in texts array
                    setTexts((prevTexts) =>
                      prevTexts.map((t) =>
                        t.id === text.id ? { ...t, selectedContent: null } : t,
                      ),
                    );

                    setShowTextBox(true);
                    setSelectedTextId(text.id);
                    setSelectedObject({
                      ...text,
                      type: "Text",
                      selectedContent: null,
                    });

                    // Clear flag after a short delay
                    setTimeout(() => {
                      doubleClickPendingRef.current = false;
                    }, 300);
                  }}
                />
              </Html>
            </Group>
          );
        })()}
    </Group>
  );
};

const TextElement = React.memo(TextElementInner, textElementPropsAreEqual);

// Export TextElement as named export for internal use
export { TextElement };

// Wrapper component to render copy button outside the Group
const TextElementWithCopyButton = (props) => {
  const {
    isSelected = false,
    isOffCanvas = false,
    isPreview = false,
    onCopyToOppositeCanvas,
    text,
    textBlockWidth,
    canvasWidth,
    canvasHeight,
  } = props;

  const shouldShowCopyButton =
    isSelected &&
    !isPreview &&
    isOffCanvas &&
    typeof onCopyToOppositeCanvas === "function";

  const textX = text?.x ?? 0;
  const textY = text?.y ?? 0;
  const textWidth = textBlockWidth || text?.width || 0;

  // Calculate effective canvas height (matching RectangleElement) for copy button positioning
  const RESIZE_HANDLE_HEIGHT = 10;
  const RESIZE_BOTTOM_SAFE = 0;
  const effectiveCanvasHeight = Math.max(
    0,
    canvasHeight - RESIZE_HANDLE_HEIGHT - RESIZE_BOTTOM_SAFE,
  );

  // Position absolutely relative to stage (like RectangleElement)
  const copyButtonLeft = Math.max(
    4,
    Math.min(textX + textWidth - 28, canvasWidth - 28),
  );
  // Use effectiveCanvasHeight for copy button positioning (matching RectangleElement)
  const copyButtonTop = Math.max(
    4,
    Math.min(textY - 36, effectiveCanvasHeight - 28),
  );

  return (
    <>
      <TextElement {...props} />
      {/* Copy button commented out */}
      {false && shouldShowCopyButton && (
        <Html>
          <div
            style={{
              position: "absolute",
              top: `${copyButtonTop}px`,
              left: `${copyButtonLeft}px`,
              pointerEvents: "none",
              zIndex: 30,
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCopyToOppositeCanvas?.();
              }}
              title="Copy to Main Canvas"
              style={{
                width: "26px",
                height: "26px",
                borderRadius: "4px",
                border: "1px solid #CBD5E1",
                backgroundColor: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 10px rgba(15, 23, 42, 0.18)",
                cursor: "pointer",
                pointerEvents: "auto",
                padding: 0,
                transition:
                  "background-color 0.15s ease, border-color 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#F1F5F9";
                e.currentTarget.style.borderColor = "#94A3B8";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#FFFFFF";
                e.currentTarget.style.borderColor = "#CBD5E1";
              }}
            >
              <svg
                width="16"
                height="19"
                viewBox="0 0 17 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.97572 15.75C5.47055 15.75 5.04297 15.575 4.69297 15.225C4.34297 14.875 4.16797 14.4474 4.16797 13.9423V2.55775C4.16797 2.05258 4.34297 1.625 4.69297 1.275C5.04297 0.925 5.47055 0.75 5.97572 0.75H14.3602C14.8654 0.75 15.293 0.925 15.643 1.275C15.993 1.625 16.168 2.05258 16.168 2.55775V13.9423C16.168 14.4474 15.993 14.875 15.643 15.225C15.293 15.575 14.8654 15.75 14.3602 15.75H5.97572ZM5.97572 14.25H14.3602C14.4372 14.25 14.5077 14.2179 14.5717 14.1538C14.6359 14.0898 14.668 14.0192 14.668 13.9423V2.55775C14.668 2.48075 14.6359 2.41025 14.5717 2.34625C14.5077 2.28208 14.4372 2.25 14.3602 2.25H5.97572C5.89872 2.25 5.82822 2.28208 5.76422 2.34625C5.70005 2.41025 5.66797 2.48075 5.66797 2.55775V13.9423C5.66797 14.0192 5.70005 14.0898 5.76422 14.1538C5.82822 14.2179 5.89872 14.25 5.97572 14.25ZM2.47572 19.25C1.97055 19.25 1.54297 19.075 1.19297 18.725C0.842969 18.375 0.667969 17.9474 0.667969 17.4423V4.55775H2.16797V17.4423C2.16797 17.5193 2.20005 17.5898 2.26422 17.6538C2.32822 17.7179 2.39872 17.75 2.47572 17.75H12.3602V19.25H2.47572Z"
                  fill="#1E293B"
                />
              </svg>
            </button>
          </div>
        </Html>
      )}
    </>
  );
};

export default TextElementWithCopyButton;
